import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { productsAPI, apiMasterAPI, settingsAPI, commercialConfigAPI, equipmentAPI, riskAssessmentAPI, impactAssessmentAPI, aiAPI, integrationsAPI } from '../services/api';
import { withReportProfile, unitReportAddress } from '../utils/reportProfiles';
import useApprovalRights from '../hooks/useApprovalRights';
import { ImpactAssessmentModal } from '../components/ImpactAssessmentModal';
import FloatingScrollWrapper from '../components/FloatingScrollWrapper';
import AIResultPanel from '../components/AIResultPanel';
import { Modal, ModalFooter, ModalSubheader, ESignModal } from '../components/Modals';
import VersionHistoryModal from '../components/VersionHistoryModal';
import ResizableTh from '../components/ResizableTh';
import { SearchBox, FilterSelect } from '../components/ListToolbar';
import { ActionsMenu } from '../components/ActionsMenu';
import AuditMeta from '../components/AuditMeta';
import useColumnWidths from '../hooks/useColumnWidths';
import useAIAction from '../hooks/useAIAction';
import { toast } from 'react-toastify';
import { FaPlus, FaEdit, FaTrash, FaEye, FaDownload, FaUpload, FaCopy, FaSave, FaCapsules, FaFlask, FaFileAlt, FaCheck, FaExclamationTriangle, FaShieldAlt, FaLayerGroup, FaPrint, FaHourglassHalf, FaTimes } from 'react-icons/fa';
import { StatCard } from '../components/UnifiedUIComponents';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { openPrintPreview, getActivePrintUnitName } from '../utils/printUtils';
import { isModuleOn } from '../utils/moduleAccess';
import { printAssessmentReport, riskAssessmentReportHTML, impactAssessmentReportHTML } from '../utils/assessmentReports';

import { matchesPlantScope } from '../utils/helpers';
import { computeWorstCaseRanking, WORST_CASE_SETTINGS_KEYS, parseJsonSetting, parseJsonObject, DEFAULT_WEIGHTS, DEFAULT_SOLUBILITY, DEFAULT_CLEANABILITY, DEFAULT_PDE_RATINGS, DEFAULT_DOSE_RATINGS, DEFAULT_PRIORITY_ORDER } from '../utils/worstCaseRanking';
import TablePagination from '../components/TablePagination';
import CleanabilityAssessmentModal from '../components/CleanabilityAssessmentModal';
import ChangeReferenceModal from '../components/ChangeReferenceModal';
import { DEFAULT_CLEANABILITY_CONFIG, cleanabilityChecklistHTML, exportCleanabilityChecklistXlsx } from '../utils/cleanabilityAssessment';

// Cleanability checklist behind a product-level override: subject block + print.
const productChecklistSubject = (product, api) => ({
  heading: 'Product (cleanability override)',
  rows: [
    ['Product Code', product?.product_code], ['Product Name', product?.product_name],
    ['Dosage Form', product?.dosage_form], ['Coating', [product?.coating_type, product?.coating_agent].filter(Boolean).join(' - ')],
    ['API', [api?.api_code, api?.api_name].filter(Boolean).join(' - ')], ['API Cleanability (API Master)', api?.api_cleanability_rating || api?.cleanability_rating],
  ],
  fileName: `Cleanability_Checklist_${product?.product_code || 'Product'}_${api?.api_code || ''}`,
});
const printProductChecklist = (subject, config, record, label) => printAssessmentReport({
  reportKey: 'cleanability_assessment',
  title: `Cleanability Checklist - ${label}`,
  documentName: 'Cleanability Rating Checklist',
  build: () => cleanabilityChecklistHTML(subject, config, record),
});

// Risk + impact assessment of one product as a single printable report.
const printProductAssessments = (product) => printAssessmentReport({
  reportKey: 'risk_assessment',
  title: `Risk & Impact Assessment Report - ${product.product_code || product.product_name}`,
  documentName: 'Risk & Impact Assessment Report',
  build: async () => {
    const [riskRes, impactRes] = await Promise.all([
      riskAssessmentAPI.getAll({ product_id: product.product_id }).catch(() => null),
      impactAssessmentAPI.getByProductId(product.product_id).catch(() => null),
    ]);
    const risk = (riskRes?.data?.data || [])[0];
    const impact = impactRes?.data?.data;
    if (!risk && !impact) throw new Error('No risk or impact assessment is recorded for this product yet');
    return [
      risk ? `<h2 style="font-size:15px;margin:4px 0 8px">1. Risk Assessment</h2>${riskAssessmentReportHTML(risk)}` : '',
      impact ? `<h2 style="font-size:15px;margin:18px 0 8px">2. Impact Assessment</h2>${impactAssessmentReportHTML(impact)}` : '',
    ].join('');
  },
});

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');

const DEFAULT_UNIT_CONFIG = Object.freeze({
  fillWeightUnits: ['mg', 'g', 'kg'],
  fillVolumeUnits: ['mL', 'L'],
  labelClaimWeightUnits: ['mg/Unit', 'g/Unit', 'mcg/Unit', 'IU/Unit', 'MIU/Unit', 'mg/g', 'mcg/g', 'IU/g', 'MIU/g', '%', 'mg/5mL'],
  labelClaimVolumeUnits: ['mg/mL', 'mcg/mL', 'IU/mL', 'MIU/mL', '%', 'mg/5mL']
});
const DEFAULT_SOLUBILITY_OPTIONS = Object.freeze([
  'Very Soluble',
  'Freely Soluble',
  'Soluble',
  'Sparingly Soluble',
  'Slightly Soluble',
  'Very Slightly Soluble',
  'Practically Insoluble',
  'Insoluble'
]);
const DEFAULT_CLEANABILITY_OPTIONS = Object.freeze(['Easy', 'Medium', 'Difficult']);
const RISK_LEVEL_COLORS = Object.freeze({
  green:  { badge: '#dcfce7', text: '#15803d', border: '#86efac' },
  yellow: { badge: '#fef9c3', text: '#a16207', border: '#fde047' },
  orange: { badge: '#ffedd5', text: '#c2410c', border: '#fdba74' },
  red:    { badge: '#fee2e2', text: '#b91c1c', border: '#fca5a5' },
});

const EMPTY_IMPACT_VALUES = Object.freeze({ contamination_method: '', cross_contamination_note: '', justification: '', manual_override: false, override_reason: '', cv_required_override: false });

const getFillUnitValueList = (unitConfig = DEFAULT_UNIT_CONFIG) => {
  return [...new Set([...(unitConfig.fillVolumeUnits || []), ...(unitConfig.fillWeightUnits || [])])];
};

const isVolumeFillUnit = (fillUnit, unitConfig = DEFAULT_UNIT_CONFIG) => (unitConfig.fillVolumeUnits || []).includes(fillUnit);
const isWeightFillUnit = (fillUnit, unitConfig = DEFAULT_UNIT_CONFIG) => (unitConfig.fillWeightUnits || []).includes(fillUnit);

const getLabelClaimUnitValueList = (fillUnit, unitConfig = DEFAULT_UNIT_CONFIG) => {
  const units = [];

  if (isVolumeFillUnit(fillUnit, unitConfig)) {
    units.push(...(unitConfig.labelClaimVolumeUnits || []));
  }

  if (isWeightFillUnit(fillUnit, unitConfig)) {
    units.push(...(unitConfig.labelClaimWeightUnits || []));
  }

  if (units.length === 0) {
    units.push(...DEFAULT_UNIT_CONFIG.labelClaimWeightUnits);
  }

  return [...new Set(units)];
};

const getDefaultLabelClaimUnit = (fillUnit, unitConfig = DEFAULT_UNIT_CONFIG) => {
  const options = getLabelClaimUnitValueList(fillUnit, unitConfig);
  return options[0] || 'mg/Unit';
};

const PRODUCT_STATUS = Object.freeze({
  DRAFT: 'Draft',
  PENDING_PRODUCT_APPROVAL: 'Pending Product Approval',
  PENDING_QA_APPROVAL: 'Pending QA Approval',
  APPROVED: 'Approved',
  PENDING_DELETION_APPROVAL: 'Pending Deletion Approval'
});

const normalizeDisplayStatus = (status) => {
  if (!status) return PRODUCT_STATUS.DRAFT;
  if (status === 'Active') return PRODUCT_STATUS.APPROVED;
  return status;
};

const APPROVAL_DECISION_OPTIONS = [
  { value: 'approve', label: 'Approve' },
  { value: 'return', label: 'Return' },
  { value: 'reject', label: 'Reject' },
  { value: 'edit_required', label: 'Edit Required' }
];


const parseFiles = (value) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [value];
    }
  }
  return [];
};

const toDataUrlIfPossible = (src) => new Promise((resolve) => {
  if (!src) {
    resolve(null);
    return;
  }

  const trimmed = String(src).trim();
  if (!trimmed) {
    resolve(null);
    return;
  }

  if (trimmed.startsWith('data:image/')) {
    resolve(trimmed);
    return;
  }

  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    } catch (error) {
      resolve(null);
    }
  };
  img.onerror = () => resolve(null);
  img.src = trimmed;
});

const toFileUrl = (file) => {
  if (!file) return '';
  if (typeof file === 'string' && /^https?:\/\//i.test(file)) return file;
  return `${API_ORIGIN}/uploads/${file}`;
};

const FileUploader = ({ label, files, onAdd, onRemove, id }) => {
  const fileList = Array.isArray(files) ? files : (files ? [files] : []);
  const [pendingAttachmentName, setPendingAttachmentName] = useState('');
  const getFileDisplayName = (file) => {
    if (file?.name) return file.name;
    const raw = String(file || '');
    const chunks = raw.split(/[\\/]/);
    return chunks[chunks.length - 1] || raw || 'Attachment';
  };

  const handleFileClick = (file) => {
    if (file instanceof File) {
      const url = URL.createObjectURL(file);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } else if (typeof file === 'string') {
      window.open(file, '_blank');
    }
  };

  const buildNamedFiles = (selectedFiles) => {
    const custom = String(pendingAttachmentName || '').trim();
    if (!custom) return selectedFiles;
    return selectedFiles.map((file, index) => {
      const originalName = String(file?.name || '');
      const extMatch = originalName.match(/(\.[^./\\]+)$/);
      const ext = extMatch ? extMatch[1] : '';
      const customHasExt = /\.[^./\\]+$/.test(custom);
      const baseName = customHasExt ? custom : `${custom}${ext}`;
      const finalName = selectedFiles.length > 1
        ? baseName.replace(/(\.[^./\\]+)?$/, (_, foundExt = '') => `_${index + 1}${foundExt}`)
        : baseName;
      return new File([file], finalName, { type: file.type, lastModified: file.lastModified });
    });
  };

  return (
    <div className="form-group" style={{ minWidth: '0' }}>
      <label className="form-label" style={{ fontWeight: '500', color: 'var(--text-secondary)', fontSize: 'var(--fs-md)', marginBottom: '4px', display: 'block' }}>{label}</label>
      <div style={{ border: '1px dashed var(--border-color)', borderRadius: '6px', padding: '8px', background: 'var(--card-bg)', minHeight: '38px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginBottom: fileList.length > 0 ? '8px' : '0' }}>
          <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>{fileList.length > 0 ? `${fileList.length} file(s)` : 'No files'}</span>
          <input
            type="text"
            className="form-control"
            placeholder="Attachment name (optional)"
            value={pendingAttachmentName}
            onChange={(e) => setPendingAttachmentName(e.target.value)}
            style={{ maxWidth: '210px', height: '28px', fontSize: 'var(--fs-sm)', padding: '4px 8px' }}
          />
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={() => document.getElementById(id).click()}
            style={{ padding: '2px 8px', fontSize: 'var(--fs-sm)', display: 'flex', alignItems: 'center', gap: '4px', border: '1px solid var(--info-border)', background: 'var(--card-bg)' }}
          >
            <FaPlus style={{ fontSize: 'var(--fs-xs)' }} /> Add
          </button>
        </div>
        <input
          type="file"
          multiple
          id={id}
          onChange={(e) => {
            if (e.target.files && e.target.files.length > 0) {
              onAdd(Array.from(e.target.files));
              const selectedFiles = Array.from(e.target.files);
              onAdd(buildNamedFiles(selectedFiles));
              setPendingAttachmentName('');
              e.target.value = '';
            }
          }}
          style={{ display: 'none' }}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
        />
        {fileList.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {fileList.map((file, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 8px', backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '4px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', overflow: 'hidden' }}>
                  <span style={{ fontSize: 'var(--fs-md)' }}>[File]</span>
                  <span 
                    style={{ 
                      fontSize: 'var(--fs-sm)', 
                      color: 'var(--primary-color, #2563eb)', 
                      whiteSpace: 'nowrap', 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      maxWidth: '100px',
                      cursor: 'pointer',
                      textDecoration: 'underline'
                    }} 
                    title={getFileDisplayName(file)}
                    onClick={() => handleFileClick(file)}
                  >
                    {getFileDisplayName(file)}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => onRemove(idx)}
                  style={{ border: 'none', background: 'none', color: 'var(--danger-color)', cursor: 'pointer', padding: '2px', display: 'flex' }}
                  title="Remove"
                >
                  <FaTrash style={{ fontSize: 'var(--fs-xs)' }} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const ProductMaster = () => {
  // Product Master State
  const [products, setProducts] = useState([]);
  const [trains, setTrains] = useState([]);
  const [allApis, setAllApis] = useState([]);
  // Cleanability scoring helper for the product-level override (same questionnaire as API Master)
  const [cleanConfig, setCleanConfig] = useState(DEFAULT_CLEANABILITY_CONFIG);
  const [overrideChecklist, setOverrideChecklist] = useState(null); // { index, readOnly }
  const [historyModal, setHistoryModal] = useState(null); // product
  const [riskByProductId, setRiskByProductId] = useState({});
  const [impactByProductId, setImpactByProductId] = useState({});
  const [showImpactModal, setShowImpactModal] = useState(false);
  const [impactModalMode, setImpactModalMode] = useState('gate');
  const [impactEvaluation, setImpactEvaluation] = useState(null);
  const [impactViewRecord, setImpactViewRecord] = useState(null);
  const [impactLoading, setImpactLoading] = useState(false);
  const [impactValues, setImpactValues] = useState(EMPTY_IMPACT_VALUES);
  const [pendingSaveMode, setPendingSaveMode] = useState(null);
  const [commercialLines, setCommercialLines] = useState([]);
  const [productLoading, setProductLoading] = useState(true);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showProductViewModal, setShowProductViewModal] = useState(false);
  const [productEditingId, setProductEditingId] = useState(null);
  const [viewingProduct, setViewingProduct] = useState(null);
  const [qmsLinked, setQmsLinked] = useState(false);
  // {product} while the Change Control/Deviation/CAPA gate is open for an Approved product
  const [changeRefGate, setChangeRefGate] = useState(null);
  const [changeRefBusy, setChangeRefBusy] = useState(false);
  const [pendingChangeRef, setPendingChangeRef] = useState(null);
  const editingApprovedProductRef = useRef(false);
  const { widths: productColWidths, setWidth: setProductColWidth } = useColumnWidths('product-master');
  const ai = useAIAction();
  const grouping = useAIAction();
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productSortBy, setProductSortBy] = useState('product_name');
  const [productSortOrder, setProductSortOrder] = useState('asc');
  const [productDosageFilter, setProductDosageFilter] = useState('All');
  const [productStatusCard, setProductStatusCard] = useState('All'); // All | Approved | Pending | Draft
  const [productLineFilter, setProductLineFilter] = useState(localStorage.getItem('activeLine') || 'All');
  const [productPlantFilter, setProductPlantFilter] = useState(localStorage.getItem('activePlant') || 'All');
  const [productTrainFilter, setProductTrainFilter] = useState('All');
  const [pendingDelete, setPendingDelete] = useState(null); // store product and stage awaiting signature
  const [pendingBulkDelete, setPendingBulkDelete] = useState(null);
  const [pendingApproval, setPendingApproval] = useState(null); // {product, stage} awaiting signature for approval
  const [productFormData, setProductFormData] = useState(() => {
    const lineRoute = localStorage.getItem('activeLineRoute') || '';
    const routeDosageMap = { oral: 'Tablet', topical: 'Ointment', injectable: 'Dry Powder Injection', inhalation: 'Solution', ophthalmic: 'Solution', transdermal: 'Ointment' };
    return {
    product_code: '',
    product_name: '',
    dosage_form: routeDosageMap[lineRoute] || 'Dry Powder Injection',
    production_line: localStorage.getItem('activeLine') || '',
      equipment_train_id: '',
      equipment_train_name: '',
      equipment_train_code: '',
      batch_size: '',
    batch_size_unit: 'Unit',
    fill_weight: '',
    fill_unit: 'mL',
    reference_document: '',
    product_notes: '',
    product_color: '',
    product_flavour: '',
    coating_type: '',
    coating_agent: '',
    attachedFiles: [],
    linked_trains: [],
    apis: [{ api_id: '', label_claim: '', label_claim_unit: getDefaultLabelClaimUnit('mL'), iu_to_mg_conversion: '' }],
    status: PRODUCT_STATUS.DRAFT
  };
  });

  // Pagination State
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productCurrentPage, setProductCurrentPage] = useState(1);
  const [selectedProductRow, setSelectedProductRow] = useState(null); // id of row clicked to show actions
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [apiSearchTerms, setApiSearchTerms] = useState({});
  const [activeApiDropdownIndex, setActiveApiDropdownIndex] = useState(null);
  const [apisLoading, setApisLoading] = useState(false);
  const [trainSearchTerm, setTrainSearchTerm] = useState('');
  const [showTrainDropdown, setShowTrainDropdown] = useState(false);
  const [showColorField, setShowColorField] = useState(false);
  const [showAddToTrainModal, setShowAddToTrainModal] = useState(false);
  const [addToTrainESign, setAddToTrainESign] = useState({ password: '', comment: '' });
  const [targetTrainId, setTargetTrainId] = useState('');
  const [targetTrainSearchTerm, setTargetTrainSearchTerm] = useState('');
  const [showTargetTrainDropdown, setShowTargetTrainDropdown] = useState(false);
  const [apiFileAttachments, setApiFileAttachments] = useState({});
  const [apiDocumentFiles, setApiDocumentFiles] = useState({});
  const [viewLoading, setViewLoading] = useState(false);
  const [viewingTrainMembers, setViewingTrainMembers] = useState([]);
  const [linkedTrainsMembers, setLinkedTrainsMembers] = useState({});
  const [showLinkedTrainDetails, setShowLinkedTrainDetails] = useState(false);
  const [showAddSecondaryTrainModal, setShowAddSecondaryTrainModal] = useState(false);
  const [secondaryTrainSearchTerm, setSecondaryTrainSearchTerm] = useState('');
  const [showSecondaryTrainDropdown, setShowSecondaryTrainDropdown] = useState(false);
  const [showESignModal, setShowESignModal] = useState(false);
  const [eSignMode, setESignMode] = useState('submit');
  const [eSignData, setESignData] = useState({ password: '', comment: '', decision: 'approve' });
  const esignPasswordRef = useRef(null);
  const initialProductRef = useRef(null);
  const mobileImportInputRef = useRef(null);
  // SAP / other-app product sync (optional, Integration Hub).
  const [productSyncSources, setProductSyncSources] = useState([]);
  const [fetchingProductSource, setFetchingProductSource] = useState('');
  const [reportSettings, setReportSettings] = useState({
    report_client_name: 'Client Name',
    report_client_address: 'Client Address',
    report_document_name: 'Product Master Report',
    report_document_rev_no: '01',
    report_client_logo: '',
    report_primeclean_logo: '',
    report_footer_tagline: 'Data is generated and handled by PrimeClean.',
    report_format_no_product_master: '',
    product_master_color_field_enabled: 'true',
    product_master_coating_fields_enabled: 'true'
  });
  const [unitConfig, setUnitConfig] = useState(DEFAULT_UNIT_CONFIG);
  const [solubilityOptions, setSolubilityOptions] = useState(DEFAULT_SOLUBILITY_OPTIONS);
  const [cleanabilityOptions, setCleanabilityOptions] = useState(DEFAULT_CLEANABILITY_OPTIONS);
  // Worst-Case ranking settings (same source as pages/WorstCase.js) — used to flag which single
  // product per equipment train is the CV worst case; see loadReportSettings and
  // worstCaseProductIdByTrainId below.
  const [wcSettings, setWcSettings] = useState({
    weights: DEFAULT_WEIGHTS, solubility: DEFAULT_SOLUBILITY, cleanability: DEFAULT_CLEANABILITY,
    pde: DEFAULT_PDE_RATINGS, dose: DEFAULT_DOSE_RATINGS, scoringMethod: 'weighted',
    priorityOrder: DEFAULT_PRIORITY_ORDER, manualOverrides: {}
  });

  // Modal-based confirmation states for actions (add, edit, delete, import)
  const [pendingAction, setPendingAction] = useState(null); // { actionType: 'add'|'edit'|'delete'|'import', data: {...} }
  const [pendingBulkApproval, setPendingBulkApproval] = useState(null); // { items: [{...}], approvalType: 'edit'|'delete'|'add' }
  const [confirmationMessage, setConfirmationMessage] = useState('');
  
  const didInitialLoad = useRef(false);
  const routedProductEditRef = useRef(false);
  // Snapshot the form the moment the modal opens so unchanged edits can skip the e-signature.
  useEffect(() => {
    initialProductRef.current = showProductModal
      ? { json: JSON.stringify(productFormData), status: productFormData.status }
      : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showProductModal]);
  const trainsRef = useRef([]);
  const location = useLocation();
  const navigate = useNavigate();
  const REQUEST_TIMEOUT_MS = 15000;
  let currentUserRole = '';
  try {
    currentUserRole = JSON.parse(localStorage.getItem('user') || '{}')?.role || '';
  } catch (error) {
    currentUserRole = '';
  }
  const isCurrentUserSuperAdmin = String(currentUserRole).trim().toLowerCase() === 'super admin';
  const isCurrentUserManager = String(currentUserRole).trim().toLowerCase() === 'manager';
  const isCurrentUserQAHead = String(currentUserRole).trim().toLowerCase() === 'qa head';
  const { canEarlier: canApproveProductStage, canFinal: canApproveQaStage } = useApprovalRights('product.approval');

  const trainOptions = useMemo(() => {
    const activeLine = localStorage.getItem('activeLine');
    const activePlant = localStorage.getItem('activePlant');
    const uniqueTrains = new Map();
    (trains || []).forEach((train) => {
      const trainId = String(train?.train_id || '').trim();
      if (!trainId) return;
      const isInactive = train?.is_active === false || Number(train?.is_active) === 0 || String(train?.is_active || '').trim().toLowerCase() === 'false';
      if (isInactive) return;

      // Strict filtering by Unit and Line
      const matchesPlant = matchesPlantScope(train.unit_plant || train.unit_id, activePlant);
      
      const matchesLine = !activeLine || activeLine === 'All' || 
                          String(train.production_line || '').includes(activeLine) ||
                          String(train.line_id || '').includes(activeLine);
      
      if (!matchesPlant || !matchesLine) return;

      if (!uniqueTrains.has(trainId)) {
        uniqueTrains.set(trainId, train);
      }
    });
    return Array.from(uniqueTrains.values()).sort((a, b) => {
      const codeCompare = String(a?.train_code || '').localeCompare(String(b?.train_code || ''));
      if (codeCompare !== 0) return codeCompare;
      return String(a?.train_name || '').localeCompare(String(b?.train_name || ''));
    });
  }, [trains]);

  // For each equipment train, the single product identified as the CV worst case (highest
  // cleaning-validation risk score among that train's products) via the same Worst Case Ranking
  // logic used on the Worst Case page. Only this product's row shows the "Worst Case" tag and the
  // CV Required/Not Required badge on the Product Master list — every other product in the same
  // train is covered by that worst-case bracketing, so repeating the badge on each row would be
  // misleading. Full CV Required/Not Required detail for every product still lives in Risk
  // Assessment and Impact Assessment themselves.
  const worstCaseProductIdByTrainId = useMemo(() => {
    const result = {};
    (trains || []).forEach((train) => {
      const trainId = train?.train_id;
      if (trainId == null) return;
      const trainName = train.train_name || train.train_code || `Train ${trainId}`;
      const ranked = computeWorstCaseRanking({
        trains, apis: allApis, products,
        weights: wcSettings.weights,
        solubilityRatings: wcSettings.solubility,
        cleanabilityRatings: wcSettings.cleanability,
        pdeRatings: wcSettings.pde,
        doseRatings: wcSettings.dose,
        scoringMethod: wcSettings.scoringMethod,
        priorityOrder: wcSettings.priorityOrder,
        manualOverrides: wcSettings.manualOverrides,
        trainFilter: trainName
      });
      const topProductId = ranked[0]?.product_id;
      if (topProductId != null) result[String(trainId)] = topProductId;
    });
    return result;
  }, [trains, allApis, products, wcSettings]);

  const isWorstCaseProduct = (product) => {
    const trainId = product?.equipment_train_id;
    if (trainId == null || trainId === '') return true; // no train to bracket against
    const winnerId = worstCaseProductIdByTrainId[String(trainId)];
    if (winnerId == null) return true; // ranking couldn't be computed, don't hide the badge
    return String(winnerId) === String(product.product_id);
  };

  const getApprovalStatusInfo = (product) => {
    const displayStatus = normalizeDisplayStatus(product.status);

    if (product.deletion_requested) {
      if (!product.deletion_product_approved) {
        return {
          text: 'Pending Deactivation Approval',
          tooltip: 'This product has been marked for deactivation and is awaiting product approval.',
          badgeClass: 'badge-danger',
        };
      }
      if (!product.deletion_qa_approved) {
        return {
          text: 'Pending QA Deactivation Approval',
          tooltip: 'This product deactivation is pending QA approval.',
          badgeClass: 'badge-danger',
        };
      }
    }

    if (displayStatus === PRODUCT_STATUS.PENDING_PRODUCT_APPROVAL) {
      const createdAt = new Date(product.created_at).getTime();
      const updatedAt = new Date(product.updated_at).getTime();
      const isNew = Math.abs(updatedAt - createdAt) < 5000; // 5 seconds threshold

      if (isNew) {
        return {
          text: 'Pending Addition Approval',
          tooltip: 'This new product is awaiting its first approval.',
          badgeClass: 'badge-info',
        };
      } else {
        return {
          text: 'Pending Edit Approval',
          tooltip: 'This product has been edited and is awaiting re-approval.',
          badgeClass: 'badge-info',
        };
      }
    }

    if (displayStatus === PRODUCT_STATUS.PENDING_QA_APPROVAL) {
      return {
        text: 'Pending QA Approval',
        tooltip: 'This product is awaiting final QA approval.',
        badgeClass: 'badge-info',
      };
    }

    if (displayStatus === PRODUCT_STATUS.DRAFT) {
      return {
        text: displayStatus,
        tooltip: 'This product is in a draft state and has not been submitted for approval.',
        badgeClass: 'badge-warning',
      };
    }

    if (displayStatus === PRODUCT_STATUS.APPROVED) {
      return {
        text: displayStatus,
        tooltip: 'This product is approved.',
        badgeClass: 'badge-success',
      };
    }

    return { text: displayStatus, tooltip: `Status: ${displayStatus}`, badgeClass: 'badge-secondary' }; // Fallback
  };

  const getProductApprovalMeta = (product) => {
    if (!product) return { at: null, by: null };

    if (product.deletion_requested) {
      if (product.deletion_qa_approved_at || product.deletion_qa_approved_by_user || product.deletion_qa_approved_by) {
        return {
          at: product.deletion_qa_approved_at,
          by: product.deletion_qa_approved_by_user || product.deletion_qa_approved_by || null,
        };
      }
      if (product.deletion_product_approved_at || product.deletion_product_approved_by_user || product.deletion_product_approved_by) {
        return {
          at: product.deletion_product_approved_at,
          by: product.deletion_product_approved_by_user || product.deletion_product_approved_by || null,
        };
      }
    } else {
      if (product.qa_approved_at || product.qa_approved_by_user || product.qa_approved_by) {
        return {
          at: product.qa_approved_at,
          by: product.qa_approved_by_user || product.qa_approved_by || null,
        };
      }
      if (product.product_approved_at || product.product_approved_by_user || product.product_approved_by) {
        return {
          at: product.product_approved_at,
          by: product.product_approved_by_user || product.product_approved_by || null,
        };
      }
    }

    return { at: null, by: null };
  };

  const formatApprover = (value) => {
    if (!value) return '-';
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return '-';
      // Avoid showing numeric IDs when username is missing
      if (/^\d+$/.test(trimmed)) return '-';
      return trimmed;
    }
    if (typeof value === 'number') return '-';
    return String(value);
  };

  const withTimeout = async (promise, timeoutMs, timeoutMessage) => {
    let timeoutId;
    try {
      return await Promise.race([
        promise,
        new Promise((_, reject) => {
          timeoutId = setTimeout(() => reject(new Error(timeoutMessage)), timeoutMs);
        })
      ]);
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  };

  useEffect(() => {
    if (didInitialLoad.current) return;
    didInitialLoad.current = true;
    loadProducts();
    loadAllApis();
    loadTrains();
    loadReportSettings();
    loadCommercialRuntime();
    loadRiskAssessments();
    loadImpactAssessments();
  }, []);

  useEffect(() => {
    if (showESignModal && esignPasswordRef.current) {
      setTimeout(() => {
        esignPasswordRef.current?.focus();
      }, 100);
    }
  }, [showESignModal]);

  useEffect(() => {
    setProductCurrentPage(1);
  }, [productSearchTerm, productDosageFilter, productLineFilter, productTrainFilter]);

  useEffect(() => {
    const syncActiveLineContext = () => {
      setProductLineFilter(localStorage.getItem('activeLine') || 'All');
      setProductPlantFilter(localStorage.getItem('activePlant') || 'All');
      setProductTrainFilter('All');
    };

    window.addEventListener('site-context-changed', syncActiveLineContext);
    return () => window.removeEventListener('site-context-changed', syncActiveLineContext);
  }, []);

  useEffect(() => {
    if (location.state?.openViewProductId && products.length > 0 && !showProductViewModal && !showProductModal) {
      const requestedProductId = String(location.state.openViewProductId);
      const selectedProduct = products.find((product) => String(product.product_id) === requestedProductId);
      if (selectedProduct) {
        (async () => {
          await handleProductView(selectedProduct);
          navigate(location.pathname, { replace: true, state: {} });
        })();
      }
    }
  }, [location.state, location.pathname, navigate, products, showProductViewModal, showProductModal]);

  useEffect(() => {
    if (!location.state?.openEditProductId) {
      routedProductEditRef.current = false;
      return;
    }
    if (showProductModal || routedProductEditRef.current) return;

    const requestedProductId = String(location.state.openEditProductId || '').trim();
    if (!requestedProductId) {
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }

    routedProductEditRef.current = true;
    (async () => {
      try {
        let resolvedProduct = products.find((product) => String(product.product_id) === requestedProductId) || null;

        try {
          const productRes = await withTimeout(
            productsAPI.getById(requestedProductId),
            REQUEST_TIMEOUT_MS,
            'Product details request timed out'
          );
          const details = productRes?.data?.data;
          if (details) {
            resolvedProduct = {
              ...(resolvedProduct || {}),
              ...details,
              product_id: details.product_id || resolvedProduct?.product_id || requestedProductId,
              attached_files: parseFiles(details.attached_files || details.attachedFiles)
            };
          }
        } catch (error) {
          if (!resolvedProduct) throw error;
        }

        if (!resolvedProduct) {
          toast.error('Product not found');
          return;
        }

        const selectedTrainId = String(location.state.preselectTrainId || '').trim();
        const selectedTrain = selectedTrainId
          ? (trainOptions.find((train) => String(train.train_id) === selectedTrainId) || trainsRef.current.find((train) => String(train.train_id) === selectedTrainId))
          : null;

        const productForEdit = selectedTrainId
          ? {
              ...resolvedProduct,
              equipment_train_id: selectedTrainId,
              train_name: selectedTrain?.train_name || resolvedProduct.train_name || '',
              train_code: selectedTrain?.train_code || resolvedProduct.train_code || ''
            }
          : resolvedProduct;

        setShowProductViewModal(false);
        setShowLinkedTrainDetails(false);
        handleProductEdit(productForEdit);
        if (selectedTrainId) {
          toast.success('Selected train loaded for this product. Save the product to confirm the link.');
        }
      } catch (error) {
        const message = error?.message === 'Product details request timed out'
          ? 'Loading product details timed out'
          : (error?.response?.data?.message || 'Failed to load product for train selection');
        toast.error(message);
      } finally {
        navigate(location.pathname, { replace: true, state: {} });
      }
    })();
  }, [location.state, location.pathname, navigate, products, showProductModal, trains]);

  // ==================== PRODUCT MASTER FUNCTIONS ====================

  const extractProductRows = (payload) => {
    if (Array.isArray(payload?.data?.data?.rows)) return payload.data.data.rows;
    if (Array.isArray(payload?.data?.data)) return payload.data.data;
    if (Array.isArray(payload?.data?.rows)) return payload.data.rows;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
  };

  const extractTrainRows = (payload) => {
    if (Array.isArray(payload?.data?.data?.rows)) return payload.data.data.rows;
    if (Array.isArray(payload?.data?.data)) return payload.data.data;
    if (Array.isArray(payload?.data?.rows)) return payload.data.rows;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
  };

  const getTrainLabel = (trainId, fallbackCode, fallbackName) => {
    if (!trainId) return '-';
    const train = trainOptions.find((t) => String(t.train_id) === String(trainId));
    if (train) return [train.train_code, train.train_name].filter(Boolean).join(' - ');
    const fallback = [fallbackCode, fallbackName].filter(Boolean).join(' - ');
    return fallback || 'Unknown Train';
  };

  const getLinkedTrainsForProduct = (product) => {
    if (!product) return [];
    if (Array.isArray(product.linked_trains) && product.linked_trains.length) return product.linked_trains;
    if (!product.equipment_train_id) return [];
    return [{
      train_id: product.equipment_train_id,
      train_code: product.train_code || '',
      train_name: product.train_name || '',
      is_primary: true,
      qualification_remark: '',
      same_size_equipment: false,
      same_cleaning_method: false,
      cv_batch_remark: ''
    }];
  };

  const getPrimaryTrainLink = (product) => {
    const links = getLinkedTrainsForProduct(product);
    return links.find((link) => link.is_primary) || links[0] || null;
  };

  const getSecondaryTrainLinks = (product) => getLinkedTrainsForProduct(product).filter((link) => !link.is_primary);

  const getTrainTypeLabel = (index) => {
    if (index === 0) return 'PRIMARY';
    if (index === 1) return 'SECONDARY';
    const num = index + 1;
    const suffix = num === 3 ? 'RD' : num === 4 ? 'TH' : 'TH';
    return `${num}${suffix}`;
  };

  const isPendingEditApproval = (product) => {
    const displayStatus = normalizeDisplayStatus(product?.status);
    if (displayStatus !== PRODUCT_STATUS.PENDING_PRODUCT_APPROVAL) return false;
    const createdAt = new Date(product.created_at).getTime();
    const updatedAt = new Date(product.updated_at).getTime();
    return Math.abs(updatedAt - createdAt) >= 5000; // Not a new product, it's been edited
  };

  const getTrainNameForProduct = (product) => {
    if (!product) return '-';
    const primaryLink = getPrimaryTrainLink(product);
    if (!primaryLink) return '-';
    const baseLabel = primaryLink.train_name || getTrainLabel(primaryLink.train_id, primaryLink.train_code, primaryLink.train_name);
    const secondaryCount = getSecondaryTrainLinks(product).length;
    return secondaryCount > 0 ? `${baseLabel} (+${secondaryCount} more)` : baseLabel;
  };

  const getFormattedLinkedTrainsForExport = (product) => {
    const links = getLinkedTrainsForProduct(product);
    if (!links || links.length === 0) return '-';
    return links.map((link, index) => {
      const trainLabel = getTrainLabel(link.train_id, link.train_code, link.train_name);
      const typeLabel = ` [${getTrainTypeLabel(index)}]`;
      return trainLabel + typeLabel;
    }).join('; ');
  };

  const buildLinkedTrainPayload = (formState) => {
    const currentLinks = Array.isArray(formState?.linked_trains) ? formState.linked_trains : [];
    const primaryTrainId = formState?.equipment_train_id ? String(formState.equipment_train_id) : '';
    const mappedLinks = currentLinks
      .filter((link) => link?.train_id)
      .map((link) => ({
        train_id: link.train_id,
        is_primary: primaryTrainId ? String(link.train_id) === primaryTrainId : !!link.is_primary,
        qualification_remark: link.qualification_remark || '',
        same_size_equipment: !!link.same_size_equipment,
        same_cleaning_method: !!link.same_cleaning_method,
        cv_batch_remark: link.cv_batch_remark || ''
      }));

    if (!primaryTrainId) return mappedLinks.filter((link) => !link.is_primary);

    const existingPrimary = mappedLinks.find((link) => String(link.train_id) === primaryTrainId);
    if (existingPrimary) {
      return mappedLinks.map((link) => ({
        ...link,
        is_primary: String(link.train_id) === primaryTrainId
      }));
    }

    return [{
      train_id: formState.equipment_train_id,
      is_primary: true,
      qualification_remark: '',
      same_size_equipment: false,
      same_cleaning_method: false,
      cv_batch_remark: ''
    }, ...mappedLinks.map((link) => ({ ...link, is_primary: false }))];
  };

  const getApiDisplayLabel = (api) => [api?.api_code, api?.api_name].filter(Boolean).join(' - ');

  const getApiSearchInputValue = (index, selectedAPI) => {
    if (Object.prototype.hasOwnProperty.call(apiSearchTerms, index)) {
      return apiSearchTerms[index];
    }
    return getApiDisplayLabel(selectedAPI);
  };

  const getFilteredApis = (index) => {
    const rawSearch = getApiSearchInputValue(index, null);
    const searchValue = String(rawSearch || '').trim().toLowerCase();
    return allApis
      .filter((apiMaster) => {
        const apiCode = String(apiMaster?.api_code || '').toLowerCase();
        const apiName = String(apiMaster?.api_name || '').toLowerCase();

        if (!searchValue) return Boolean(apiCode || apiName);
        return apiCode.includes(searchValue) || apiName.includes(searchValue);
      });
  };

  const buildProductApiFormEntry = (api = {}, fillUnit = 'mL') => {
    const linkedMasterApi = allApis.find((item) => String(item.api_id) === String(api?.api_id));
    return {
      api_id: api?.api_id || linkedMasterApi?.api_id || '',
      api_code: api?.api_code || linkedMasterApi?.api_code || '',
      api_name: api?.api_name || linkedMasterApi?.api_name || '',
      label_claim: api?.label_claim || '',
      label_claim_unit: api?.label_claim_unit || getDefaultLabelClaimUnit(fillUnit, unitConfig),
      iu_to_mg_conversion: api?.iu_to_mg_conversion || linkedMasterApi?.iu_to_mg_conversion || '',
      max_daily_dose: api?.max_daily_dose || linkedMasterApi?.max_daily_dose || '',
      max_daily_dose_unit: api?.max_daily_dose_unit || linkedMasterApi?.max_daily_dose_unit || 'mg',
      therapeutic_dose: api?.therapeutic_dose || linkedMasterApi?.therapeutic_dose || '',
      therapeutic_dose_unit: api?.therapeutic_dose_unit || linkedMasterApi?.therapeutic_dose_unit || 'mg',
      pde_value: api?.pde_value || linkedMasterApi?.pde_value || '',
      pde_unit: api?.pde_unit || linkedMasterApi?.pde_unit || '',
      ld50_value: api?.ld50_value || linkedMasterApi?.ld50_value || '',
      ld50_value_unit: api?.ld50_value_unit || api?.ld50_unit || linkedMasterApi?.ld50_value_unit || linkedMasterApi?.ld50_unit || '',
      solubility_classification: api?.solubility_classification || api?.solubility || linkedMasterApi?.solubility_classification || linkedMasterApi?.solubility || '',
      cleanability_rating: api?.cleanability_rating || api?.cleanability || linkedMasterApi?.cleanability_rating || linkedMasterApi?.cleanability || '',
      is_genotoxic: api?.is_genotoxic ?? linkedMasterApi?.is_genotoxic ?? false,
      is_carcinogenic: api?.is_carcinogenic ?? linkedMasterApi?.is_carcinogenic ?? false,
      is_sensitizer: api?.is_sensitizer ?? linkedMasterApi?.is_sensitizer ?? false,
      notes: api?.notes || linkedMasterApi?.notes || '',
      dosage_document: parseFiles(api?.dosage_document || api?.dose_document || linkedMasterApi?.dosage_document || linkedMasterApi?.dose_document),
      pde_document: parseFiles(api?.pde_document || linkedMasterApi?.pde_document),
      ld50_document: parseFiles(api?.ld50_document || linkedMasterApi?.ld50_document),
      solubility_document: parseFiles(api?.solubility_document || linkedMasterApi?.solubility_document),
      cleanability_document: parseFiles(api?.cleanability_document || linkedMasterApi?.cleanability_document),
      // Product-level cleanability override (blank = same as API). api_cleanability_rating is the API Master value.
      api_cleanability_rating: api?.api_cleanability_rating || linkedMasterApi?.cleanability_rating || '',
      cleanability_override: api?.cleanability_override || '',
      cleanability_override_reason: api?.cleanability_override_reason || '',
      cleanability_override_document: parseFiles(api?.cleanability_override_document),
      // Saved scoring-helper checklist behind the override; _pending (when set) is the unsaved change:
      // undefined = keep, null = remove, object = new answers (re-scored by the server on save)
      cleanability_override_assessment: api?.cleanability_override_assessment || null,
      cleanability_override_assessment_pending: api?.cleanability_override_assessment_pending
    };
  };

  const openTrainDetails = (trainId, mode = 'view') => {
    if (!trainId) return;
    navigate('/equipment-trains', {
      state: {
        ...(mode === 'edit' ? { openEditTrainId: trainId } : { openViewTrainId: trainId }),
        from: location.pathname
      }
    });
  };

  const openEquipmentDetails = (equipmentId, mode = 'view') => {
    if (!equipmentId) return;
    navigate('/equipment-master', {
      state: mode === 'edit' ? { openEditEquipmentId: equipmentId } : { openViewEquipmentId: equipmentId }
    });
  };

  const handleShowAllLinkedEquipment = async () => {
    if (!showLinkedTrainDetails) {
      // Fetching - load equipment for all linked trains
      try {
        const linkedTrains = getLinkedTrainsForProduct(viewingProduct);
        const membersData = {};
        
        for (const link of linkedTrains) {
          try {
            const res = await equipmentAPI.getTrainMembers(link.train_id);
            if (res?.data?.data) {
              membersData[link.train_id] = res.data.data;
            }
          } catch (err) {
            console.error(`Failed to load members for train ${link.train_id}:`, err);
          }
        }
        
        setLinkedTrainsMembers(membersData);
      } catch (error) {
        console.error('Error loading linked train equipment:', error);
      }
    }
    setShowLinkedTrainDetails((prev) => !prev);
  };

  const getActiveUnitName = () => getExportContextDisplay(localStorage.getItem('activePlant'), 'N/A');

  const getProductLineDisplay = (product) => {
    const productLines = String(product?.production_line || '')
      .split('|')
      .map((line) => line.trim())
      .filter(Boolean);

    if (productLines.length > 0) return productLines.join(', ');
    return getExportContextDisplay(localStorage.getItem('activeLine'), 'All Sections');
  };

  const extractApiRows = (payload) => {
    if (Array.isArray(payload?.data?.data?.rows)) return payload.data.data.rows;
    if (Array.isArray(payload?.data?.data)) return payload.data.data;
    if (Array.isArray(payload?.data?.rows)) return payload.data.rows;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
  };

  const loadProducts = async () => {
    try {
      const activeLine = localStorage.getItem('activeLine');
      const params = { page: 1, limit: 5000, sortBy: 'updated_at', sortOrder: 'DESC' };
      if (activeLine && activeLine !== 'All') {
        params.production_line = activeLine;
      }
      const response = await withTimeout(
        productsAPI.getAll(params),
        REQUEST_TIMEOUT_MS,
        'Products request timed out'
      );
      const nextProducts = extractProductRows(response);
      setProducts(nextProducts);
      setSelectedProducts((prev) => prev.filter((id) => nextProducts.some((item) => item.product_id === id)));
    } catch (error) {
      console.error('Error loading products:', error);
      if (error.message === 'Products request timed out') {
        toast.error('Loading products timed out. Please check the server and try again.', { toastId: 'products-timeout' });
      } else if (!error.response) {
        toast.error('Cannot reach backend API at http://localhost:5000. Start backend server and refresh.', { toastId: 'products-network' });
      } else {
        toast.error(error.response?.data?.message || error.response?.data?.error || 'Failed to load products', { toastId: 'products-failed' });
      }
      setProducts([]);
    } finally {
      setProductLoading(false);
    }
  };

  const loadRiskAssessments = async () => {
    try {
      const response = await riskAssessmentAPI.getAll();
      const rows = response?.data?.data || [];
      const byProduct = {};
      rows.forEach((row) => {
        if (row.product_id != null) byProduct[String(row.product_id)] = row;
      });
      setRiskByProductId(byProduct);
    } catch (error) {
      console.error('Error loading risk assessments:', error);
    }
  };

  const loadImpactAssessments = async () => {
    try {
      const response = await impactAssessmentAPI.getAll();
      const rows = response?.data?.data || [];
      const byProduct = {};
      rows.forEach((row) => {
        if (row.product_id != null) byProduct[String(row.product_id)] = row;
      });
      setImpactByProductId(byProduct);
    } catch (error) {
      console.error('Error loading impact assessments:', error);
    }
  };

  useEffect(() => {
    apiMasterAPI.getCleanabilityConfig()
      .then((res) => { if (res?.data?.data?.questions?.length) setCleanConfig(res.data.data); })
      .catch(() => { /* keep defaults */ });
  }, []);

  const loadAllApis = async () => {
    setApisLoading(true);
    try {
      const params = { page: 1, limit: 5000, sortBy: 'updated_at', sortOrder: 'desc' };
      const response = await withTimeout(
        apiMasterAPI.getAll(params),
        REQUEST_TIMEOUT_MS,
        'API list request timed out'
      );
      const apiRows = extractApiRows(response).filter((api) => api.is_active !== false);
      setAllApis(
        apiRows.map((api) => ({
          ...api,
          pde_unit: api.pde_unit || api.pde_unit || '',
          ld50_value_unit: api.ld50_value_unit || api.ld50_unit || ''
        }))
      );
    } catch (error) {
      setAllApis([]);
      console.error('Error loading API list:', error);
      if (error.message === 'API list request timed out') {
        toast.error('Loading API list timed out. Please check the server and try again.', { toastId: 'apis-timeout' });
      } else if (!error.response) {
        toast.error('Cannot reach backend API at http://localhost:5000. Start backend server and refresh.', { toastId: 'apis-network' });
      } else {
        toast.error(error.response?.data?.message || error.response?.data?.error || 'Failed to load API list', { toastId: 'apis-failed' });
      }
    } finally {
      setApisLoading(false);
    }
  };

  const loadTrains = async () => {
    const fetchInline = async () => {
      const inlineResponse = await withTimeout(
        equipmentAPI.getTrainsInline(),
        REQUEST_TIMEOUT_MS,
        'Equipment trains request timed out'
      );
      return extractTrainRows(inlineResponse);
    };

    try {
      const response = await withTimeout(
        equipmentAPI.getTrains(),
        REQUEST_TIMEOUT_MS,
        'Equipment trains request timed out'
      );
      let rows = extractTrainRows(response);

      if (!rows.length) {
        try {
          rows = await fetchInline();
        } catch (inlineError) {
          console.error('Error loading inline trains:', inlineError);
        }
      }

      trainsRef.current = rows;
      setTrains(rows);
      return rows;
    } catch (error) {
      console.error('Error loading trains:', error);
      let rows = [];
      try {
        rows = await fetchInline();
      } catch (inlineError) {
        console.error('Error loading inline trains:', inlineError);
        const message = error?.response?.data?.message || error?.message || 'Failed to load equipment trains';
        toast.error(message);
      }
      trainsRef.current = rows;
      setTrains(rows);
      return rows;
    }
  };

  const loadReportSettings = async () => {
    try {
      const response = await withTimeout(
        settingsAPI.getAll(),
        REQUEST_TIMEOUT_MS,
        'Settings request timed out'
      );

      const settings = response?.data?.data || {};
      setReportSettings((prev) => ({
        ...prev,
        report_client_name: settings.report_client_name || prev.report_client_name,
        report_client_address: settings.report_client_address || prev.report_client_address,
        report_document_name: settings.report_document_name || prev.report_document_name,
        report_document_rev_no: settings.report_document_rev_no || prev.report_document_rev_no,
        report_client_logo: settings.report_client_logo || prev.report_client_logo,
        report_primeclean_logo: settings.report_primeclean_logo || prev.report_primeclean_logo,
        report_footer_tagline: settings.report_footer_tagline || prev.report_footer_tagline,
        report_format_no_product_master: settings.report_format_no_product_master || prev.report_format_no_product_master,
        product_master_color_field_enabled: settings.product_master_color_field_enabled ?? prev.product_master_color_field_enabled,
        product_master_coating_fields_enabled: settings.product_master_coating_fields_enabled ?? prev.product_master_coating_fields_enabled
      }));

      const toTitle = (value) =>
        String(value || '')
          .split(' ')
          .map((w) => (w ? `${w.charAt(0).toUpperCase()}${w.slice(1)}` : ''))
          .join(' ')
          .trim();

      try {
        const solubilityRaw = settings.worst_case_solubility_ratings;
        if (solubilityRaw) {
          const parsed = JSON.parse(solubilityRaw);
          const next = Object.keys(parsed || {}).map(toTitle).filter(Boolean);
          if (next.length) setSolubilityOptions(Array.from(new Set(next)));
        }
      } catch (error) {
        setSolubilityOptions(DEFAULT_SOLUBILITY_OPTIONS);
      }

      try {
        const cleanabilityRaw = settings.worst_case_cleanability_ratings;
        if (cleanabilityRaw) {
          const parsed = JSON.parse(cleanabilityRaw);
          const next = Object.keys(parsed || {}).map(toTitle).filter(Boolean);
          if (next.length) setCleanabilityOptions(Array.from(new Set(next)));
        }
      } catch (error) {
        setCleanabilityOptions(DEFAULT_CLEANABILITY_OPTIONS);
      }

      setWcSettings({
        weights: parseJsonSetting(settings[WORST_CASE_SETTINGS_KEYS.weights], DEFAULT_WEIGHTS),
        solubility: parseJsonSetting(settings[WORST_CASE_SETTINGS_KEYS.solubility], DEFAULT_SOLUBILITY),
        cleanability: parseJsonSetting(settings[WORST_CASE_SETTINGS_KEYS.cleanability], DEFAULT_CLEANABILITY),
        pde: parseJsonSetting(settings[WORST_CASE_SETTINGS_KEYS.pde], DEFAULT_PDE_RATINGS),
        dose: parseJsonSetting(settings[WORST_CASE_SETTINGS_KEYS.dose], DEFAULT_DOSE_RATINGS),
        scoringMethod: settings[WORST_CASE_SETTINGS_KEYS.scoringMethod] === 'priority' ? 'priority' : 'weighted',
        priorityOrder: parseJsonSetting(settings[WORST_CASE_SETTINGS_KEYS.priorityOrder], DEFAULT_PRIORITY_ORDER),
        manualOverrides: parseJsonObject(settings[WORST_CASE_SETTINGS_KEYS.manualOverrides])
      });
    } catch (error) {
      // Keep defaults if settings are unavailable
    }
  };

  const loadCommercialRuntime = async () => {
    try {
      const [runtimeRes, fullRes] = await Promise.allSettled([
        withTimeout(commercialConfigAPI.getRuntime(), REQUEST_TIMEOUT_MS, 'Commercial runtime request timed out'),
        withTimeout(commercialConfigAPI.getFull(), REQUEST_TIMEOUT_MS, 'Commercial full request timed out')
      ]);

      let units = {};
      if (runtimeRes.status === 'fulfilled') {
        units = runtimeRes.value?.data?.data?.units || {};
        const integrations = runtimeRes.value?.data?.data?.integrations || {};
        setProductSyncSources(Object.entries(integrations).filter(([, item]) => item?.features?.product_sync).map(([key, item]) => ({ key, label: item.label })));
        setQmsLinked(!!integrations.aiventra_qms?.features?.change_control_link);
      } else if (fullRes.status === 'fulfilled') {
        units = fullRes.value?.data?.data?.units || {};
      }
      if (fullRes.status === 'fulfilled') {
        setCommercialLines(fullRes.value?.data?.data?.lines || []);
      }

      const nextConfig = {
        fillWeightUnits: Array.isArray(units.fill_weight_units) && units.fill_weight_units.length
          ? units.fill_weight_units
          : DEFAULT_UNIT_CONFIG.fillWeightUnits,
        fillVolumeUnits: Array.isArray(units.fill_volume_units) && units.fill_volume_units.length
          ? units.fill_volume_units
          : DEFAULT_UNIT_CONFIG.fillVolumeUnits,
        labelClaimWeightUnits: Array.isArray(units.label_claim_weight_units) && units.label_claim_weight_units.length
          ? units.label_claim_weight_units
          : DEFAULT_UNIT_CONFIG.labelClaimWeightUnits,
        labelClaimVolumeUnits: Array.isArray(units.label_claim_volume_units) && units.label_claim_volume_units.length
          ? units.label_claim_volume_units
          : DEFAULT_UNIT_CONFIG.labelClaimVolumeUnits
      };

      setUnitConfig(nextConfig);

      setProductFormData((prev) => {
        const availableFillUnits = getFillUnitValueList(nextConfig);
        const normalizedFillUnit = availableFillUnits.includes(prev.fill_unit)
          ? prev.fill_unit
          : (availableFillUnits[0] || prev.fill_unit || 'mL');
        const validLabelClaimUnits = getLabelClaimUnitValueList(normalizedFillUnit, nextConfig);
        const fallbackLabelUnit = getDefaultLabelClaimUnit(normalizedFillUnit, nextConfig);

        return {
          ...prev,
          fill_unit: normalizedFillUnit,
          apis: (prev.apis || []).map((api) => ({
            ...api,
            label_claim_unit: validLabelClaimUnits.includes(api.label_claim_unit)
              ? api.label_claim_unit
              : fallbackLabelUnit
          }))
        };
      });
    } catch (error) {
      // Keep default unit config if runtime endpoint is unavailable
    }
  };

  const promptForActionESign = (actionLabel) => {
    const password = window.prompt(`Enter your login password to ${actionLabel}:`);
    if (!password) return null;
    const comment = window.prompt(`Enter reason/comment to ${actionLabel}:`);
    if (!comment || !comment.trim()) {
      toast.error('Electronic signature reason/comment is required');
      return null;
    }
    return { password, comment: comment.trim() };
  };

  const getDeleteApprovalStage = (product) => {
    const displayStatus = normalizeDisplayStatus(product?.status);
    if (displayStatus === PRODUCT_STATUS.DRAFT) return 'draft';
    if (!product?.deletion_requested) return 'request';
    if (!product?.deletion_product_approved) return 'product';
    return 'qa';
  };

  const getDeleteActionLabel = (stage) => {
    if (stage === 'draft') return 'deactivate draft product';
    if (stage === 'request') return 'request product deactivation approval';
    if (stage === 'product') return 'complete Product deactivation approval stage';
    return 'complete QA deactivation approval stage';
  };

  const getDeleteConfirmMessage = (stage, productName) => {
    if (stage === 'draft') return `Deactivate draft product "${productName}" now?`;
    if (stage === 'request') return `Request deactivation approval for "${productName}"?`;
    if (stage === 'product') return `Complete Product deactivation approval stage for "${productName}"?`;
    return `Complete QA deactivation approval stage for "${productName}" and finalize deactivation?`;
  };

  const handleProductDelete = (product) => {
    if (!product?.product_id) return;
    const stage = getDeleteApprovalStage(product);
    const message = getDeleteConfirmMessage(stage, product.product_name);
    setConfirmationMessage(message);
    setPendingAction({ actionType: 'delete', data: { product, stage } });
    setESignMode('submit');
    setESignData({ password: '', comment: '', decision: 'approve' });
    setShowESignModal(true);
  };

  const handleGenerateRiskAssessment = async (product) => {
    if (!product?.product_id) return;
    try {
      await riskAssessmentAPI.generate(product.product_id);
      toast.success('Risk assessment generated');
      await loadProducts();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to generate risk assessment');
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      // Select all currently filtered/sorted products
      setSelectedProducts(sortedProducts.map(p => p.product_id));
    } else {
      setSelectedProducts([]);
    }
  };

  const handleSelectProduct = (id) => {
    if (selectedProducts.includes(id)) {
      setSelectedProducts(selectedProducts.filter(pId => pId !== id));
    } else {
      setSelectedProducts([...selectedProducts, id]);
    }
  };

  const handleRowClick = (product) => {
    setSelectedProductRow(product?.product_id || null);
  };

  const handleBulkDelete = async () => {
    if (selectedProducts.length === 0) return;
    if (!isCurrentUserSuperAdmin) {
      toast.error('Only Super Admin can bulk deactivate products');
      return;
    }

    const message = `Deactivate ${selectedProducts.length} selected product(s)?`;
    setConfirmationMessage(message);
    setPendingBulkDelete(selectedProducts);
    setESignMode('submit');
    setESignData({ password: '', comment: '', decision: 'approve' });
    setShowESignModal(true);
  };

  const handleOpenAddToExistingTrain = () => {
    if (!selectedProducts.length) return;
    setTargetTrainId('');

    setTargetTrainSearchTerm('');
    setShowTargetTrainDropdown(false);
    setShowAddToTrainModal(true);
    void loadTrains();
  };

  const handleBulkLinkProductsToTrain = async () => {
    if (!selectedProducts.length) return;
    if (!targetTrainId) return;
    if (!addToTrainESign.password || !addToTrainESign.comment.trim()) {
      toast.error('Electronic signature reason and password are required');
      return;
    }

    setProductLoading(true);
    try {
      const response = await productsAPI.bulkLinkTrain(selectedProducts, targetTrainId, {
        esign_password: addToTrainESign.password,
        esign_comment: addToTrainESign.comment.trim()
      });
      toast.success(response?.data?.message || 'Selected products linked to equipment train');
      setAddToTrainESign({ password: '', comment: '' });
      setShowAddToTrainModal(false);
      setTargetTrainId('');
      setTargetTrainSearchTerm('');
      setShowTargetTrainDropdown(false);

      setSelectedProducts([]);
      setSelectedProductRow(null);
      await loadProducts();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to link selected products to equipment train');
    } finally {
      setProductLoading(false);
    }
  };

  const handleProductView = async (product) => {
    setViewingProduct({
      ...product,
      attached_files: parseFiles(product.attached_files || product.attachedFiles)
    });
    setViewingTrainMembers([]);
    setLinkedTrainsMembers({});
    setShowLinkedTrainDetails(false);
    setShowProductViewModal(true);
    setViewLoading(true);

    try {
      const promises = [
        withTimeout(
          productsAPI.getById(product.product_id),
          REQUEST_TIMEOUT_MS,
          'Product details request timed out'
        )
      ];

      if (product.equipment_train_id) {
        promises.push(
          equipmentAPI.getTrainMembers(product.equipment_train_id)
            .catch(() => ({ data: { data: [] } }))
        );
      }

      const [productRes, trainRes] = await Promise.all(promises);

      const details = productRes?.data?.data;
      if (details) {
        setViewingProduct({
          ...product,
          ...details,
          attached_files: parseFiles(details.attached_files || details.attachedFiles)
        });
      }

      if (trainRes?.data?.data) {
        setViewingTrainMembers(trainRes.data.data);
      }
    } catch (error) {
      console.error('Error loading product details:', error);
      if (error.message === 'Product details request timed out') {
        toast.error('Loading product details timed out');
      } else {
        toast.error('Failed to load full product details');
      }
    } finally {
      setViewLoading(false);
    }
  };

  const handleProductStageApproval = (product, stage, decision = 'approve') => {
    // store approval pending and open e-sign modal
    setPendingApproval({ product, stage });
    setESignMode('submit');
    setESignData({ password: '', comment: '', decision });
    setShowESignModal(true);
  };

  const resetProductForm = () => {
    const defaultFillUnit = getFillUnitValueList(unitConfig)[0] || 'mL';
    setProductFormData({
      product_code: '',
      product_name: '',
      dosage_form: 'Dry Powder Injection',
      production_line: localStorage.getItem('activeLine') || '',
      equipment_train_id: '',
      equipment_train_name: '',
      equipment_train_code: '',
      batch_size: '',
      batch_size_unit: 'Unit',
      fill_weight: '',
      fill_unit: defaultFillUnit,
      reference_document: '',
      product_notes: '',
      product_color: '',
      product_flavour: '',
      coating_type: '',
      coating_agent: '',
      attachedFiles: [],
      linked_trains: [],
      apis: [{ api_id: '', label_claim: '', label_claim_unit: getDefaultLabelClaimUnit('mL'), iu_to_mg_conversion: '' }],
      status: PRODUCT_STATUS.DRAFT
    });
    setProductEditingId(null);
    setTrainSearchTerm('');
    setApiSearchTerms({});
    setActiveApiDropdownIndex(null);
    setApiFileAttachments({});
    editingApprovedProductRef.current = false;
    setPendingChangeRef(null);
  };

  const handleProductAdd = () => {
    resetProductForm();
    setShowProductModal(true);
  };

  const handleProductEdit = (product) => {
    if (normalizeDisplayStatus(product.status) === PRODUCT_STATUS.APPROVED || product.qa_approved) {
      setPendingChangeRef(null);
      setChangeRefGate({ product });
      return;
    }
    openProductEditForm(product);
  };

  const confirmChangeReference = async (data) => {
    setChangeRefBusy(true);
    try {
      setPendingChangeRef(data);
      editingApprovedProductRef.current = true;
      openProductEditForm(changeRefGate.product);
      setChangeRefGate(null);
    } finally {
      setChangeRefBusy(false);
    }
  };

  const openProductEditForm = (product) => {
    const availableFillUnits = getFillUnitValueList(unitConfig);
    const fillUnit = availableFillUnits.includes(product.fill_unit)
      ? product.fill_unit
      : (availableFillUnits[0] || 'mL');
    const primaryTrain = getPrimaryTrainLink(product);
    const selectedTrain = trainOptions.find((t) => String(t.train_id) === String(primaryTrain?.train_id || product.equipment_train_id));
    setTrainSearchTerm('');
    setProductEditingId(product.product_id);
    setProductFormData({
      ...product,
      equipment_train_id: primaryTrain?.train_id || product.equipment_train_id || '',
      equipment_train_name: selectedTrain?.train_name || primaryTrain?.train_name || product.train_name || '',
      equipment_train_code: selectedTrain?.train_code || primaryTrain?.train_code || product.train_code || '',
      production_line: product.production_line || '',
      fill_unit: fillUnit,
      attachedFiles: parseFiles(product.attached_files || product.attachedFiles),
      linked_trains: getLinkedTrainsForProduct(product),
      apis: (product.apis || []).map((api) => buildProductApiFormEntry(api, fillUnit))
    });
    setShowProductModal(true);
  };

  const handleChangeSelectedTrain = (product) => {
    if (!product?.product_id) return;
    setShowLinkedTrainDetails(false);
    setShowProductViewModal(false);
    navigate('/equipment-trains', {
      state: {
        linkProduct: {
          product_id: product.product_id,
          product_code: product.product_code || '',
          product_name: product.product_name || '',
          dosage_form: product.dosage_form || '',
          equipment_train_id: product.equipment_train_id || '',
          train_code: product.train_code || '',
          train_name: product.train_name || ''
        },
        from: location.pathname
      }
    });
  };

  const handleProductDuplicate = (product) => {
    const primaryTrain = getPrimaryTrainLink(product);
    const selectedTrain = trainOptions.find((t) => String(t.train_id) === String(primaryTrain?.train_id || product.equipment_train_id));
    const existingCodes = new Set((products || []).map((item) => String(item.product_code || '').trim().toLowerCase()));
    const baseCode = String(product.product_code || 'PRD').trim() || 'PRD';
    const withMaxLen = (value) => String(value || '').slice(0, 100);
    let candidateCode = `COPY-${baseCode}`;
    let copyIndex = 2;
    while (existingCodes.has(candidateCode.toLowerCase())) {
      candidateCode = withMaxLen(`COPY${copyIndex}-${baseCode}`);
      copyIndex += 1;
    }

    const availableFillUnits = getFillUnitValueList(unitConfig);
    const fillUnit = availableFillUnits.includes(product.fill_unit)
      ? product.fill_unit
      : (availableFillUnits[0] || 'mL');
    setProductEditingId(null);
    setApiSearchTerms({});
    setActiveApiDropdownIndex(null);
    setApiFileAttachments({});
    setTrainSearchTerm('');
    editingApprovedProductRef.current = false;
    setPendingChangeRef(null);
    setProductFormData({
      ...product,
      equipment_train_id: primaryTrain?.train_id || product.equipment_train_id || '',
      equipment_train_name: selectedTrain?.train_name || primaryTrain?.train_name || product.train_name || '',
      equipment_train_code: selectedTrain?.train_code || primaryTrain?.train_code || product.train_code || '',
      production_line: product.production_line || localStorage.getItem('activeLine') || '',
      product_code: candidateCode,
      product_name: `${product.product_name} (Copy)`,
      apis: (product.apis || []).map((api) => buildProductApiFormEntry(api, fillUnit)),
      attachedFiles: parseFiles(product.attached_files || product.attachedFiles),
      linked_trains: getLinkedTrainsForProduct(product),
      status: 'Draft'
    });
    setShowProductModal(true);
  };

  // Gate: before a brand-new product can be saved, screen it for cleaning-validation impact
  // (worst-case/MACO change, uncovered equipment) and require documentation of the outcome.
  // Edits to existing products keep the original save flow (already-assessed at creation time).
  const handleProceedToSave = async (mode) => {
    if (productEditingId || !isModuleOn('impact_assessment')) {
      return mode === 'draft' ? openESignModalForDraftSave() : openESignModalForActiveSave();
    }
    setPendingSaveMode(mode);
    setImpactModalMode('gate');
    setImpactValues(EMPTY_IMPACT_VALUES);
    setImpactEvaluation(null);
    setShowImpactModal(true);
    setImpactLoading(true);
    try {
      const trainIds = buildLinkedTrainPayload(productFormData).map((t) => t.train_id).filter(Boolean);
      const apiIds = (productFormData.apis || []).map((a) => a.api_id).filter(Boolean);
      const response = await impactAssessmentAPI.evaluate({
        train_ids: trainIds,
        api_ids: apiIds,
        batch_size: productFormData.batch_size,
        product_color: productFormData.product_color || '',
        coating_type: productFormData.coating_type || '',
        coating_agent: productFormData.coating_agent || '',
      });
      setImpactEvaluation(response?.data?.data || null);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to run the cleaning validation impact assessment');
      setShowImpactModal(false);
      setPendingSaveMode(null);
    } finally {
      setImpactLoading(false);
    }
  };

  const handleImpactModalChange = (field, value) => {
    setImpactValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleImpactConfirm = () => {
    setShowImpactModal(false);
    const mode = pendingSaveMode;
    setPendingSaveMode(null);
    if (mode === 'draft') openESignModalForDraftSave();
    else openESignModalForActiveSave();
  };

  const handleImpactModalClose = () => {
    setShowImpactModal(false);
    setPendingSaveMode(null);
  };

  const openImpactViewModal = async (product) => {
    setImpactModalMode('view');
    setImpactViewRecord(null);
    setShowImpactModal(true);
    setImpactLoading(true);
    try {
      const response = await impactAssessmentAPI.getByProductId(product.product_id);
      setImpactViewRecord(response?.data?.data || null);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.info('No impact assessment recorded for this product yet.');
      } else {
        toast.error(error.response?.data?.message || 'Failed to load impact assessment');
      }
      setShowImpactModal(false);
    } finally {
      setImpactLoading(false);
    }
  };

  // Documentation edit / submit / QA decision from the impact assessment view modal
  const handleImpactWorkflow = async (action, payload) => {
    const record = impactViewRecord;
    if (!record) return;
    try {
      let decisionRes = null;
      if (action === 'update') await impactAssessmentAPI.update(record.id, payload);
      else if (action === 'submit') await impactAssessmentAPI.submit(record.id, payload);
      else decisionRes = await impactAssessmentAPI.decide(record.id, payload);
      if (decisionRes?.data?.fullyApproved === false) {
        toast.info(decisionRes.data.message || `Signed. Awaiting level ${decisionRes.data.nextLevel} approval.`);
      } else {
        toast.success(action === 'update' ? 'Documentation saved' : action === 'submit' ? 'Submitted for QA approval' : `Impact assessment ${payload.decision}${payload.decision === 'approve' ? 'd' : 'ed'}`);
      }
      const refreshed = await impactAssessmentAPI.getByProductId(record.product_id);
      setImpactViewRecord(refreshed?.data?.data || null);
      loadImpactAssessments();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Impact assessment action failed');
      throw error;
    }
  };

  const handleSaveProduct = async (status, signature = null) => {
    const normalizedStatus = status === PRODUCT_STATUS.DRAFT
      ? PRODUCT_STATUS.DRAFT
      : PRODUCT_STATUS.PENDING_PRODUCT_APPROVAL;

    // Basic validation for both Draft and final submission
    if (!productFormData.product_code || !productFormData.product_name) {
      toast.error('Product Code and Name are required');
      return false;
    }

    // when saving as draft after e-sign we do not prompt additional confirmation
    // previous code had window.confirm; simply proceed
    if (normalizedStatus === PRODUCT_STATUS.DRAFT) {
      // no confirmation dialog per requirement
    }

    // Strict validation only for final submission
    if (normalizedStatus !== PRODUCT_STATUS.DRAFT) {
      if (productFormData.apis.length === 0) {
        toast.error('A product must have at least one API.');
        return false;
      }

      // Validate numeric fields are non-negative
      if (parseFloat(productFormData.batch_size) < 0) {
        toast.error('Batch size cannot be negative');
        return false;
      }
      if (productFormData.batch_size && !Number.isInteger(Number(productFormData.batch_size))) {
        toast.error('Batch size must be a whole number');
        return false;
      }
      if (parseFloat(productFormData.fill_weight) < 0) {
        toast.error('Fill weight cannot be negative');
        return false;
      }

      // Validate API fields
      for (const api of productFormData.apis) {
        const apiLabel = api.api_name || 'Selected API';

        if (parseFloat(api.label_claim) < 0) {
          toast.error(`Label claim for ${apiLabel} cannot be negative`);
          return false;
        }
        if (api.cleanability_override && !String(api.cleanability_override_reason || '').trim()) {
          toast.error(`Give a reason for the product cleanability override on ${apiLabel}`);
          return false;
        }
        if (api.max_daily_dose && parseFloat(api.max_daily_dose) < 0) {
          toast.error(`Max daily dose for ${apiLabel} cannot be negative`);
          return false;
        }
        if (api.therapeutic_dose && parseFloat(api.therapeutic_dose) < 0) {
          toast.error(`Therapeutic dose for ${apiLabel} cannot be negative`);
          return false;
        }
        if (api.pde_value && parseFloat(api.pde_value) < 0) {
          toast.error(`PDE value for ${apiLabel} cannot be negative`);
          return false;
        }
        if (api.ld50_value && parseFloat(api.ld50_value) < 0) {
          toast.error(`LD50 value for ${apiLabel} cannot be negative`);
          return false;
        }
        if (api.iu_to_mg_conversion && parseFloat(api.iu_to_mg_conversion) < 0) {
          toast.error(`IU conversion factor for ${apiLabel} cannot be negative`);
          return false;
        }

        if (api.label_claim_unit && (api.label_claim_unit.includes('IU') || api.label_claim_unit.includes('MIU'))) {
          const conversion = parseFloat(api.iu_to_mg_conversion);
          if (!conversion || conversion <= 0) {
            toast.error(`Please provide a valid IU/MIU to mg conversion factor for API ${apiLabel}`);
            return false;
          }
        }
      }
    }

    if (!signature?.password) {
      toast.error('Electronic signature password is required');
      return false;
    }
    if (!signature?.comment?.trim()) {
      toast.error('Electronic signature reason/comment is required');
      return false;
    }

    const formData = new FormData();
    formData.append('status', normalizedStatus);
    formData.append('esign_password', signature.password);
    formData.append('esign_comment', signature.comment.trim());
    if (productEditingId && editingApprovedProductRef.current && pendingChangeRef) {
      formData.append('change_reference_type', pendingChangeRef.reference_type);
      formData.append('change_reference_number', pendingChangeRef.reference_number);
      formData.append('change_reference_files', JSON.stringify(pendingChangeRef.reference_files || []));
    }

    // 1. Append simple product data
    const tabletProduct = String(productFormData.dosage_form || '').trim().toLowerCase() === 'tablet';
    Object.keys(productFormData).forEach(key => {
      if (key !== 'apis' && key !== 'attachedFiles' && key !== 'status' && key !== 'linked_trains') {
        const value = !tabletProduct && ['coating_type', 'coating_agent'].includes(key) ? '' : productFormData[key];
        if (value !== null && value !== undefined) {
          formData.append(key, value);
        }
      }
    });

    // 2. Handle main product reference files
    const existingFiles = [];
    (productFormData.attachedFiles || []).forEach(file => {
      if (file instanceof File) {
        formData.append('attachedFiles', file, file.name);
      } else if (typeof file === 'string') {
        existingFiles.push(file);
      }
    });
    formData.append('existingAttachedFiles', JSON.stringify(existingFiles));

    // 3. Process APIs: separate JSON data from files
    const apisForJson = productFormData.apis.map((api, index) => {
      const apiData = { ...api };
      const fileFields = ['dosage_document', 'pde_document', 'ld50_document', 'solubility_document', 'cleanability_document', 'cleanability_override_document'];
      // The server carries the saved checklist across itself; only a change (_pending) is sent
      delete apiData.cleanability_override_assessment;
      
      fileFields.forEach(field => {
        const files = apiData[field] ? (Array.isArray(apiData[field]) ? apiData[field] : [apiData[field]]) : [];
        const existingApiFiles = [];
        
        files.forEach((file) => {
          if (file instanceof File) {
            // Append the file with a unique name for the backend to parse
            formData.append(`api_files_${index}_${field}`, file, file.name);
          } else if (typeof file === 'string') {
            existingApiFiles.push(file);
          }
        });

        // For updates, the JSON will contain the list of existing files to keep
        apiData[field] = existingApiFiles;
      });
      
      return apiData;
    });

    formData.append('apis', JSON.stringify(apisForJson));
    formData.append('linked_trains', JSON.stringify(buildLinkedTrainPayload(productFormData)));

    if (!productEditingId) {
      formData.append('impact_contamination_method', impactValues.contamination_method || '');
      formData.append('impact_cross_contamination_note', impactValues.cross_contamination_note || '');
      formData.append('impact_justification', impactValues.justification || '');
      formData.append('impact_manual_override', impactValues.manual_override ? 'true' : 'false');
      formData.append('impact_override_reason', impactValues.override_reason || '');
      formData.append('impact_cv_required_override', impactValues.cv_required_override ? 'true' : 'false');
    }

    try {
      if (productEditingId) {
        await productsAPI.update(productEditingId, formData);
        toast.success(normalizedStatus === PRODUCT_STATUS.DRAFT
          ? 'Product updated as draft successfully'
          : 'Product updated and submitted for Product/QA approval');
      } else {
        await productsAPI.create(formData);
        toast.success(normalizedStatus === PRODUCT_STATUS.DRAFT
          ? 'Product saved as draft. Risk ranking assessment and CV impact assessment auto-generated — review anytime from the product actions menu.'
          : 'Product submitted for Product/QA approval. Risk ranking assessment and CV impact assessment auto-generated — review anytime from the product actions menu.');
      }
      setImpactValues(EMPTY_IMPACT_VALUES);
      loadImpactAssessments();
      setShowProductModal(false);
      setShowESignModal(false);
      setESignData({ password: '', comment: '', decision: 'approve' });
      resetProductForm();
      setProductCurrentPage(1);
      loadProducts();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data?.error || 'Operation failed');
      return false;
    }
  };

  // Editing an existing record with nothing changed: warn instead of asking for a signature.
  // Submitting a Draft for approval is still a real action, so that path is exempt.
  const isUnchangedEdit = (mode) => {
    if (!productEditingId || !initialProductRef.current) return false;
    const { json, status } = initialProductRef.current;
    if (mode === 'submit' && status === PRODUCT_STATUS.DRAFT) return false;
    return json === JSON.stringify(productFormData);
  };

  const openESignModalForDraftSave = () => {
    if (isUnchangedEdit('draft')) return toast.info('No changes were made — nothing to save, so no signature is required.');
    setESignMode('draft');
    setESignData({ password: '', comment: '', decision: 'approve' });
    setShowESignModal(true);
  };

  const openESignModalForActiveSave = () => {
    if (isUnchangedEdit('submit')) return toast.info('No changes were made — nothing to save, so no signature is required.');
    setESignMode('submit');
    setESignData({ password: '', comment: '', decision: 'approve' });
    setShowESignModal(true);
  };

  const handleBulkApprovalForProducts = () => {
    // Filter selected products that are pending product approval
    const eligibleProducts = selectedProducts.filter(productId => {
      const product = products.find(p => p.product_id === productId);
      return product && normalizeDisplayStatus(product.status) === PRODUCT_STATUS.PENDING_PRODUCT_APPROVAL;
    });

    if (eligibleProducts.length === 0) {
      toast.error('No eligible products for Product stage approval. Products must be in "Pending Product Approval" status.');
      return;
    }
    if (!canApproveProductStage) {
      toast.error('Only Manager or Super Admin can approve product-stage requests');
      return;
    }

    const message = `Approve ${eligibleProducts.length} selected product(s) for Product stage?`;
    setConfirmationMessage(message);
    setPendingBulkApproval({ items: eligibleProducts, approvalType: 'product' });
    setESignMode('submit');
    setESignData({ password: '', comment: '', decision: 'approve' });
    setShowESignModal(true);
  };

  const handleBulkApprovalForQA = () => {
    // Filter selected products that are pending QA approval
    const eligibleProducts = selectedProducts.filter(productId => {
      const product = products.find(p => p.product_id === productId);
      return product && normalizeDisplayStatus(product.status) === PRODUCT_STATUS.PENDING_QA_APPROVAL;
    });

    if (eligibleProducts.length === 0) {
      toast.error('No eligible products for QA stage approval. Products must be in "Pending QA Approval" status.');
      return;
    }
    if (!canApproveQaStage) {
      toast.error('Only QA Head or Super Admin can approve QA-stage requests');
      return;
    }

    const message = `Apply QA approval for ${eligibleProducts.length} selected product(s)?`;
    setConfirmationMessage(message);
    setPendingBulkApproval({ items: eligibleProducts, approvalType: 'qa' });
    setESignMode('submit');
    setESignData({ password: '', comment: '', decision: 'approve' });
    setShowESignModal(true);
  };

  const handleConfirmESignAndSave = async (decisionOverride) => {
    if (!eSignData.password) {
      toast.error('Electronic signature password is required');
      return;
    }

    // Handle bulk approvals (for Manager & QA Head)
    if (pendingBulkApproval) {
      const { items, approvalType } = pendingBulkApproval;
      setProductLoading(true);
      try {
        let successCount = 0;
        let failureCount = 0;
        const failureDetails = [];

        for (const productId of items) {
          try {
            const payload = {
              esign_password: eSignData.password,
              esign_comment: eSignData.comment,
              decision: decisionOverride || eSignData.decision
            };

            if (approvalType === 'product') {
              await productsAPI.approveProduct(productId, payload);
            } else if (approvalType === 'qa') {
              await productsAPI.approveQa(productId, payload);
            }
            successCount += 1;
          } catch (error) {
            failureCount += 1;
            const errorMsg = error?.response?.data?.message || error?.message || 'Unknown error';
            failureDetails.push(errorMsg);
            console.error(`Approval failed for product ${productId}:`, error?.response?.data || error.message);
          }
        }

        const approvalLabel = approvalType === 'product' ? 'Product' : 'QA';
        if (successCount > 0) {
          toast.success(`${successCount} product(s) ${approvalLabel} approval completed`);
          setSelectedProducts([]);
          setPendingBulkApproval(null);
          await loadProducts();
        } else if (failureCount > 0) {
          const errorDetail = failureDetails.length > 0 ? `: ${failureDetails[0]}` : '';
          toast.error(`Failed to approve ${failureCount} product(s)${errorDetail}`);
          setSelectedProducts([]);
          setPendingBulkApproval(null);
          await loadProducts();
        }
      } catch (error) {
        console.error('Bulk approval error:', error);
        toast.error(error?.response?.data?.message || error.message || 'Bulk approval failed');
      } finally {
        setProductLoading(false);
        setShowESignModal(false);
        setESignData({ password: '', comment: '', decision: 'approve' });
      }
      return;
    }

    // Handle pending action (add, edit, delete, import)
    if (pendingAction) {
      const { actionType, data } = pendingAction;
      
      if (actionType === 'delete') {
        const { product, stage } = data;
        setProductLoading(true);
        try {
          const payload = {
            esign_password: eSignData.password,
            esign_comment: eSignData.comment
          };
          if (stage !== 'draft') payload.approval_stage = stage;
          if (stage === 'product' || stage === 'qa') payload.decision = eSignData.decision;
          const response = await productsAPI.delete(product.product_id, payload);
          toast.success(response?.data?.message || 'Product deactivation requested');
          await loadProducts();
        } catch (error) {
          toast.error(error?.response?.data?.message || error?.message || 'Failed to deactivate product');
        } finally {
          setProductLoading(false);
          setPendingAction(null);
          setShowESignModal(false);
        }
        return;
      }

      if (actionType === 'import') {
        const { file, target } = data;
        setProductLoading(true);
        try {
          const rawRows = await parseImportFileRows(file);
          if (!Array.isArray(rawRows) || rawRows.length === 0) {
            toast.error('No import rows found in file');
            target.value = '';
            return;
          }
          const rows = normalizeImportRows(rawRows);

          if (!trainsRef.current.length && !trains.length) {
            await loadTrains();
          }

          const payloads = buildImportPayloads(rows);

          let successCount = 0;
          const failures = [];

          for (const item of payloads) {
            if (item.error) {
              failures.push({ item, error: item.error });
              continue;
            }
            try {
              const formData = new FormData();
              const { apis, ...productData } = item.payload;
              
              Object.keys(productData).forEach(key => {
                const value = productData[key];
                if (value !== null && value !== undefined) {
                  if (Array.isArray(value) || (typeof value === 'object' && !(value instanceof File))) {
                    formData.append(key, JSON.stringify(value));
                  } else {
                    formData.append(key, value);
                  }
                }
              });

              formData.append('apis', JSON.stringify(apis));
              formData.append('esign_password', eSignData.password);
              formData.append('esign_comment', eSignData.comment);
              // Bulk-imported products are existing/historical records, not newly
              // introduced ones — Risk/Impact Assessment are skipped here and can be
              // generated afterward (Risk Assessment via its "Generate" action; Impact
              // Assessment doesn't apply to already-existing products at all).
              formData.append('bulk_import', 'true');

              await productsAPI.create(formData);
              successCount += 1;
            } catch (error) {
              const backendMessage = error?.response?.data?.message;
              const backendError = error?.response?.data?.error;
              const errorMessage =
                (backendMessage && backendMessage !== 'Server error' ? backendMessage : null) ||
                (backendError && backendError !== 'Server error' ? backendError : null) ||
                error.message ||
                'Unknown error';
              failures.push({ item, error: errorMessage });
            }
          }

          if (successCount > 0) {
            toast.success(`Successfully imported ${successCount} product(s)`);
            if (failures.length > 0) {
              const failureDetails = failures.map(f => `${f.item.label}: ${f.error}`).join('; ');
              toast.error(`${failures.length} product(s) were NOT imported: ${failureDetails}`, { autoClose: false });
            }
            target.value = '';
            await loadProducts();
          } else if (failures.length > 0) {
            const failureDetails = failures.map(f => `${f.item.label}: ${f.error}`).join('; ');
            toast.error(`Failed to import ${failures.length} product(s): ${failureDetails}`);
            target.value = '';
            await loadProducts();
          }
        } catch (error) {
          toast.error(error.message || 'Import failed');
          target.value = '';
        } finally {
          setProductLoading(false);
          setPendingAction(null);
          setShowESignModal(false);
        }
        return;
      }
    }

    if (pendingApproval) {
      const { product, stage } = pendingApproval;
      setProductLoading(true);
      try {
        const payload = {
          esign_password: eSignData.password,
          esign_comment: eSignData.comment,
          decision: decisionOverride || eSignData.decision
        };
        if (stage === 'product') {
          await productsAPI.approveProduct(product.product_id, payload);
        } else {
          await productsAPI.approveQa(product.product_id, payload);
        }
        const label = stage === 'product' ? 'Product' : 'QA';
        toast.success(`${label} approval completed`);
        loadProducts();
      } catch (error) {
        toast.error(error?.response?.data?.message || `${stage === 'product' ? 'Product' : 'QA'} approval failed`);
      } finally {
        setProductLoading(false);
      }
      setPendingApproval(null);
      setShowESignModal(false);
      return;
    }

    if (pendingBulkDelete) {
      setProductLoading(true);
      try {
        const signature = {
          password: eSignData.password,
          comment: eSignData.comment
        };
        
        try {
          const response = await productsAPI.bulkDelete(pendingBulkDelete, {
            esign_password: signature.password,
            esign_comment: signature.comment
          });
          toast.success(response?.data?.message || 'Selected products deactivated successfully');
          setSelectedProducts([]);
          await loadProducts();
        } catch (error) {
          console.error('Bulk deactivate error:', error);

          if (error.response && error.response.status === 401) {
            toast.error(error.response.data.message || 'Invalid electronic signature credentials');
          } else {
            toast.warn('Bulk deactivation failed. Attempting to deactivate items individually...');

            let successCount = 0;
            const failures = [];

            for (const id of pendingBulkDelete) {
              try {
                await productsAPI.delete(id, {
                  esign_password: signature.password,
                  esign_comment: signature.comment
                });
                successCount += 1;
              } catch (deleteError) {
                failures.push(id);
              }
            }

            if (successCount > 0) {
              toast.success(`Deactivated ${successCount} product(s).`);
              setSelectedProducts([]);
              await loadProducts();
            }
            if (failures.length > 0) {
              toast.error(`Failed to deactivate ${failures.length} product(s).`);
              setSelectedProducts([]);
              await loadProducts();
            }
          }
        }
      } finally {
        setProductLoading(false);
        setPendingBulkDelete(null);
        setShowESignModal(false);
        setESignData({ password: '', comment: '', decision: 'approve' });
      }
      return;
    }

    if (pendingDelete) {
      // process deletion instead of save
      const { product, stage } = pendingDelete;
      setProductLoading(true);
      try {
        const payload = {
          esign_password: eSignData.password,
          esign_comment: eSignData.comment,
          decision: eSignData.decision
        };
        if (stage !== 'draft') payload.approval_stage = stage;
        const response = await productsAPI.delete(product.product_id, payload);
        toast.success(response?.data?.message || 'Product deactivation requested');
        loadProducts();
      } catch (error) {
        toast.error(error?.response?.data?.message || error?.message || 'Failed to deactivate product');
      } finally {
        setProductLoading(false);
      }
      setPendingDelete(null);
      setShowESignModal(false);
      return;
    }

    const targetStatus = eSignMode === 'draft'
      ? PRODUCT_STATUS.DRAFT
      : PRODUCT_STATUS.PENDING_PRODUCT_APPROVAL;
    await handleSaveProduct(targetStatus, eSignData);
  };

  const handleProductApiChange = (index, field, value) => {
    const updatedApis = [...productFormData.apis];
    const nextApi = { ...(updatedApis[index] || {}) };
    nextApi[field] = value;

    if (field === 'cleanability_override') {
      const checklist = nextApi.cleanability_override_assessment_pending !== undefined
        ? nextApi.cleanability_override_assessment_pending
        : nextApi.cleanability_override_assessment;
      if (checklist && checklist.final_rating !== value) {
        nextApi.cleanability_override_assessment_pending = null;
        toast.info('Product cleanability changed by hand, so its checklist result was removed. Run "Help me rate" again to keep it as the basis.');
      }
    }

    if (field === 'api_id') {
      const defaultLabelUnit = getDefaultLabelClaimUnit(productFormData.fill_unit, unitConfig);
      const selected = allApis.find((item) => String(item.api_id) === String(value));
      if (selected) {
        updatedApis[index] = buildProductApiFormEntry({
          ...nextApi,
          api_id: value,
          label_claim: '',
          label_claim_unit: defaultLabelUnit
        }, productFormData.fill_unit);
        setProductFormData({ ...productFormData, apis: updatedApis });
        return;
      }
      nextApi.api_code = '';
      nextApi.api_name = '';
      nextApi.label_claim = '';
      nextApi.label_claim_unit = defaultLabelUnit;
    }

    updatedApis[index] = nextApi;
    setProductFormData({ ...productFormData, apis: updatedApis });
  };

  const handleFillUnitChange = (newFillUnit) => {
    const validUnits = getLabelClaimUnitValueList(newFillUnit, unitConfig);
    const fallbackUnit = getDefaultLabelClaimUnit(newFillUnit, unitConfig);

    setProductFormData((prev) => ({
      ...prev,
      fill_unit: newFillUnit,
      apis: (prev.apis || []).map((api) => ({
        ...api,
        label_claim_unit: validUnits.includes(api.label_claim_unit) ? api.label_claim_unit : fallbackUnit
      }))
    }));
  };

  const handleProductApiFileAdd = (index, field, newFiles) => {
    const updatedApis = [...productFormData.apis];
    const currentVal = updatedApis[index][field];
    const currentFiles = Array.isArray(currentVal) ? currentVal : (currentVal ? [currentVal] : []);
    updatedApis[index][field] = [...currentFiles, ...newFiles];
    setProductFormData({ ...productFormData, apis: updatedApis });
  };

  const handleProductApiFileRemove = (index, field, fileIndex) => {
    const updatedApis = [...productFormData.apis];
    const currentVal = updatedApis[index][field];
    const currentFiles = Array.isArray(currentVal) ? currentVal : (currentVal ? [currentVal] : []);
    updatedApis[index][field] = currentFiles.filter((_, i) => i !== fileIndex);
    setProductFormData({ ...productFormData, apis: updatedApis });
  };

  const addProductApiToForm = () => {
    setProductFormData({
      ...productFormData,
      apis: [...productFormData.apis, { api_id: '', label_claim: '', label_claim_unit: getDefaultLabelClaimUnit(productFormData.fill_unit, unitConfig), iu_to_mg_conversion: '' }]
    });
  };

  const removeProductApiFromForm = (index) => {
    const updatedApis = productFormData.apis.filter((_, i) => i !== index);
    const updatedSearchTerms = { ...apiSearchTerms };
    delete updatedSearchTerms[index];
    setProductFormData({ ...productFormData, apis: updatedApis });
    setApiSearchTerms(updatedSearchTerms);
    setActiveApiDropdownIndex((current) => (current === index ? null : current));
  };

  const calculateContentPerUnit = (index) => {
    const api = productFormData.apis[index];
    const fillWeight = parseFloat(productFormData.fill_weight);
    const fillUnit = productFormData.fill_unit;
    const claim = parseFloat(api.label_claim);
    const unit = api.label_claim_unit;
    const conversion = parseFloat(api.iu_to_mg_conversion);

    if (!claim || !unit) return { display: '-', mg: 0 };

    let display = '';
    let mg = 0;

    if (unit.includes('/Unit')) {
      display = `${claim} ${unit.split('/')[0]}`;
      if (unit === 'mg/Unit') mg = claim;
      else if (unit === 'g/Unit') mg = claim * 1000;
      else if (unit === 'mcg/Unit') mg = claim / 1000;
      else if ((unit === 'IU/Unit' || unit === 'MIU/Unit') && conversion) {
         const iu = unit === 'MIU/Unit' ? claim * 1000000 : claim;
         mg = iu / conversion;
      }
    } else if (fillWeight) {
      let weightInBase = fillWeight;
      if (fillUnit === 'L' || fillUnit === 'kg') weightInBase = fillWeight * 1000;
      else if (fillUnit === 'mg') weightInBase = fillWeight / 1000;
      
      if (unit === '%') {
        const amountG = (claim / 100) * weightInBase; 
        display = `${(amountG * 1000).toFixed(2)} mg`;
        mg = amountG * 1000;
      } else {
        const total = claim * weightInBase;
        const base = unit.split('/')[0];
        display = `${total.toFixed(2)} ${base}`;
        
        if (base === 'mg') mg = total;
        else if (base === 'g') mg = total * 1000;
        else if (base === 'mcg') mg = total / 1000;
        else if ((base === 'IU' || base === 'MIU') && conversion) {
           const iu = base === 'MIU' ? total * 1000000 : total;
           mg = iu / conversion;
        }
      }
    } else {
      display = 'Enter Fill Wt';
    }

    return { display, mg };
  };

  const getLabelClaimUnitOptions = (fillUnit) => {
    const isVolume = isVolumeFillUnit(fillUnit, unitConfig);
    const isWeight = isWeightFillUnit(fillUnit, unitConfig);
    const volumeUnits = (unitConfig.labelClaimVolumeUnits || []).filter((unit) => unit !== '%');
    const weightUnits = (unitConfig.labelClaimWeightUnits || []);
    const weightPerUnitUnits = weightUnits.filter((unit) => unit.includes('/Unit'));
    const weightPerGramUnits = weightUnits.filter((unit) => unit.includes('/g'));
    const percentageUnits = [...new Set([
      ...(isVolume ? (unitConfig.labelClaimVolumeUnits || []) : []),
      ...(isWeight ? (unitConfig.labelClaimWeightUnits || []) : [])
    ])].filter((unit) => unit === '%');
    if (percentageUnits.length === 0) percentageUnits.push('%');

    return (
      <>
        {isVolume && volumeUnits.length > 0 && (
          <optgroup label="Per mL / Volume">
            {volumeUnits.map((unit) => (
              <option key={unit} value={unit}>{unit}</option>
            ))}
          </optgroup>
        )}
        {isWeight && weightPerUnitUnits.length > 0 && (
          <optgroup label="Per Unit (Weight Based Product)">
            {weightPerUnitUnits.map((unit) => (
              <option key={unit} value={unit}>{unit}</option>
            ))}
          </optgroup>
        )}
        {isWeight && weightPerGramUnits.length > 0 && (
          <optgroup label="Per Gram">
            {weightPerGramUnits.map((unit) => (
              <option key={unit} value={unit}>{unit}</option>
            ))}
          </optgroup>
        )}
        <optgroup label="Percentage">
          {percentageUnits.map((unit) => (
            <option key={unit} value={unit}>{unit}</option>
          ))}
        </optgroup>
      </>
    );
  };

  // Product Filtering & Sorting
  const dosageFilterOptions = ['All', ...Array.from(new Set(products.map((p) => p.dosage_form).filter(Boolean))).sort()];

  const trainFilterOptions = (() => {
    const seen = new Set();
    const options = [];
    products.forEach((product) => {
      const links = Array.isArray(product.linked_trains) && product.linked_trains.length
        ? product.linked_trains
        : product.equipment_train_id
          ? [{ train_id: product.equipment_train_id, train_code: product.train_code || '', train_name: product.train_name || '' }]
          : [];
      links.forEach((link) => {
        const id = String(link.train_id || '');
        if (!id || seen.has(id)) return;
        // Only include trains matching current plant + line context
        const train = trains.find((t) => String(t.train_id) === id);
        if (train) {
          const matchesPlant = !productPlantFilter || productPlantFilter === 'All' ||
            String(train.unit_plant || '').includes(productPlantFilter) ||
            String(train.unit_id || '').includes(productPlantFilter);
          const matchesLine = !productLineFilter || productLineFilter === 'All' ||
            String(train.production_line || '').includes(productLineFilter) ||
            String(train.line_id || '').includes(productLineFilter);
          if (!matchesPlant || !matchesLine) return;
        }
        seen.add(id);
        const label = train
          ? [train.train_code, train.train_name].filter(Boolean).join(' - ')
          : [link.train_code, link.train_name].filter(Boolean).join(' - ') || id;
        options.push({ train_id: id, label });
      });
    });
    return options.sort((a, b) => a.label.localeCompare(b.label));
  })();

  const getSortValue = (product, sortKey) => {
    switch (sortKey) {
      case 'batch_size':
        return Number(product.batch_size) || 0;
      case 'status':
        return normalizeDisplayStatus(product.status).toLowerCase();
      case 'apis':
        return (product.apis || []).length;
      case 'updated_at':
        return product.updated_at ? new Date(product.updated_at).getTime() : 0;
      default: {
        const value = product[sortKey];
        return typeof value === 'string' ? value.toLowerCase() : (value ?? '');
      }
    }
  };

  const handleHeaderSort = (sortKey) => {
    if (productSortBy === sortKey) {
      setProductSortOrder(productSortOrder === 'asc' ? 'desc' : 'asc');
      return;
    }
    setProductSortBy(sortKey);
    setProductSortOrder('asc');
  };

  const renderSortIndicator = (sortKey) => {
    const isActive = productSortBy === sortKey;
    return (
      <span style={{ display: 'inline-flex', flexDirection: 'column', marginLeft: '6px', lineHeight: 0.75, verticalAlign: 'middle' }}>
        <span style={{ color: isActive && productSortOrder === 'asc' ? 'var(--primary-color, #2563eb)' : 'var(--text-light)', fontSize: 'var(--fs-2xs)' }}>{'\u25B2'}</span>
        <span style={{ color: isActive && productSortOrder === 'desc' ? 'var(--primary-color, #2563eb)' : 'var(--text-light)', fontSize: 'var(--fs-2xs)', marginTop: '-2px' }}>{'\u25BC'}</span>
      </span>
    );
  };

  const filteredProducts = products.filter((product) => {
    const searchText = productSearchTerm.trim().toLowerCase();
    const matchesSearch = !searchText ||
      (product.product_code || '').toLowerCase().includes(searchText) ||
      (product.product_name || '').toLowerCase().includes(searchText) ||
      (product.dosage_form || '').toLowerCase().includes(searchText);

    const matchesDosage = productDosageFilter === 'All' || (product.dosage_form || '') === productDosageFilter;
    const productLines = String(product.production_line || '')
      .split('|')
      .map((line) => line.trim())
      .filter(Boolean);
    const matchesLine = productLineFilter === 'All' || productLines.includes(productLineFilter);

    const productTrainLinks = Array.isArray(product.linked_trains) && product.linked_trains.length
      ? product.linked_trains
      : product.equipment_train_id
        ? [{ train_id: product.equipment_train_id }]
        : [];
    const matchesTrain = productTrainFilter === 'All' ||
      productTrainLinks.some((link) => String(link.train_id) === productTrainFilter);

    const status = normalizeDisplayStatus(product.status);
    const matchesStatus = productStatusCard === 'All'
      || (productStatusCard === 'Approved' && status === PRODUCT_STATUS.APPROVED)
      || (productStatusCard === 'Pending' && [PRODUCT_STATUS.PENDING_PRODUCT_APPROVAL, PRODUCT_STATUS.PENDING_QA_APPROVAL].includes(status))
      || (productStatusCard === 'Draft' && status === PRODUCT_STATUS.DRAFT);

    return matchesSearch && matchesDosage && matchesLine && matchesTrain && matchesStatus;
  });

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    const aVal = getSortValue(a, productSortBy);
    const bVal = getSortValue(b, productSortBy);
    if (aVal > bVal) return productSortOrder === 'asc' ? 1 : -1;
    if (aVal < bVal) return productSortOrder === 'asc' ? -1 : 1;
    return 0;
  });

  // Product Pagination
  const productIndexOfLastItem = productCurrentPage * itemsPerPage;
  const productIndexOfFirstItem = productIndexOfLastItem - itemsPerPage;
  const productCurrentItems = sortedProducts.slice(productIndexOfFirstItem, productIndexOfLastItem);

  const getExportMetadata = (recordCount) => {
    let savedUser = null;
    try {
      const userRaw = localStorage.getItem('user');
      savedUser = userRaw ? JSON.parse(userRaw) : null;
    } catch (error) {
      savedUser = null;
    }

    const exportedAt = new Date();
    const exportedBy =
      savedUser?.username ||
      savedUser?.full_name ||
      savedUser?.name ||
      savedUser?.employee_id ||
      'Unknown User';
    const exportedByRole = savedUser?.role || 'Unknown Role';
    const systemName = 'PrimeClean';
    const systemHost = window?.location?.origin || 'Unknown Origin';
    const systemNode = window?.location?.hostname || 'Unknown Host';
    const unitName = getExportContextDisplay(localStorage.getItem('activePlant'), 'N/A');
    const productionLine = getExportContextDisplay(localStorage.getItem('activeLine'), 'All Sections');
    const equipmentTrain = productTrainFilter === 'All'
      ? 'All Trains'
      : (trainFilterOptions.find((t) => t.train_id === productTrainFilter)?.label || productTrainFilter);

    return {
      reportId: `PM-EXP-${exportedAt.getTime()}`,
      exportedAt,
      exportedAtDisplay: exportedAt.toLocaleString(),
      exportedAtIso: exportedAt.toISOString(),
      exportedBy,
      exportedByRole,
      systemName,
      systemHost,
      systemNode,
      unitName,
      productionLine,
      equipmentTrain,
      recordCount
    };
  };

  const resolveApiReference = (api) => {
    const apiId = api?.api_id;
    const linkedApi = allApis.find((item) => String(item.api_id) === String(apiId));
    return {
      apiCode: api?.api_code || linkedApi?.api_code || '',
      apiName: api?.api_name || linkedApi?.api_name || 'Unknown API'
    };
  };

  const getFileNamesForExport = (value) => {
    return parseFiles(value)
      .map((file) => {
        if (typeof file === 'string') return file;
        if (file && typeof file === 'object' && file.name) return file.name;
        return '';
      })
      .filter(Boolean)
      .join(' | ');
  };

  const getExportContextDisplay = (value, fallback) => {
    const normalized = String(value || '').trim();
    if (!normalized) return fallback;
    if (normalized.toLowerCase() === 'all') return fallback;
    return normalized;
  };

  const getExportProducts = () => {
    return [...filteredProducts].sort((a, b) => {
      const aCode = (a.product_code || '').toLowerCase();
      const bCode = (b.product_code || '').toLowerCase();
      if (aCode > bCode) return 1;
      if (aCode < bCode) return -1;
      return 0;
    });
  };

  const drawPdfGridTable = (doc, {
    headers,
    rows,
    startY,
    topMargin,
    bottomMargin,
    leftMargin,
    rightMargin,
    headerFillColor,
    headerTextColor,
    bodyTextColor,
    alternateRowColor,
    fontSize,
    colWidthWeights,
    drawHeaderFooter
  }) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const tableWidth = pageWidth - leftMargin - rightMargin;
    const colCount = headers.length;
    const normalizedWeights = Array.isArray(colWidthWeights) && colWidthWeights.length === colCount
      ? colWidthWeights.map((weight) => {
          const parsed = Number(weight);
          return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
        })
      : Array.from({ length: colCount }, () => 1);
    const totalWeight = normalizedWeights.reduce((sum, weight) => sum + weight, 0) || colCount;
    const colWidths = normalizedWeights.map((weight) => (tableWidth * weight) / totalWeight);
    const cellPadding = 3;
    const lineHeight = Math.max(8, fontSize + 2);
    const minRowHeight = Math.max(14, lineHeight + 2);
    const headerFontSize = Math.max(fontSize + 0.6, 6.4);
    const headerLineHeight = Math.max(8, headerFontSize + 1.8);
    const originalFontSize = typeof doc.getFontSize === 'function' ? doc.getFontSize() : fontSize;
    doc.setFontSize(headerFontSize);
    const wrappedHeaderCells = headers.map((header, index) =>
      doc
        .splitTextToSize(String(header || '').trim(), Math.max(colWidths[index] - (cellPadding * 2), 10))
        .map((line) => String(line || '').trim())
        .filter(Boolean)
    );
    doc.setFontSize(originalFontSize);
    const maxHeaderLines = wrappedHeaderCells.reduce((acc, lines) => Math.max(acc, lines.length || 1), 1);
    const headerHeight = Math.max(minRowHeight + 4, (maxHeaderLines * headerLineHeight) + (cellPadding * 2) + 2);
    let y = startY;

    const drawHeaderRow = () => {
      // Keep table headers unfilled for maximum readability in PDF viewers.
      doc.setFillColor(255, 255, 255);
      doc.rect(leftMargin, y, tableWidth, headerHeight, 'F');
      doc.setDrawColor(180, 192, 208);
      doc.setTextColor(...headerTextColor);
      doc.setFontSize(headerFontSize);
      doc.setFont('helvetica', 'bold');
      let x = leftMargin;
      headers.forEach((_, index) => {
        doc.rect(x, y, colWidths[index], headerHeight, 'S');
        const lineArray = Array.isArray(wrappedHeaderCells[index]) && wrappedHeaderCells[index].length > 0
          ? wrappedHeaderCells[index]
          : ['-'];
        lineArray.forEach((line, lineIndex) => {
          const textY = y + cellPadding + headerFontSize + (lineIndex * headerLineHeight);
          if (textY <= y + headerHeight - cellPadding) {
            doc.text(line, x + cellPadding, textY);
          }
        });
        x += colWidths[index];
      });
      doc.setFont('helvetica', 'normal');
      y += headerHeight;
    };

    const addNewPageWithHeader = () => {
      doc.addPage();
      if (typeof drawHeaderFooter === 'function') drawHeaderFooter();
      y = topMargin;
      drawHeaderRow();
    };

    if (y + headerHeight > pageHeight - bottomMargin) {
      addNewPageWithHeader();
    } else {
      drawHeaderRow();
    }

    rows.forEach((row, rowIndex) => {
      const wrappedCells = row.map((cell, index) => {
        const cellText = String(cell ?? '-');
        return doc.splitTextToSize(cellText, colWidths[index] - (cellPadding * 2));
      });
      const maxLines = wrappedCells.reduce((acc, lines) => Math.max(acc, lines.length || 1), 1);
      const rowHeight = Math.max(minRowHeight, (maxLines * lineHeight) + (cellPadding * 2));

      if (y + rowHeight > pageHeight - bottomMargin) {
        addNewPageWithHeader();
      }

      if (rowIndex % 2 === 1) {
        doc.setFillColor(...alternateRowColor);
        doc.rect(leftMargin, y, tableWidth, rowHeight, 'F');
      }

      doc.setTextColor(...bodyTextColor);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(fontSize);

      let x = leftMargin;
      wrappedCells.forEach((lines, index) => {
        doc.setDrawColor(203, 213, 225);
        doc.rect(x, y, colWidths[index], rowHeight);

        const lineArray = Array.isArray(lines) && lines.length > 0 ? lines : ['-'];
        lineArray.forEach((line, lineIndex) => {
          const textY = y + cellPadding + fontSize + (lineIndex * lineHeight);
          if (textY <= y + rowHeight - cellPadding) {
            doc.text(line, x + cellPadding, textY);
          }
        });
        x += colWidths[index];
      });

      y += rowHeight;
    });

    return y;
  };

  // Product Export to PDF (Landscape with template header/footer)
  const exportProductToPDF = async () => {
    // Header/footer from Settings → App Configuration → Reports & Documents.
    const reportSettings = withReportProfile('product_master', 'Product Master Report');
    try {
      const sourceProducts = getExportProducts();
      if (sourceProducts.length === 0) {
        toast.info('No products available to export');
        return;
      }

      const metadata = getExportMetadata(sourceProducts.length);
      const [clientLogoData, primeLogoData] = await Promise.all([
        toDataUrlIfPossible(reportSettings.report_client_logo),
        toDataUrlIfPossible(reportSettings.report_primeclean_logo)
      ]);

      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      const headerTop = 16;
      const headerRowSplit = 44;
      const headerBottom = 82;
      const leftLogoWidth = 130;
      const rightLogoWidth = 130;
      const bodyTop = headerBottom + 8;
      const footerTop = pageHeight - 42;
      const formatCellValue = (value) => {
        if (value === null || value === undefined) return '-';
        const text = String(value).trim();
        return text ? text : '-';
      };
      const combineValueUnit = (value, unit) => {
        const normalizedValue = formatCellValue(value);
        const normalizedUnit = formatCellValue(unit);
        if (normalizedValue === '-' && normalizedUnit === '-') return '-';
        if (normalizedValue === '-') return normalizedUnit;
        if (normalizedUnit === '-') return normalizedValue;
        return `${normalizedValue} ${normalizedUnit}`;
      };
      const truncatePdfHeaderText = (value, maxWidth) => {
        const text = String(value || '').trim();
        if (!text) return '-';
        if (doc.getTextWidth(text) <= maxWidth) return text;

        let truncated = text;
        while (truncated.length > 0 && doc.getTextWidth(`${truncated}...`) > maxWidth) {
          truncated = truncated.slice(0, -1);
        }

        return truncated ? `${truncated}...` : '...';
      };
      const drawHeaderFooter = () => {
        const pageInfo = doc.internal.getCurrentPageInfo ? doc.internal.getCurrentPageInfo() : null;
        const pageNo = pageInfo?.pageNumber || doc.internal.getNumberOfPages();
        const totalPages = doc.internal.getNumberOfPages();

        // Header table outline similar to provided template
        doc.setDrawColor(80, 80, 80);
        doc.rect(20, headerTop, pageWidth - 40, headerBottom - headerTop);
        doc.line(20 + leftLogoWidth, headerTop, 20 + leftLogoWidth, headerRowSplit);
        doc.line(pageWidth - 20 - rightLogoWidth, headerTop, pageWidth - 20 - rightLogoWidth, headerRowSplit);
        doc.line(20, headerRowSplit, pageWidth - 20, headerRowSplit);

        if (clientLogoData) {
          doc.addImage(clientLogoData, 'PNG', 26, headerTop + 7, leftLogoWidth - 12, 18);
        } else {
          doc.setFontSize(8);
          doc.text('Client Logo', 26, headerTop + 19);
        }

        if (primeLogoData) {
          doc.addImage(primeLogoData, 'PNG', pageWidth - 20 - rightLogoWidth + 6, headerTop + 7, rightLogoWidth - 12, 18);
        } else {
          doc.setFontSize(8);
          doc.text('PrimeClean Logo', pageWidth - 20 - rightLogoWidth + 8, headerTop + 19);
        }

        doc.setFontSize(8.6);
        doc.setTextColor(15, 23, 42);
        doc.text(reportSettings.report_client_name || 'Client Name', pageWidth / 2, headerTop + 16, { align: 'center' });
        doc.setFontSize(7.6);
        doc.text(unitReportAddress(reportSettings) || 'Client Address', pageWidth / 2, headerTop + 27, { align: 'center' });

        doc.setFontSize(8.5);
        doc.text(
          truncatePdfHeaderText(`Document Name: ${reportSettings.report_document_name || 'Product Master Report'}`, 220),
          26,
          headerRowSplit + 14
        );
        doc.text(
          truncatePdfHeaderText(`Unit Name: ${metadata.unitName}`, 210),
          pageWidth / 2,
          headerRowSplit + 14,
          { align: 'center' }
        );
        doc.text(`Effective Date: ${metadata.exportedAtDisplay}`, pageWidth - 24, headerRowSplit + 14, { align: 'right' });
        doc.text(
          truncatePdfHeaderText(`Section: ${metadata.productionLine}`, 260),
          26,
          headerRowSplit + 26
        );
        doc.text(
          truncatePdfHeaderText(`Document & Rev. No.: ${reportSettings.report_document_rev_no || '01'}`, 210),
          pageWidth / 2,
          headerRowSplit + 26,
          { align: 'center' }
        );
        doc.text(
          truncatePdfHeaderText(`Equipment Train: ${metadata.equipmentTrain}`, 220),
          pageWidth - 24,
          headerRowSplit + 26,
          { align: 'right' }
        );
        // Footer
        doc.line(20, footerTop, pageWidth - 20, footerTop);
        doc.setFontSize(7.2);
        doc.text(
          `Report ID: ${metadata.reportId}  Generated By: ${metadata.exportedBy} (${metadata.exportedByRole})  System: ${metadata.systemName} | ${metadata.systemNode}`,
          pageWidth / 2,
          pageHeight - 27,
          { align: 'center' }
        );
        if (reportSettings.report_format_no_product_master) {
          doc.text(`Format No: ${reportSettings.report_format_no_product_master}`, pageWidth / 2, pageHeight - 19, { align: 'center' });
        }
        doc.setFontSize(8);
        doc.text(`Page ${pageNo} of ${totalPages}`, 24, pageHeight - 12);
        doc.text(`Print: ${metadata.exportedAtDisplay}`, pageWidth / 2, pageHeight - 12, { align: 'center' });
        doc.text(reportSettings.report_footer_tagline || 'Data is generated and handled by PrimeClean.', pageWidth - 24, pageHeight - 12, { align: 'right' });
      };

      const apiWiseRows = [];
      sourceProducts.forEach((product) => {
        const productApis = Array.isArray(product.apis) ? product.apis : [];
        const baseProductValues = {
          productCode: formatCellValue(product.product_code),
          productName: formatCellValue(product.product_name),
          dosageForm: formatCellValue(product.dosage_form),
          equipmentTrain: formatCellValue(getTrainNameForProduct(product)),
          allLinkedTrains: formatCellValue(getFormattedLinkedTrainsForExport(product)),
          batch: formatCellValue(`${product.batch_size ?? '-'} ${product.batch_size_unit || ''}`),
          fill: formatCellValue(`${product.fill_weight ?? '-'} ${product.fill_unit || ''}`),
          status: formatCellValue(normalizeDisplayStatus(product.status)),
          productNotes: formatCellValue(product.product_notes)
        };

        if (productApis.length === 0) {
          apiWiseRows.push([
            baseProductValues.productCode,
            baseProductValues.productName,
            baseProductValues.dosageForm,
            baseProductValues.equipmentTrain,
            baseProductValues.batch,
            baseProductValues.fill,
            baseProductValues.status,
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            baseProductValues.productNotes,
            '-'
          ]);
          return;
        }

        productApis.forEach((api) => {
          const linkedApi = allApis.find((item) => String(item.api_id) === String(api.api_id));
          apiWiseRows.push([
            baseProductValues.productCode,
            baseProductValues.productName,
            baseProductValues.dosageForm,
            baseProductValues.equipmentTrain,
            baseProductValues.allLinkedTrains,
            baseProductValues.batch,
            baseProductValues.fill,
            baseProductValues.status,
            formatCellValue(api.api_code || linkedApi?.api_code),
            formatCellValue(api.api_name || linkedApi?.api_name),
            combineValueUnit(api.label_claim, api.label_claim_unit),
            combineValueUnit(api.therapeutic_dose || linkedApi?.therapeutic_dose, api.therapeutic_dose_unit || linkedApi?.therapeutic_dose_unit),
            combineValueUnit(api.max_daily_dose || linkedApi?.max_daily_dose, api.max_daily_dose_unit || linkedApi?.max_daily_dose_unit),
            combineValueUnit(api.pde_value || linkedApi?.pde_value, api.pde_unit || api.pde_unit || linkedApi?.pde_unit || linkedApi?.pde_unit),
            combineValueUnit(api.ld50_value || linkedApi?.ld50_value, api.ld50_value_unit || linkedApi?.ld50_value_unit),
            formatCellValue(api.solubility_classification || linkedApi?.solubility_classification),
            formatCellValue(api.cleanability_rating || linkedApi?.cleanability_rating),
            baseProductValues.productNotes,
            formatCellValue(api.notes || linkedApi?.notes)
          ]);
        });
      });

      drawHeaderFooter();

      drawPdfGridTable(doc, {
        headers: [
          'Product Code',
          'Product Name',
          'Dosage Form',
          'Equipment Train',
          'All Linked Trains',
          'Batch Size',
          'Fill Value',
          'Status',
          'API Code',
          'API Name',
          'Label Claim',
          'Therapeutic Dose',
          'Max Daily Dose',
          'PDE',
          'LD50',
          'Solubility',
          'Cleanability',
          'Product Notes',
          'API Notes'
        ],
        rows: apiWiseRows,
        startY: bodyTop + 4,
        topMargin: bodyTop,
        bottomMargin: 40,
        leftMargin: 20,
        rightMargin: 20,
        headerFillColor: [255, 255, 255],
        headerTextColor: [0, 0, 0],
        bodyTextColor: [15, 23, 42],
        alternateRowColor: [248, 250, 252],
        fontSize: 6.6,
        colWidthWeights: [
          1.1, 1.9, 1.1, 1.5, 1.0, 1.0, 0.95, 1.0, 1.8, 1.25, 1.3, 1.3, 1.15, 1.1, 1.0, 1.0, 1.9, 1.75
        ],
        drawHeaderFooter
      });

      const fileStamp = metadata.exportedAt.toISOString().replace(/[:.]/g, '-');
      doc.save(`products_master_${fileStamp}.pdf`);
      toast.success('PDF exported successfully');
    } catch (error) {
      console.error('PDF export failed:', error);
      toast.error('Failed to export PDF');
    }
  };

  const printProductDetails = () => {
    const content = document.getElementById('product-details-print');
    if (!content) {
      toast.error('Unable to find content to print');
      return;
    }
    
    openPrintPreview({
      reportKey: 'product_master',
      contentHTML: content.innerHTML,
      title: `${viewingProduct?.product_name || 'Product'} Details`,
      documentName: 'Product Master Details',
      clientName: reportSettings?.report_client_name || 'Client Name',
      clientAddress: unitReportAddress(reportSettings) || 'Client Address',
      clientLogoUrl: reportSettings?.report_client_logo || '',
      primeCleanLogoUrl: reportSettings?.report_primeclean_logo || '',
      revisionNo: reportSettings?.report_document_rev_no || '01',
      formatNo: reportSettings?.report_format_no_product_master || '',
      unitName: getActivePrintUnitName(),
      timestamp: new Date().toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    });
  };


  // Product Export to Excel
  const exportProductToExcel = () => {
    const sourceProducts = getExportProducts();
    if (sourceProducts.length === 0) {
      toast.info('No products available to export');
      return;
    }

    const metadata = getExportMetadata(sourceProducts.length);
    const apiRows = [];
    sourceProducts.forEach((product) => {
      const productApis = Array.isArray(product.apis) ? product.apis : [];
      const baseProductColumns = {
        'Product ID': product.product_id || '',
        'Product Code': product.product_code || '',
        'Product Name': product.product_name || '',
        'Dosage Form': product.dosage_form || '',
        'Equipment Train': getTrainNameForProduct(product),
        'Equipment Train Code': getPrimaryTrainLink(product)?.train_code || '',
        'All Linked Trains': getFormattedLinkedTrainsForExport(product),
        'Batch Size': product.batch_size || '',
        'Batch Size Unit': product.batch_size_unit || '',
        'Fill Weight/Vol/Avg Wt': product.fill_weight || '',
        'Fill Unit': product.fill_unit || '',
        'Reference Document': product.reference_document || '',
        'Product Notes': product.product_notes || '',
        'Product Status': normalizeDisplayStatus(product.status),
        'Attached Files': getFileNamesForExport(product.attached_files || product.attachedFiles),
        'Product Created At': product.created_at ? new Date(product.created_at).toLocaleString() : '',
        'Product Updated At': product.updated_at ? new Date(product.updated_at).toLocaleString() : '',
        'Product Updated By': product.updated_by_user || product.created_by_user || ''
      };

      if (productApis.length === 0) {
        apiRows.push({
          ...baseProductColumns,
          'API Sequence': '',
          'API ID': '',
          'API Code': '',
          'API Name': '',
          'Label Claim': '',
          'Label Claim Unit': '',
          'IU to mg Conversion': '',
          'Therapeutic Dose': '',
          'Therapeutic Dose Unit': '',
          'Max Daily Dose': '',
          'Max Daily Dose Unit': '',
          'PDE Value': '',
          'PDE Unit': '',
          'LD50 Value': '',
          'LD50 Unit': '',
          'Solubility Classification': '',
          'Cleanability Rating': '',
          'API Notes': '',
          'Dosage Document': '',
          'Therapeutic Dose Document': '',
          'Max Daily Dose Document': '',
          'PDE Document': '',
          'LD50 Document': '',
          'Solubility Document': '',
          'Cleanability Document': ''
        });
        return;
      }

      productApis.forEach((api, index) => {
        const apiRef = resolveApiReference(api);
        apiRows.push({
          ...baseProductColumns,
          'API Sequence': index + 1,
          'API ID': api.api_id || '',
          'API Code': apiRef.apiCode || '',
          'API Name': apiRef.apiName || '',
          'Label Claim': api.label_claim || '',
          'Label Claim Unit': api.label_claim_unit || '',
          'IU to mg Conversion': api.iu_to_mg_conversion || '',
          'Therapeutic Dose': api.therapeutic_dose || '',
          'Therapeutic Dose Unit': api.therapeutic_dose_unit || '',
          'Max Daily Dose': api.max_daily_dose || '',
          'Max Daily Dose Unit': api.max_daily_dose_unit || '',
          'PDE Value': api.pde_value || '',
          'PDE Unit': api.pde_unit || '',
          'LD50 Value': api.ld50_value || '',
          'LD50 Unit': api.ld50_value_unit || '',
          'Solubility Classification': api.solubility_classification || '',
          'Cleanability Rating': api.cleanability_rating || '',
          'API Notes': api.notes || '',
          'Dosage Document': getFileNamesForExport(api.dosage_document),
          'Therapeutic Dose Document': getFileNamesForExport(api.therapeutic_dose_document),
          'Max Daily Dose Document': getFileNamesForExport(api.max_daily_dose_document),
          'PDE Document': getFileNamesForExport(api.pde_document),
          'LD50 Document': getFileNamesForExport(api.ld50_document),
          'Solubility Document': getFileNamesForExport(api.solubility_document),
          'Cleanability Document': getFileNamesForExport(api.cleanability_document)
        });
      });
    });

    const metadataRows = [
      ['PrimeClean Product API Details Export'],
      ['21 CFR Part 11 Compliance Context'],
      [],
      ['Report ID', metadata.reportId],
      ['Report Name', 'Product Details'],
      ['Unit Name', metadata.unitName],
      ['Section', metadata.productionLine],
      ['Equipment Train', metadata.equipmentTrain],
      ['Exported At', metadata.exportedAtDisplay],
      ['Exported At (ISO)', metadata.exportedAtIso],
      ['Exported By', metadata.exportedBy],
      ['Exported By Role', metadata.exportedByRole],
      ['System Name', metadata.systemName],
      ['System Node', metadata.systemNode],
      ['System Origin', metadata.systemHost],
      ['Product Count', metadata.recordCount],
      ['Product/API Rows', apiRows.length],
      ['Note', 'Electronic record export with trace metadata for audit review.']
    ];

    const buildColumnWidths = (rows, minWidth = 12, maxWidth = 55) => {
      if (!rows.length) return [];
      const keys = Object.keys(rows[0]);
      return keys.map((key) => {
        let widest = key.length;
        rows.forEach((row) => {
          const valueLength = String(row[key] ?? '').length;
          if (valueLength > widest) widest = valueLength;
        });
        return { wch: Math.min(Math.max(widest + 2, minWidth), maxWidth) };
      });
    };

    const tableStartRow = metadataRows.length + 2; // 1-based row index where table header will begin
    const wsApi = XLSX.utils.aoa_to_sheet(metadataRows);
    XLSX.utils.sheet_add_json(wsApi, apiRows, { origin: `A${tableStartRow}`, skipHeader: false });

    if (apiRows.length > 0) {
      const columnCount = Object.keys(apiRows[0]).length;
      wsApi['!autofilter'] = {
        ref: XLSX.utils.encode_range({
          s: { r: tableStartRow - 1, c: 0 },
          e: { r: tableStartRow + apiRows.length - 1, c: columnCount - 1 }
        })
      };

      const tableColWidths = buildColumnWidths(apiRows);
      if (tableColWidths.length > 0) {
        tableColWidths[0] = { wch: Math.max(tableColWidths[0].wch, 14) };
        tableColWidths[1] = { wch: Math.max(tableColWidths[1].wch, 18) };
      }
      wsApi['!cols'] = tableColWidths;
    } else {
      wsApi['!cols'] = [{ wch: 34 }, { wch: 110 }];
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsApi, 'Product Details');

    const fileStamp = metadata.exportedAt.toISOString().replace(/[:.]/g, '-');
    XLSX.writeFile(wb, `products_master_${fileStamp}.xlsx`);
    toast.success('Excel exported in Product Details sheet');
  };

  const downloadProductImportTemplate = () => {
    const activeTrainOption = productTrainFilter !== 'All'
      ? trainFilterOptions.find((t) => t.train_id === productTrainFilter)
      : null;
    const activeTrainCode = activeTrainOption
      ? (trainOptions.find((t) => String(t.train_id) === productTrainFilter)?.train_code || activeTrainOption.label)
      : (getImportableTrains()[0]?.train_code || 'TRN-TAB-01');
    const activeUnitLabel = getExportContextDisplay(localStorage.getItem('activePlant'), 'All Units');
    const validTrainRows = getImportableTrains()
      .map((t) => ({ train_code: t.train_code || '', train_name: t.train_name || '', unit: t.unit_plant || '', production_line: t.production_line || '' }))
      .sort((a, b) => String(a.train_code).localeCompare(String(b.train_code)));

    const templateRows = [
      {
        product_code: 'PRD-TAB-001',
        product_name: 'Amoxicillin and Clavulanate potassium Tablets 500 mg + 125 mg',
        dosage_form: 'Tablet',
        equipment_train_code: activeTrainCode,
        production_line: 'Tablet Line 1',
        batch_size: 125,
        batch_size_unit: 'kg',
        fill_weight: 500,
        fill_unit: 'mg',
        reference_document: 'DOC-PROD-001',
        product_notes: 'Sample import row for first API',
        api_code: 'API-001',
        api_name: 'Amoxicillin Trihydrate',
        label_claim: 500,
        label_claim_unit: 'mg/Unit',
        iu_to_mg_conversion: '',
        therapeutic_dose: 125,
        therapeutic_dose_unit: 'mg',
        max_daily_dose: 6000,
        max_daily_dose_unit: 'mg',
        pde_value: 3.75,
        pde_unit: 'mg',
        ld50_value: 5000,
        ld50_value_unit: 'mg/kg',
        solubility_classification: 'Slightly Soluble',
        cleanability_rating: 'Difficult',
        api_notes: '',
        dosage_document: '',
        pde_document: '',
        ld50_document: '',
        solubility_document: '',
        cleanability_document: ''
      },
      {
        product_code: 'PRD-TAB-001',
        product_name: 'Amoxicillin and Clavulanate potassium Tablets 500 mg + 125 mg',
        dosage_form: 'Tablet',
        equipment_train_code: activeTrainCode,
        production_line: 'Tablet Line 1',
        batch_size: 125,
        batch_size_unit: 'kg',
        fill_weight: 500,
        fill_unit: 'mg',
        reference_document: 'DOC-PROD-001',
        product_notes: 'Sample import row for second API',
        api_code: 'API-002',
        api_name: 'Clavulanate potassium',
        label_claim: 125,
        label_claim_unit: 'mg/Unit',
        iu_to_mg_conversion: '',
        therapeutic_dose: 28.5,
        therapeutic_dose_unit: 'mg',
        max_daily_dose: 250,
        max_daily_dose_unit: 'mg',
        pde_value: 3.75,
        pde_unit: 'mg',
        ld50_value: 4526,
        ld50_value_unit: 'mg/kg',
        solubility_classification: 'Freely Soluble',
        cleanability_rating: 'Easy',
        api_notes: '',
        dosage_document: '',
        pde_document: '',
        ld50_document: '',
        solubility_document: '',
        cleanability_document: ''
      }
    ];

    const instructions = [
      ['Product Import Template (Inlined Format)'],
      [],
      ['Active Context at Download:'],
      ['  Unit / Plant', activeUnitLabel],
      ['  Equipment Train', activeTrainOption ? activeTrainOption.label : 'All Trains (no filter active)'],
      [],
      ['equipment_train_code must exactly match a train code or name on the "Valid Trains" sheet (trains of the active unit only).'],
      ['A product\'s unit comes from its equipment train, so a valid train is REQUIRED when a specific unit is selected.'],
      ['To link a product to more than one train, repeat its rows with a different equipment_train_code (first one becomes primary).'],
      ['production_line is informational; the line is derived from the train equipment.'],
      ['Product Codes that already exist are rejected. API codes/names not found in API Master are created from the row data.'],
      ['Fill one API per row. Products with multiple APIs should have multiple rows with the SAME Product Code.'],
      ['The importer will automatically group all rows sharing the same Product Code into a single Product.'],
      ['Imported products are submitted in Pending Product Approval status.'],
      ['Bulk import requires an electronic signature.'],
      ['For IU/MIU label-claim units, provide iu_to_mg_conversion.'],
      ['For document columns, list existing file names separated by | (uploads are not embedded in Excel).'],
      ['Equipment Train can be a train code or train name. Sample rows are pre-filled with the active train filter.'],
      ['Do not change header names in the Product Details sheet.']
    ];

    const wsTemplate = XLSX.utils.json_to_sheet(templateRows);
    const wsInstructions = XLSX.utils.aoa_to_sheet(instructions);

    wsTemplate['!autofilter'] = {
      ref: XLSX.utils.encode_range({
        s: { r: 0, c: 0 },
        e: { r: templateRows.length, c: Object.keys(templateRows[0]).length - 1 }
      })
    };
    wsTemplate['!cols'] = Object.keys(templateRows[0]).map((key) => ({ wch: Math.min(Math.max(key.length + 4, 16), 32) }));
    wsInstructions['!cols'] = [{ wch: 90 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsTemplate, 'Product Details');
    XLSX.utils.book_append_sheet(wb, wsInstructions, 'Read Me');
    const wsTrains = XLSX.utils.json_to_sheet(validTrainRows.length ? validTrainRows : [{ train_code: '', train_name: '', unit: '', production_line: '' }]);
    wsTrains['!cols'] = [{ wch: 18 }, { wch: 36 }, { wch: 18 }, { wch: 24 }];
    XLSX.utils.book_append_sheet(wb, wsTrains, 'Valid Trains');
    XLSX.writeFile(wb, 'product_import_template.xlsx');
    toast.success('Product import template downloaded');
  };

  // External masters carry code/name/batch data but not the API composition, train or
  // toxicology, so fetched products are handed back as a pre-filled import workbook to
  // complete; importing it then runs the normal e-signed import and approval workflow.
  const PRODUCT_IMPORT_COLUMNS = ['product_code', 'product_name', 'dosage_form', 'equipment_train_code', 'production_line', 'batch_size', 'batch_size_unit',
    'fill_weight', 'fill_unit', 'reference_document', 'product_notes', 'api_code', 'api_name', 'label_claim', 'label_claim_unit', 'iu_to_mg_conversion',
    'therapeutic_dose', 'therapeutic_dose_unit', 'max_daily_dose', 'max_daily_dose_unit', 'pde_value', 'pde_unit', 'ld50_value', 'ld50_value_unit',
    'solubility_classification', 'cleanability_rating', 'api_notes', 'dosage_document', 'pde_document', 'ld50_document', 'solubility_document', 'cleanability_document'];

  const fetchProductsFrom = async (source) => {
    setFetchingProductSource(source.key);
    try {
      const res = await integrationsAPI.pull(source.key, 'products');
      const data = res?.data?.data || {};
      const fresh = (data.rows || []).filter((r) => !r._exists);
      if (!fresh.length) { toast.info(`No new products in ${source.label} (${data.summary?.existing || 0} already exist)`); return; }
      const sheetRows = fresh.map((row) => PRODUCT_IMPORT_COLUMNS.reduce((acc, col) => { acc[col] = row[col] ?? ''; return acc; }, {}));
      const ws = XLSX.utils.json_to_sheet(sheetRows, { header: PRODUCT_IMPORT_COLUMNS });
      ws['!cols'] = PRODUCT_IMPORT_COLUMNS.map((key) => ({ wch: Math.min(Math.max(key.length + 4, 16), 32) }));
      const validTrainRows = getImportableTrains()
        .map((t) => ({ train_code: t.train_code || '', train_name: t.train_name || '', unit: t.unit_plant || '', production_line: t.production_line || '' }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Product Details');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
        [`Products fetched from ${source.label} on ${new Date().toLocaleString()}`],
        ['Complete equipment_train_code and at least one API row (api_code, label_claim, …) per product, then use Import.'],
        ['Products with several APIs: repeat the row with the same product_code, one API per row.'],
        ['Imported products are submitted in Pending Product Approval status and need an electronic signature.']
      ]), 'Read Me');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(validTrainRows.length ? validTrainRows : [{ train_code: '', train_name: '', unit: '', production_line: '' }]), 'Valid Trains');
      XLSX.writeFile(wb, `products_from_${source.key}.xlsx`);
      toast.success(`${fresh.length} new product(s) from ${source.label} downloaded — complete the train/API columns, then Import.${data.summary?.existing ? ` ${data.summary.existing} existing code(s) left out.` : ''}`, { autoClose: 9000 });
    } catch (error) {
      toast.error(error?.response?.data?.message || `Could not fetch products from ${source.label}`);
    } finally {
      setFetchingProductSource('');
    }
  };

  // Product Import from File
  const normalizeImportRow = (row = {}) => {
    return Object.entries(row).reduce((acc, [key, value]) => {
      const rawKey = String(key || '').trim().toLowerCase();
      if (!rawKey) return acc;
      acc[rawKey] = value;
      const sanitizedKey = rawKey.replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
      if (sanitizedKey && acc[sanitizedKey] === undefined) {
        acc[sanitizedKey] = value;
      }
      return acc;
    }, {});
  };

  const normalizeImportRows = (rows = []) => rows.map((row) => normalizeImportRow(row));

  const parseImportFiles = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return [];
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return parsed;
      } catch (error) {
        // fall through to delimiter parsing
      }
      return trimmed.split(/[|;]/).map((item) => item.trim()).filter(Boolean);
    }
    return [];
  };

  const getRowValue = (row, keys = []) => {
    for (const key of keys) {
      if (!key) continue;
      const value = row[key];
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        return value;
      }
    }
    return '';
  };

  const parseImportFileRows = async (file) => {
    const lowerName = file.name.toLowerCase();
    if (lowerName.endsWith('.csv')) {
      return new Promise((resolve, reject) => {
        Papa.parse(file, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => resolve(results.data || []),
          error: (error) => reject(error)
        });
      });
    }

    if (lowerName.endsWith('.xlsx') || lowerName.endsWith('.xls')) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (evt) => {
          try {
            const wb = XLSX.read(evt.target.result, { type: 'binary' });
            const preferredSheet =
              wb.SheetNames.find((name) => String(name).trim().toLowerCase() === 'product details') ||
              wb.SheetNames[0];
            // Exports put report metadata rows above the table, so locate the real header row.
            const grid = XLSX.utils.sheet_to_json(wb.Sheets[preferredSheet], { header: 1, defval: '', blankrows: false });
            const isHeaderCell = (cell) => String(cell || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_') === 'product_code';
            const headerIdx = grid.findIndex((cells) => cells.some(isHeaderCell));
            if (headerIdx < 0) {
              resolve(XLSX.utils.sheet_to_json(wb.Sheets[preferredSheet], { defval: '' }));
              return;
            }
            const headers = grid[headerIdx].map((cell) => String(cell || '').trim());
            const data = grid.slice(headerIdx + 1).map((cells) => headers.reduce((row, header, i) => {
              if (header) row[header] = cells[i] ?? '';
              return row;
            }, {}));
            resolve(data);
          } catch (error) {
            reject(error);
          }
        };
        reader.onerror = () => reject(new Error('Error reading Excel file'));
        reader.onabort = () => reject(new Error('Excel file read was aborted'));
        reader.readAsBinaryString(file);
      });
    }

    throw new Error('Please upload a CSV or Excel file');
  };

  // Trains an import may use: active, and inside the currently selected unit/plant.
  const getImportableTrains = () => {
    const activePlant = localStorage.getItem('activePlant');
    const anyPlant = !activePlant || activePlant === 'All' || activePlant === 'All Units';
    return (trainsRef.current.length ? trainsRef.current : trains).filter((train) => {
      const inactive = train?.is_active === false || Number(train?.is_active) === 0 || String(train?.is_active || '').trim().toLowerCase() === 'false';
      if (inactive) return false;
      if (anyPlant) return true;
      return matchesPlantScope(train.unit_plant || train.unit_id, activePlant);
    });
  };

  // Exact code/name match only, within the active unit. Returns null when not found.
  const resolveTrainIdFromLabel = (labelValue) => {
    const normalized = String(labelValue || '').replace(/\s*\(\+\d+ more\)\s*$/i, '').trim().toLowerCase();
    if (!normalized) return null;
    const match = getImportableTrains().find((train) => {
      const code = String(train.train_code || '').trim().toLowerCase();
      const name = String(train.train_name || '').trim().toLowerCase();
      return (code && code === normalized) || (name && name === normalized);
    });
    return match ? match.train_id : null;
  };

  const requireImportTrain = (labels, productCode) => {
    const activePlant = localStorage.getItem('activePlant');
    const anyPlant = !activePlant || activePlant === 'All' || activePlant === 'All Units';
    if (!labels.length) {
      if (anyPlant) return [];
      throw new Error(`Equipment Train is required for ${productCode} (needed to place the product in ${activePlant})`);
    }
    return labels.map((label) => {
      const id = resolveTrainIdFromLabel(label);
      if (!id) {
        throw new Error(`Equipment Train "${label}" for ${productCode} was not found${anyPlant ? '' : ` in ${activePlant}`}. See the "Valid Trains" sheet in the template.`);
      }
      return id;
    });
  };

  const buildImportPayload = (rawRow) => {
    const row = normalizeImportRow(rawRow);
    const productCode = String(row.product_code || '').trim();
    const productName = String(row.product_name || '').trim();
    const fillUnit = String(row.fill_unit || '').trim() || (getFillUnitValueList(unitConfig)[0] || 'mL');
    const trainIdRaw = row.equipment_train_id;
    const trainLabel = String(row.equipment_train_code || row.equipment_train || row.equipment_train_name || '').trim();
    const trainList = trainsRef.current.length ? trainsRef.current : trains;
    let trainId = null;

    if (trainIdRaw) {
      const t = trainList.find((t) => String(t.train_id) === String(trainIdRaw));
      if (t) trainId = t.train_id;
    }

    if (!productCode || !productName) {
      throw new Error('Product Code and Product Name are required');
    }

    if (!trainId) {
      trainId = requireImportTrain(trainLabel ? [trainLabel] : [], productCode)[0] || null;
    }

    const apis = [];
    for (let i = 1; i <= 3; i += 1) {
      const apiCode = String(row[`api_${i}_code`] || '').trim();
      const apiNameFromRow = String(row[`api_${i}_name`] || '').trim();
      const labelClaim = row[`api_${i}_label_claim`];
      const labelClaimUnit = String(row[`api_${i}_label_claim_unit`] || '').trim();
      const conversion = row[`api_${i}_iu_to_mg_conversion`];
      const therapeuticDose = row[`api_${i}_therapeutic_dose`];
      const therapeuticDoseUnit = String(row[`api_${i}_therapeutic_dose_unit`] || '').trim();
      const maxDailyDose = row[`api_${i}_max_daily_dose`];
      const maxDailyDoseUnit = String(row[`api_${i}_max_daily_dose_unit`] || '').trim();
      const pdeValue = row[`api_${i}_pde_value`];
      const pdeUnit = String(row[`api_${i}_pde_unit`] || '').trim();
      const ld50Value = row[`api_${i}_ld50_value`];
      const ld50Unit = String(row[`api_${i}_ld50_value_unit`] || row[`api_${i}_ld50_unit`] || '').trim();
      const solubility = String(row[`api_${i}_solubility_classification`] || '').trim();
      const cleanability = String(row[`api_${i}_cleanability_rating`] || '').trim();
      const apiNotes = String(row[`api_${i}_notes`] || '').trim();
      const dosageDocs = parseImportFiles(row[`api_${i}_dosage_document`] || row[`api_${i}_dose_document`]);
      const pdeDocs = parseImportFiles(row[`api_${i}_pde_document`]);
      const ld50Docs = parseImportFiles(row[`api_${i}_ld50_document`]);
      const solubilityDocs = parseImportFiles(row[`api_${i}_solubility_document`]);
      const cleanabilityDocs = parseImportFiles(row[`api_${i}_cleanability_document`]);

      if (!apiCode && !apiNameFromRow && !String(labelClaim || '').trim()) {
        continue;
      }

      const linkedApi =
        allApis.find((item) => String(item.api_code || '').trim().toLowerCase() === apiCode.toLowerCase()) ||
        allApis.find((item) => String(item.api_name || '').trim().toLowerCase() === apiNameFromRow.toLowerCase());

      apis.push({
        api_id: linkedApi?.api_id || '',
        api_code: apiCode || linkedApi?.api_code || '',
        api_name: apiNameFromRow || linkedApi?.api_name || '',
        label_claim: labelClaim === '' ? null : labelClaim,
        label_claim_unit: labelClaimUnit || getDefaultLabelClaimUnit(fillUnit, unitConfig),
        iu_to_mg_conversion: conversion === '' ? null : conversion,
        therapeutic_dose: therapeuticDose === '' ? (linkedApi?.therapeutic_dose ?? null) : therapeuticDose,
        therapeutic_dose_unit: therapeuticDoseUnit || linkedApi?.therapeutic_dose_unit || '',
        max_daily_dose: maxDailyDose === '' ? (linkedApi?.max_daily_dose ?? null) : maxDailyDose,
        max_daily_dose_unit: maxDailyDoseUnit || linkedApi?.max_daily_dose_unit || '',
        pde_value: pdeValue === '' ? (linkedApi?.pde_value ?? null) : pdeValue,
        pde_unit: pdeUnit || linkedApi?.pde_unit || '',
        ld50_value: ld50Value === '' ? (linkedApi?.ld50_value ?? null) : ld50Value,
        ld50_value_unit: ld50Unit || linkedApi?.ld50_value_unit || linkedApi?.ld50_unit || '',
        solubility_classification: solubility || linkedApi?.solubility_classification || '',
        cleanability_rating: cleanability || linkedApi?.cleanability_rating || '',
        notes: apiNotes || linkedApi?.notes || '',
        dosage_document: dosageDocs,
        pde_document: pdeDocs,
        ld50_document: ld50Docs,
        solubility_document: solubilityDocs,
        cleanability_document: cleanabilityDocs
      });
    }

    if (apis.length === 0) {
      throw new Error('At least one API row is required');
    }

    return {
      product_code: productCode,
      product_name: productName,
      dosage_form: String(row.dosage_form || '').trim() || 'Dry Powder Injection',
      equipment_train_id: trainId,
      batch_size: row.batch_size === '' ? null : row.batch_size,
      batch_size_unit: String(row.batch_size_unit || '').trim() || 'Unit',
      fill_weight: row.fill_weight === '' ? null : row.fill_weight,
      fill_unit: fillUnit,
      reference_document: String(row.reference_document || '').trim(),
      product_notes: String(row.product_notes || '').trim(),
      apis,
      status: PRODUCT_STATUS.PENDING_PRODUCT_APPROVAL
    };
  };

  const buildApiFromExportRow = (row, fillUnit) => {
    const apiIdRaw = getRowValue(row, ['api_id']);
    const apiCode = String(getRowValue(row, ['api_code'])).trim();
    const apiName = String(getRowValue(row, ['api_name'])).trim();
    const labelClaim = getRowValue(row, ['label_claim']);
    const labelClaimUnit = String(getRowValue(row, ['label_claim_unit'])).trim();
    const iuConversion = getRowValue(row, ['iu_to_mg_conversion']);
    const therapeuticDose = getRowValue(row, ['therapeutic_dose']);
    const therapeuticDoseUnit = String(getRowValue(row, ['therapeutic_dose_unit'])).trim();
    const maxDailyDose = getRowValue(row, ['max_daily_dose']);
    const maxDailyDoseUnit = String(getRowValue(row, ['max_daily_dose_unit'])).trim();
    const pdeValue = getRowValue(row, ['pde_value']);
    const pdeUnit = String(getRowValue(row, ['pde_unit'])).trim();
    const ld50Value = getRowValue(row, ['ld50_value']);
    const ld50Unit = String(getRowValue(row, ['ld50_unit', 'ld50_value_unit'])).trim();
    const solubility = String(getRowValue(row, ['solubility_classification'])).trim();
    const cleanability = String(getRowValue(row, ['cleanability_rating'])).trim();
    const apiNotes = String(getRowValue(row, ['api_notes', 'notes'])).trim();
    const dosageDocs = parseImportFiles(getRowValue(row, ['dosage_document', 'dose_document']));
    const pdeDocs = parseImportFiles(getRowValue(row, ['pde_document']));
    const ld50Docs = parseImportFiles(getRowValue(row, ['ld50_document']));
    const solubilityDocs = parseImportFiles(getRowValue(row, ['solubility_document']));
    const cleanabilityDocs = parseImportFiles(getRowValue(row, ['cleanability_document']));

    if (!apiIdRaw && !apiCode && !apiName && String(labelClaim || '').trim() === '') {
      return null;
    }

    const linkedApi =
      allApis.find((item) => String(item.api_id || '').trim() === String(apiIdRaw || '').trim()) ||
      allApis.find((item) => String(item.api_code || '').trim().toLowerCase() === apiCode.toLowerCase()) ||
      allApis.find((item) => String(item.api_name || '').trim().toLowerCase() === apiName.toLowerCase());

    return {
      // Ignore stale api_id values from imported files; trust live API lookup only.
      api_id: linkedApi?.api_id || '',
      api_code: apiCode || linkedApi?.api_code || '',
      api_name: apiName || linkedApi?.api_name || '',
      label_claim: labelClaim === '' ? null : labelClaim,
      label_claim_unit: labelClaimUnit || getDefaultLabelClaimUnit(fillUnit, unitConfig),
      iu_to_mg_conversion: iuConversion === '' ? null : iuConversion,
      therapeutic_dose: therapeuticDose === '' ? (linkedApi?.therapeutic_dose ?? null) : therapeuticDose,
      therapeutic_dose_unit: therapeuticDoseUnit || linkedApi?.therapeutic_dose_unit || '',
      max_daily_dose: maxDailyDose === '' ? (linkedApi?.max_daily_dose ?? null) : maxDailyDose,
      max_daily_dose_unit: maxDailyDoseUnit || linkedApi?.max_daily_dose_unit || '',
      pde_value: pdeValue === '' ? (linkedApi?.pde_value ?? null) : pdeValue,
      pde_unit: pdeUnit || linkedApi?.pde_unit || '',
      ld50_value: ld50Value === '' ? (linkedApi?.ld50_value ?? null) : ld50Value,
      ld50_value_unit: ld50Unit || linkedApi?.ld50_value_unit || linkedApi?.ld50_unit || '',
      solubility_classification: solubility || linkedApi?.solubility_classification || '',
      cleanability_rating: cleanability || linkedApi?.cleanability_rating || '',
      notes: apiNotes || linkedApi?.notes || '',
      dosage_document: dosageDocs,
      pde_document: pdeDocs,
      ld50_document: ld50Docs,
      solubility_document: solubilityDocs,
      cleanability_document: cleanabilityDocs
    };
  };

  const buildPayloadsFromExportRows = (rows) => {
    const groups = new Map();

    rows.forEach((row, index) => {
      const productCode = String(getRowValue(row, ['product_code'])).trim();
      const productName = String(getRowValue(row, ['product_name'])).trim();
      if (!productCode && !productName) return;
      const key = productCode || productName;
      if (!groups.has(key)) {
        groups.set(key, { rows: [], rowIndex: index });
      }
      groups.get(key).rows.push(row);
    });

    return Array.from(groups.entries()).map(([key, group]) => {
      try {
      const base = group.rows[0] || {};
      const productCode = String(getRowValue(base, ['product_code'])).trim();
      const productName = String(getRowValue(base, ['product_name'])).trim();
      if (!productCode || !productName) {
        throw new Error(`Product Code and Name are required for ${key}`);
      }

      const fillUnit = String(getRowValue(base, ['fill_unit'])).trim() || (getFillUnitValueList(unitConfig)[0] || 'mL');
      
      const allTrainLabels = group.rows.flatMap((row) => {
        const primary = String(getRowValue(row, ['equipment_train_code', 'equipment_train', 'equipment_train_name'])).trim();
        const linked = String(getRowValue(row, ['all_linked_trains'])).trim();
        const linkedLabels = linked && linked !== '-' ? linked.split(';').map((label) => label.replace(/\s*\[[^\]]+\]\s*$/, '').trim()) : [];
        return [primary, ...linkedLabels].filter(Boolean);
      });
      const uniqueTrainIds = [...new Set(requireImportTrain([...new Set(allTrainLabels)], productCode))];
      
      const primaryTrainId = uniqueTrainIds[0] || '';
      const linkedTrains = uniqueTrainIds.map((tid, idx) => ({
        train_id: tid,
        is_primary: idx === 0,
        qualification_remark: '',
        same_size_equipment: false,
        same_cleaning_method: false,
        cv_batch_remark: ''
      }));

      const rawApis = group.rows.map((row) => buildApiFromExportRow(row, fillUnit)).filter(Boolean);
      const apisMap = new Map();
      rawApis.forEach(api => {
        const apiKey = api.api_id || api.api_code || api.api_name;
        if (apiKey && !apisMap.has(apiKey)) {
          apisMap.set(apiKey, api);
        }
      });
      const apis = Array.from(apisMap.values());

      if (!apis.length) {
        throw new Error(`At least one API row is required for ${productCode}`);
      }

      return {
        payload: {
          product_code: productCode,
          product_name: productName,
          dosage_form: String(getRowValue(base, ['dosage_form'])).trim() || 'Dry Powder Injection',
          production_line: String(getRowValue(base, ['production_line']) || localStorage.getItem('activeLine') || '').trim(),
          equipment_train_id: primaryTrainId,
          linked_trains: linkedTrains,
          batch_size: getRowValue(base, ['batch_size']) === '' ? null : getRowValue(base, ['batch_size']),
          batch_size_unit: String(getRowValue(base, ['batch_size_unit'])).trim() || 'Unit',
          fill_weight: getRowValue(base, ['fill_weight', 'fill_weight_vol_avg_wt']) === '' ? null : getRowValue(base, ['fill_weight', 'fill_weight_vol_avg_wt']),
          fill_unit: fillUnit,
          reference_document: String(getRowValue(base, ['reference_document'])).trim(),
          product_notes: String(getRowValue(base, ['product_notes'])).trim(),
          apis,
          status: PRODUCT_STATUS.PENDING_PRODUCT_APPROVAL
        },
        label: `Product ${productCode}`
      };
      } catch (error) {
        return { payload: null, label: `Product ${key}`, error: error.message };
      }
    });
  };

  const buildImportPayloads = (rows) => {
    const hasIndexedApi = rows.some((row) => Object.keys(row).some((key) => /^api_[1-3]_/.test(key)));
    if (hasIndexedApi) {
      return rows.map((row, index) => {
        try {
          return { payload: buildImportPayload(row), label: `Row ${index + 2}` };
        } catch (error) {
          return { payload: null, label: `Row ${index + 2}`, error: error.message };
        }
      });
    }

    const hasExportApi = rows.some((row) => row.api_code || row.api_name || row.api_id || row.api_sequence);
    if (hasExportApi) {
      return buildPayloadsFromExportRows(rows);
    }

    throw new Error('Unrecognized product import format. Use the provided template or the Product Details export.');
  };

  const handleProductFileImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const message = 'Bulk import will submit products for Product/QA approval workflow. Continue?';
    setConfirmationMessage(message);
    setPendingAction({ actionType: 'import', data: { file, target: e.target } });
    setESignMode('submit');
    setESignData({ password: '', comment: '', decision: 'approve' });
    setShowESignModal(true);
  };

  // ==================== RENDER ====================

  const loading = productLoading;
  const productTableColSpan = isCurrentUserSuperAdmin ? 9 : 8;
  const ROUTE_DISPLAY = { oral: 'Oral / Solid', topical: 'Topical / Dermal', injectable: 'Injectable / Parenteral', inhalation: 'Inhalation', ophthalmic: 'Ophthalmic', transdermal: 'Transdermal' };
  const lineRouteMap = Object.fromEntries(commercialLines.map(l => [l.line_name, l.route_of_admin || '']));
  const isDraftESignMode = eSignMode === 'draft';

  // Count products eligible for each approval stage
  const productsEligibleForProductApproval = selectedProducts.filter(productId => {
    const product = products.find(p => p.product_id === productId);
    return product && normalizeDisplayStatus(product.status) === PRODUCT_STATUS.PENDING_PRODUCT_APPROVAL;
  }).length;

  const productsEligibleForQAApproval = selectedProducts.filter(productId => {
    const product = products.find(p => p.product_id === productId);
    return product && normalizeDisplayStatus(product.status) === PRODUCT_STATUS.PENDING_QA_APPROVAL;
  }).length;

  if (loading) return <div className="loading-container"><div className="spinner"></div></div>;

  return (
    <div>
      <div>
        <div className="product-master-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
          <div className="product-master-header-copy">
            <h2 className="page-title">Product Master</h2>
            <p className="page-subtitle">Product catalog with dosage form, equipment train linkage, APIs and approval status.</p>
          </div>
              <div className="toolbar-full" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button
                  className="btn btn-outline"
                  title="AI-suggest worst-case product groupings from risk profile and dosage form"
                  onClick={() => grouping.run('AI Suggest Grouping — Worst-Case Products', () => aiAPI.suggestGrouping('product_worstcase'))}
                >
                  <FaLayerGroup /> AI Suggest Grouping
                </button>
                {isCurrentUserSuperAdmin && selectedProducts.length > 0 && (
                  <button className="btn btn-danger" onClick={handleBulkDelete}>
                    <FaTrash /> Deactivate Selected ({selectedProducts.length})
                  </button>
                )}
                {canApproveProductStage && productsEligibleForProductApproval > 0 && (
                  <>
                    <button className="btn btn-warning" onClick={handleBulkApprovalForProducts}>
                      <FaCheck /> Approve Products ({productsEligibleForProductApproval})
                    </button>
                    {canApproveQaStage && productsEligibleForQAApproval > 0 && (
                      <button className="btn btn-info" onClick={handleBulkApprovalForQA}>
                        <FaCheck /> QA Approve ({productsEligibleForQAApproval})
                      </button>
                    )}
                  </>
                )}
                {canApproveQaStage && productsEligibleForQAApproval > 0 && productsEligibleForProductApproval === 0 && (
                  <button className="btn btn-info" onClick={handleBulkApprovalForQA}>
                    <FaCheck /> QA Approve ({productsEligibleForQAApproval})
                  </button>
                )}
                <button className="btn btn-success" onClick={exportProductToExcel}>
                  <FaDownload /> Excel
                </button>
                <button className="btn btn-danger" onClick={exportProductToPDF}>
                  <FaDownload /> PDF
                </button>
                <button className="btn btn-outline" onClick={downloadProductImportTemplate}>
                  <FaDownload /> Import Template
                </button>
                <label className="btn btn-info" style={{ margin: 0, cursor: 'pointer' }}>
                  <FaUpload /> Import
                  <input type="file" accept=".csv,.xlsx,.xls" onChange={handleProductFileImport} style={{ display: 'none' }} />
                </label>
                {productSyncSources.map((source) => (
                  <button key={source.key} className="btn btn-outline" onClick={() => fetchProductsFrom(source)} disabled={!!fetchingProductSource} title={`Download new products from ${source.label} as a pre-filled import file`}>
                    <FaDownload /> {fetchingProductSource === source.key ? 'Fetching…' : `Fetch from ${source.label}`}
                  </button>
                ))}
                <button className="btn btn-primary" onClick={handleProductAdd}>
                  <FaCapsules /> Add Product
                </button>
          </div>

          {/* Mobile: primary action + "more" menu, kept to a single row */}
          <div className="toolbar-compact" style={{ gap: '10px' }}>
            <button className="btn btn-primary" onClick={handleProductAdd}>
              <FaCapsules /> Add Product
            </button>
            <ActionsMenu
              title="More actions"
              items={[
                { label: 'AI Suggest Grouping', icon: <FaLayerGroup />, onClick: () => grouping.run('AI Suggest Grouping — Worst-Case Products', () => aiAPI.suggestGrouping('product_worstcase')) },
                { label: `Deactivate Selected (${selectedProducts.length})`, icon: <FaTrash />, danger: true, show: isCurrentUserSuperAdmin && selectedProducts.length > 0, onClick: handleBulkDelete },
                { label: `Approve Products (${productsEligibleForProductApproval})`, icon: <FaCheck />, show: canApproveProductStage && productsEligibleForProductApproval > 0, onClick: handleBulkApprovalForProducts },
                { label: `QA Approve (${productsEligibleForQAApproval})`, icon: <FaCheck />, show: canApproveQaStage && productsEligibleForQAApproval > 0, onClick: handleBulkApprovalForQA },
                { divider: true },
                { label: 'Export Excel', icon: <FaDownload />, onClick: exportProductToExcel },
                { label: 'Export PDF', icon: <FaDownload />, onClick: exportProductToPDF },
                { label: 'Import Template', icon: <FaDownload />, onClick: downloadProductImportTemplate },
                { label: 'Import', icon: <FaUpload />, onClick: () => mobileImportInputRef.current?.click() },
                ...productSyncSources.map((source) => ({ label: `Fetch from ${source.label}`, icon: <FaDownload />, onClick: () => fetchProductsFrom(source) })),
              ]}
            />
            <input ref={mobileImportInputRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleProductFileImport} style={{ display: 'none' }} />
          </div>
        </div>

        <div className="stat-card-grid">
          {[
            { key: 'All', title: 'Total Products', value: products.length, icon: FaCapsules, color: 'primary' },
            { key: 'Approved', title: 'Approved', value: products.filter((p) => normalizeDisplayStatus(p.status) === PRODUCT_STATUS.APPROVED).length, icon: FaCheck, color: 'success' },
            { key: 'Pending', title: 'Pending Approval', value: products.filter((p) => [PRODUCT_STATUS.PENDING_PRODUCT_APPROVAL, PRODUCT_STATUS.PENDING_QA_APPROVAL].includes(normalizeDisplayStatus(p.status))).length, icon: FaHourglassHalf, color: 'warning' },
            { key: 'Draft', title: 'Draft', value: products.filter((p) => normalizeDisplayStatus(p.status) === PRODUCT_STATUS.DRAFT).length, icon: FaFileAlt, color: 'info' },
          ].map((c) => (
            <StatCard
              key={c.key} compact filterHint title={c.title} value={c.value} icon={c.icon} color={c.color}
              active={productStatusCard === c.key}
              onClick={() => { setProductStatusCard(c.key === 'All' || productStatusCard === c.key ? 'All' : c.key); setProductCurrentPage(1); }}
            />
          ))}
        </div>

        {/* Search & Filter Bar */}
        <div className="product-master-filter-row" style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                <SearchBox
                  placeholder="Search by code, name, or dosage form..."
                  name="product_master_lookup"
                  data-form-type="other"
                  data-lpignore="true"
                  data-1p-ignore
                  data-bitwarden-ignore
                  value={productSearchTerm}
                  onChange={(e) => setProductSearchTerm(e.target.value)}
                />
                <FilterSelect label="Dosage Form:" value={productDosageFilter} onChange={(e) => setProductDosageFilter(e.target.value)}>
                  {dosageFilterOptions.map((dosage) => (
                    <option key={dosage} value={dosage}>{dosage === 'All' ? 'All Dosage' : dosage}</option>
                  ))}
                </FilterSelect>
                <FilterSelect label="Equipment Train:" value={productTrainFilter} onChange={(e) => setProductTrainFilter(e.target.value)} selectStyle={{ minWidth: '150px' }}>
                  <option value="All">All Trains</option>
                  {trainFilterOptions.map((opt) => (
                    <option key={opt.train_id} value={opt.train_id}>{opt.label}</option>
                  ))}
                </FilterSelect>
                {productLineFilter && productLineFilter !== 'All' && (
                  <div className="product-master-section-badge" style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: '0 0 auto' }}>
                    <span style={{ fontSize: 'var(--fs-md)', fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Section:</span>
                    <span style={{ padding: '4px 10px', borderRadius: '10px', background: 'var(--success-color, #10b981)', color: '#fff', fontSize: 'var(--fs-md)', fontWeight: 600, whiteSpace: 'nowrap' }}>{productLineFilter}</span>
                  </div>
                )}
        </div>

            {isCurrentUserSuperAdmin && selectedProducts.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={handleOpenAddToExistingTrain}
                  title="Add selected products to an existing equipment train"
                >
                  <FaPlus /> Add to Existing Train ({selectedProducts.length})
                </button>
              </div>
            )}

            <FloatingScrollWrapper innerClassName="table-container">
              <table>
                <thead>
                  <tr>
                    {isCurrentUserSuperAdmin && (
                      <th style={{ width: '40px' }}>
                        <input
                          type="checkbox"
                          onChange={handleSelectAll}
                          checked={sortedProducts.length > 0 && selectedProducts.length === sortedProducts.length}
                        />
                      </th>
                    )}
                    <ResizableTh column="product_code" defaultWidth={130} widths={productColWidths} onResize={setProductColWidth}>
                      <button type="button" onClick={() => handleHeaderSort('product_code')} style={{ border: 'none', background: 'transparent', fontWeight: '600', cursor: 'pointer', padding: 0, color: 'inherit' }}>
                        Product Code {renderSortIndicator('product_code')}
                      </button>
                    </ResizableTh>
                    <ResizableTh column="product_name" defaultWidth={200} widths={productColWidths} onResize={setProductColWidth}>
                      <button type="button" onClick={() => handleHeaderSort('product_name')} style={{ border: 'none', background: 'transparent', fontWeight: '600', cursor: 'pointer', padding: 0, color: 'inherit' }}>
                        Product Name {renderSortIndicator('product_name')}
                      </button>
                    </ResizableTh>
                    <ResizableTh column="dosage_form" defaultWidth={130} widths={productColWidths} onResize={setProductColWidth} title="Dosage Form / Route of Administration">
                      <button type="button" onClick={() => handleHeaderSort('dosage_form')} style={{ border: 'none', background: 'transparent', fontWeight: '600', cursor: 'pointer', padding: 0, color: 'inherit' }}>
                        Dosage Form / Route {renderSortIndicator('dosage_form')}
                      </button>
                    </ResizableTh>
                    <ResizableTh column="equipment_train" defaultWidth={150} widths={productColWidths} onResize={setProductColWidth}>
                      <button type="button" onClick={() => handleHeaderSort('equipment_train_id')} style={{ border: 'none', background: 'transparent', fontWeight: '600', cursor: 'pointer', padding: 0, color: 'inherit' }}>
                        Equipment Train {renderSortIndicator('equipment_train_id')}
                      </button>
                    </ResizableTh>
                    <ResizableTh column="batch_size" defaultWidth={110} widths={productColWidths} onResize={setProductColWidth}>
                      <button type="button" onClick={() => handleHeaderSort('batch_size')} style={{ border: 'none', background: 'transparent', fontWeight: '600', cursor: 'pointer', padding: 0, color: 'inherit' }}>
                        Batch Size {renderSortIndicator('batch_size')}
                      </button>
                    </ResizableTh>
                    <ResizableTh column="status" defaultWidth={120} widths={productColWidths} onResize={setProductColWidth}>
                      <button type="button" onClick={() => handleHeaderSort('status')} style={{ border: 'none', background: 'transparent', fontWeight: '600', cursor: 'pointer', padding: 0, color: 'inherit' }}>
                        Status {renderSortIndicator('status')}
                      </button>
                    </ResizableTh>
                    <ResizableTh column="risk" defaultWidth={120} widths={productColWidths} onResize={setProductColWidth} title="Cleaning Validation risk ranking level (auto-generated on product creation). The Worst Case / CV Required tag is shown only on the one product per equipment train that is the CV worst case.">Risk Ranking</ResizableTh>
                    <ResizableTh column="apis" defaultWidth={260} widths={productColWidths} onResize={setProductColWidth}>
                      <button type="button" onClick={() => handleHeaderSort('apis')} style={{ border: 'none', background: 'transparent', fontWeight: '600', cursor: 'pointer', padding: 0, color: 'inherit' }}>
                        APIs & Label Claims {renderSortIndicator('apis')}
                      </button>
                    </ResizableTh>
                    <ResizableTh column="actions" defaultWidth={120} widths={productColWidths} onResize={setProductColWidth} style={{ textAlign: 'center' }}>Actions</ResizableTh>
                  </tr>
                </thead>
                <tbody>
                  {productCurrentItems.length === 0 ? (
                    <tr><td colSpan={productTableColSpan} className="text-center">No products found</td></tr>
                  ) : (
                    productCurrentItems.map((product) => {
                      const displayStatus = normalizeDisplayStatus(product.status);
                      const isDeletionPending = product.deletion_requested;
                      const showProductApproveButton = canApproveProductStage && !isDeletionPending && displayStatus === PRODUCT_STATUS.PENDING_PRODUCT_APPROVAL;
                      const showQaApproveButton = canApproveQaStage && !isDeletionPending && displayStatus === PRODUCT_STATUS.PENDING_QA_APPROVAL;
                      const showQaDeleteApproveButton = canApproveQaStage && isDeletionPending && product.deletion_product_approved && !product.deletion_qa_approved;

                      return (
                        <tr
                          key={product.product_id}
                          onClick={() => handleRowClick(product)}
                          onDoubleClick={() => handleProductView(product)}
                          className={selectedProductRow === product.product_id ? 'selected' : ''}
                          style={{ cursor: 'pointer' }}
                        >
                          {isCurrentUserSuperAdmin && (
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedProducts.includes(product.product_id)}
                                onChange={() => handleSelectProduct(product.product_id)}
                              />
                            </td>
                          )}
                          <td><strong>{product.product_code}</strong></td>
                          <td style={{ maxWidth: '250px', fontSize: 'var(--fs-base)' }}>{product.product_name}</td>
                          <td>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                              <span className="badge badge-primary">{product.dosage_form}</span>
                              {(() => {
                                const route = lineRouteMap[product.production_line] || '';
                                const label = ROUTE_DISPLAY[route] || route;
                                if (!label) return <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--fs-sm)' }}>—</span>;
                                return (
                                  <button
                                    type="button"
                                    title={`Route: ${label}\nClick to view MACO Dose Safety Factor settings for this route`}
                                    onClick={(e) => { e.stopPropagation(); navigate('/maco', { state: { openTab: 'settings', highlightRoute: route } }); }}
                                    style={{ fontSize: 'var(--fs-sm)', padding: '2px 8px', borderRadius: '10px', background: 'color-mix(in srgb, var(--success-color, #10b981) 12%, var(--card-bg))', color: 'var(--success-color, #10b981)', border: '1px solid var(--success-color, #10b981)', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
                                  >
                                    {label}
                                  </button>
                                );
                              })()}
                            </div>
                          </td>
                          <td>
                            {getPrimaryTrainLink(product)?.train_id ? (
                              <span
                                className="record-link"
                                title="Open this equipment train"
                                onClick={(e) => { e.stopPropagation(); navigate('/equipment-trains', { state: { openViewTrainId: getPrimaryTrainLink(product).train_id } }); }}
                              >
                                {getTrainNameForProduct(product)}
                              </span>
                            ) : getTrainNameForProduct(product)}
                          </td>
                          <td>{product.batch_size} {product.batch_size_unit}</td>
                          <td>
                            <span
                              className={`badge ${getApprovalStatusInfo(product).badgeClass}`}
                              title={getApprovalStatusInfo(product).tooltip}
                            >
                              {getApprovalStatusInfo(product).text}
                            </span>
                          </td>
                          <td>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                              {(() => {
                                const risk = riskByProductId[String(product.product_id)];
                                if (!risk) return <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--fs-sm)' }}>Not generated</span>;
                                const colors = RISK_LEVEL_COLORS[risk.risk_color] || RISK_LEVEL_COLORS.green;
                                return (
                                  <span
                                    title={`Risk Ranking Score: ${risk.total_risk_score}\nStatus: ${risk.status}\nClick "Risk" button below for details`}
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 9px', borderRadius: '999px', fontWeight: 700, fontSize: 'var(--fs-xs)', background: colors.badge, color: colors.text, border: `1px solid ${colors.border}` }}
                                  >
                                    {risk.risk_level} ({risk.total_risk_score})
                                  </span>
                                );
                              })()}
                              {(() => {
                                const isWorstCase = isWorstCaseProduct(product);
                                const trainId = product?.equipment_train_id;
                                const hasTrain = trainId != null && trainId !== '';
                                if (!hasTrain || !isWorstCase) return null;
                                return (
                                  <span
                                    title="This product is the CV worst case for its equipment train (highest cleaning-validation risk score, per Worst Case Ranking)."
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 9px', borderRadius: '999px', fontWeight: 700, fontSize: 'var(--fs-xs)', background: '#ede9fe', color: '#6d28d9', border: '1px solid #c4b5fd' }}
                                  >
                                    Worst Case
                                  </span>
                                );
                              })()}
                              {(() => {
                                const impact = impactByProductId[String(product.product_id)];
                                if (!impact) return null;
                                if (!isWorstCaseProduct(product)) return null;
                                const cvColors = impact.cv_required ? { badge: '#fee2e2', text: '#b91c1c', border: '#fca5a5' } : { badge: '#dcfce7', text: '#15803d', border: '#86efac' };
                                return (
                                  <span
                                    title={`CV Impact Assessment\nStatus: ${impact.status}\nClick "Impact Assessment" in the row menu for details`}
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 9px', borderRadius: '999px', fontWeight: 700, fontSize: 'var(--fs-xs)', background: cvColors.badge, color: cvColors.text, border: `1px solid ${cvColors.border}` }}
                                  >
                                    CV {impact.cv_required ? 'Required' : 'Not Required'}{impact.status === 'Pending Documentation' ? ' *' : ''}
                                  </span>
                                );
                              })()}
                            </div>
                          </td>
                          <td>
                            <ul style={{ margin: 0, paddingLeft: '15px', fontSize: 'var(--fs-md)' }}>
                              {(product.apis || []).map((api, i) => {
                                const apiDetails = allApis.find(a => String(a.api_id) === String(api.api_id));
                                const solubility = api.solubility_classification || apiDetails?.solubility_classification || '-';
                                const cleanability = api.cleanability_rating || apiDetails?.cleanability_rating || '-';
                                return (
                                  <li key={i} style={{ marginBottom: '3px', cursor: 'help' }} title={`Solubility: ${solubility}\nCleanability: ${cleanability}`}>
                                    <strong>{apiDetails?.api_name || api.api_name || 'Unknown'}:</strong> {api.label_claim} {api.label_claim_unit}
                                  </li>
                                );
                              })}
                            </ul>
                          </td>
                          <td className="action-cell" style={{ width: '120px', textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', alignItems: 'center' }}>
                              {showProductApproveButton && (
                                <button className="btn btn-sm btn-success" onClick={(e) => { e.stopPropagation(); handleProductStageApproval(product, 'product'); }} title="Approve Product Stage">
                                  <FaCheck />
                                </button>
                              )}
                              {showQaApproveButton && (
                                <button className="btn btn-sm btn-warning" onClick={(e) => { e.stopPropagation(); handleProductStageApproval(product, 'qa'); }} title="Approve QA Stage">
                                  QA
                                </button>
                              )}
                              {showQaDeleteApproveButton && (
                                <button className="btn btn-sm btn-warning" onClick={(e) => { e.stopPropagation(); handleProductStageApproval(product, 'qa'); }} title="Approve QA Deactivation Stage">
                                  QA Deactivate
                                </button>
                              )}
                              {product.deletion_requested && !product.deletion_product_approved && (
                                <button className="btn btn-sm btn-danger-outline" onClick={(e) => { e.stopPropagation(); handleProductDelete(product); }} title="Deactivate Product">
                                  <FaTrash />
                                </button>
                              )}
                              <ActionsMenu items={[
                                {
                                  label: '360 View',
                                  icon: <FaLayerGroup />,
                                  description: 'Everything related to this product — trains, risk/impact, worst case, MACO, sampling plans, results, CV documents, revalidation — in one place',
                                  onClick: () => navigate(`/product-master/${product.product_id}/overview`),
                                },
                                {
                                  label: 'Version History',
                                  icon: <FaHourglassHalf />,
                                  description: 'Every approved revision of this product, what changed, who, and when',
                                  onClick: () => setHistoryModal(product),
                                },
                                {
                                  label: 'Risk Assessment',
                                  icon: <FaShieldAlt />,
                                  module: 'risk_assessment',
                                  description: 'View/review the auto-generated Risk Ranking Assessment for this product',
                                  onClick: () => {
                                    const api = (product.apis || [])[0] || {};
                                    const params = new URLSearchParams({
                                      product_id: product.product_id || '',
                                      product_name: product.product_name || '',
                                      product_code: product.product_code || '',
                                      api_name: api.api_name || '',
                                      ade_pde_value: api.pde_value || '',
                                      solubility_class: api.solubility_classification || api.solubility || '',
                                      cleanability_rating: api.cleanability_rating || api.cleanability || '',
                                    });
                                    navigate(`/risk-assessment?${params.toString()}`);
                                  },
                                },
                                {
                                  label: 'Impact Assessment',
                                  icon: <FaExclamationTriangle />,
                                  description: 'View the recorded cleaning validation (CV) impact assessment for this product',
                                  module: 'impact_assessment',
                                  onClick: () => openImpactViewModal(product),
                                },
                                {
                                  label: 'Generate Risk Assessment',
                                  icon: <FaShieldAlt />,
                                  module: 'risk_assessment',
                                  description: 'This product has no risk assessment yet (likely bulk-imported) — generate one now from its linked API and equipment train data',
                                  onClick: () => handleGenerateRiskAssessment(product),
                                  show: !product.has_risk_assessment,
                                },
                                { label: 'View Details', icon: <FaEye />, onClick: () => handleProductView(product) },
                                { label: 'Edit', icon: <FaEdit />, onClick: () => handleProductEdit(product), show: !isPendingEditApproval(product) },
                                { label: 'Duplicate', icon: <FaCopy />, onClick: () => handleProductDuplicate(product), show: !isPendingEditApproval(product) },
                                { label: 'Deactivate', icon: <FaTrash />, onClick: () => handleProductDelete(product), show: !isPendingEditApproval(product), danger: true },
                              ]} />
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </FloatingScrollWrapper>

            {/* Pagination Controls */}
            <TablePagination total={sortedProducts.length} page={productCurrentPage} pageSize={itemsPerPage} onPageChange={setProductCurrentPage} onPageSizeChange={(n) => { setItemsPerPage(n); setProductCurrentPage(1); }} itemLabel="products" />
          </div>

          {/* Product Add/Edit Modal */}
          <Modal
            isOpen={showProductModal}
            size="xl"
            onClose={() => { setShowProductModal(false); setShowESignModal(false); resetProductForm(); setPendingApproval(null); setPendingDelete(null); setPendingBulkDelete(null); setPendingAction(null); setPendingBulkApproval(null); }}
            onKeyDown={(e) => showESignModal && e.stopPropagation()}
            title={<><FaCapsules style={{ color: 'var(--primary-color, #2563eb)' }} /> {productEditingId ? 'Edit' : 'Add'} Product</>}
            footer={
              <>
                <button type="button" className="btn btn-outline" onClick={() => { setShowProductModal(false); setShowESignModal(false); resetProductForm(); setPendingApproval(null); setPendingDelete(null); setPendingBulkDelete(null); setPendingAction(null); setPendingBulkApproval(null); }} style={{ padding: '10px 24px' }}>Cancel</button>
                <button type="button" className="btn btn-secondary" onClick={() => handleProceedToSave('draft')} style={{ padding: '10px 24px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FaSave /> Save as Draft
                </button>
                <button type="button" className="btn btn-primary" onClick={() => handleProceedToSave('submit')} style={{ padding: '10px 24px', boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.2)' }}>Submit for Approval</button>
              </>
            }
          >
                  <form onSubmit={(e) => e.preventDefault()}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px' }}>

                      {/* Card 1: Product Details */}
                      <div style={{ background: 'var(--card-bg)', padding: '12px', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid var(--border-color)' }}>
                        <h4 style={{ fontSize: 'var(--fs-xl)', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ width: '4px', height: '20px', background: 'var(--primary-color, #2563eb)', borderRadius: '2px' }}></span>
                          <FaCapsules style={{ color: 'var(--primary-color, #2563eb)' }} />
                          Product Details
                        </h4>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '20px', marginBottom: '20px' }}>
                          <div className="form-group">
                            <label className="form-label" style={{ fontWeight: '500', color: 'var(--text-secondary)' }}>Product Code *</label>
                            <input className="form-control" value={productFormData.product_code} onChange={(e) => setProductFormData({ ...productFormData, product_code: e.target.value })} required style={{ padding: '10px 12px', borderRadius: '6px', border: '1px solid var(--border-color)' }} />
                          </div>
                          <div className="form-group">
                            <label className="form-label" style={{ fontWeight: '500', color: 'var(--text-secondary)' }}>Product Name *</label>
                            <input className="form-control" value={productFormData.product_name} onChange={(e) => setProductFormData({ ...productFormData, product_name: e.target.value })} required style={{ padding: '10px 12px', borderRadius: '6px', border: '1px solid var(--border-color)' }} />
                          </div>
                        </div>
                        <div style={{ marginBottom: '20px' }}>
                          <label className="form-label" style={{ fontWeight: '500', color: 'var(--text-secondary)' }}>Equipment Train</label>
                          <div style={{ position: 'relative' }}>
                            {productFormData.equipment_train_id ? (
                              <div style={{ display: 'flex', alignItems: 'center', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '0.25rem', padding: '0.375rem 0.75rem', height: '38px' }}>
                                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 'var(--fs-base)' }}>
                                  {getTrainLabel(productFormData.equipment_train_id, productFormData.equipment_train_code, productFormData.equipment_train_name)}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setProductFormData((prev) => ({ ...prev, equipment_train_id: '', equipment_train_name: '', equipment_train_code: '', linked_trains: [] }));
                                    setTrainSearchTerm('');
                                    setShowTrainDropdown(false);
                                  }}
                                  style={{ border: 'none', background: 'transparent', color: 'var(--danger-color)', cursor: 'pointer', padding: '0 0 0 8px' }}
                                >
                                  x
                                </button>
                              </div>
                            ) : (
                              <>
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Search train code or name..."
                                  value={trainSearchTerm}
                                  onChange={(e) => setTrainSearchTerm(e.target.value)}
                                  onFocus={() => { setShowTrainDropdown(true); if (!trainOptions.length) loadTrains(); }}
                                  onBlur={() => setTimeout(() => setShowTrainDropdown(false), 250)}
                                  style={{ padding: '10px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '100%' }}
                                />
                                {showTrainDropdown && (
                                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000, background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', maxHeight: '220px', display: 'flex', flexDirection: 'column', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }} onMouseDown={(e) => e.preventDefault()}>
                                    <div style={{ overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}>
                                      {trainOptions.length === 0 && (
                                      <div style={{ padding: '8px 12px', color: 'var(--text-secondary)', fontSize: 'var(--fs-base)' }}>No trains loaded</div>
                                    )}
                                    {trainOptions.filter((t) =>
                                      String(t.train_name || '').toLowerCase().includes(trainSearchTerm.toLowerCase()) ||
                                      String(t.train_code || '').toLowerCase().includes(trainSearchTerm.toLowerCase())
                                    ).map((train) => (
                                      <div
                                        key={train.train_id}
                                        onMouseDown={(e) => {
                                          e.preventDefault();
                                          setProductFormData((prev) => {
                                            const otherLinks = getSecondaryTrainLinks(prev).filter((link) => String(link.train_id) !== String(train.train_id));
                                            return {
                                              ...prev,
                                              equipment_train_id: train.train_id,
                                              equipment_train_name: train.train_name || '',
                                              equipment_train_code: train.train_code || '',
                                              linked_trains: [{
                                                train_id: train.train_id,
                                                train_code: train.train_code || '',
                                                train_name: train.train_name || '',
                                                is_primary: true,
                                                qualification_remark: '',
                                                same_size_equipment: false,
                                                same_cleaning_method: false,
                                                cv_batch_remark: ''
                                              }, ...otherLinks.map((link) => ({ ...link, is_primary: false }))]
                                            };
                                          });
                                          setTrainSearchTerm('');
                                          setShowTrainDropdown(false);
                                        }}
                                        style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border-light)' }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--card-hover-bg, #f9fafb)'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                      >
                                        <strong>{train.train_code}</strong> - {train.train_name}
                                      </div>
                                    ))}
                                    {trainOptions.filter((t) =>
                                      String(t.train_name || '').toLowerCase().includes(trainSearchTerm.toLowerCase()) ||
                                      String(t.train_code || '').toLowerCase().includes(trainSearchTerm.toLowerCase())
                                    ).length === 0 && (
                                      <div style={{ padding: '8px 12px', color: 'var(--text-secondary)', fontSize: 'var(--fs-base)' }}>
                                        No matches found.
                                      </div>
                                    )}
                                    </div>
                                    <div style={{ padding: '8px 12px', borderTop: '1px solid var(--border-light)', background: 'var(--card-bg)', flex: '0 0 auto' }}>
                                      <button
                                        type="button"
                                        onMouseDown={(e) => {
                                          e.preventDefault();
                                          window.location.href = '/equipment-trains';
                                        }}
                                        style={{ display: 'inline-block', color: 'var(--primary-dark, #1e40af)', textDecoration: 'none', fontWeight: 700, background: 'var(--info-bg, #dbeafe)', border: '1px solid var(--info-border, #93c5fd)', padding: '4px 8px', borderRadius: '999px', boxShadow: '0 0 12px rgba(59,130,246,0.35)', cursor: 'pointer' }}
                                      >
                                        + Add New Train
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                        </div>
                      </div>
                      {productFormData.equipment_train_id && (
                        <div style={{ marginBottom: '20px', padding: '12px', background: 'var(--card-bg)', borderRadius: '6px', border: '1px solid var(--border-color)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <label style={{ fontWeight: 'bold', color: 'var(--text-secondary)', margin: 0 }}>Linked Trains</label>
                            <button
                              type="button"
                              onClick={() => { setShowAddSecondaryTrainModal(true); setSecondaryTrainSearchTerm(''); }}
                              style={{ background: 'var(--primary-dark, #1e40af)', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: 'var(--fs-base)', fontWeight: 500 }}
                            >
                              + Add Equipment Train
                            </button>
                          </div>
                          {Array.isArray(productFormData.linked_trains) && productFormData.linked_trains.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              {productFormData.linked_trains.map((train, index) => (
                                <div key={train.train_id} style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '4px', padding: '8px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <div style={{ flex: 1 }}>
                                    <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{train.train_code || train.train_id} - {train.train_name}</span>
                                    <span style={{ marginLeft: '12px', padding: '2px 8px', borderRadius: '3px', fontSize: 'var(--fs-md)', fontWeight: 600, background: train.is_primary ? 'var(--success-bg, #dcfce7)' : 'var(--info-bg, #dbeafe)', color: train.is_primary ? 'var(--success-text, #166534)' : 'var(--info-text, #1e40af)' }}>
                                      {getTrainTypeLabel(index)}
                                    </span>
                                  </div>
                                  {!train.is_primary && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setProductFormData((prev) => ({
                                          ...prev,
                                          linked_trains: (prev.linked_trains || []).filter((t) => t.train_id !== train.train_id)
                                        }));
                                      }}
                                      style={{ background: 'none', border: 'none', color: 'var(--danger-color)', cursor: 'pointer', fontSize: 'var(--fs-xl)', padding: '0 4px' }}
                                      title="Remove secondary train"
                                    >
                                      ×
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 'var(--fs-base)' }}>No additional trains linked</p>
                          )}
                        </div>
                      )}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '12px' }}>
                          {commercialLines.length > 0 && (() => {
                            const ROUTE_DISPLAY = { oral: 'Oral / Solid', topical: 'Topical / Dermal', injectable: 'Injectable / Parenteral', inhalation: 'Inhalation', ophthalmic: 'Ophthalmic', transdermal: 'Transdermal' };
                            const selectedLineObj = commercialLines.find((l) => l.line_name === (productFormData.production_line || ''));
                            const selectedRoute = selectedLineObj?.route_of_admin || '';
                            const routeLabel = ROUTE_DISPLAY[selectedRoute] || selectedRoute;
                            return (
                              <>
                                <div className="form-group" style={{ marginBottom: 0 }}>
                                  <label className="form-label" style={{ fontWeight: '500', color: 'var(--text-secondary)' }}>Section</label>
                                  <select
                                    className="form-control"
                                    value={productFormData.production_line || ''}
                                    onChange={(e) => {
                                      const lineName = e.target.value;
                                      const lineObj = commercialLines.find((l) => l.line_name === lineName);
                                      const routeDosageMap = { oral: 'Tablet', topical: 'Ointment', injectable: 'Dry Powder Injection', inhalation: 'Solution', ophthalmic: 'Solution', transdermal: 'Ointment' };
                                      const newDosage = lineObj?.route_of_admin ? (routeDosageMap[lineObj.route_of_admin] || productFormData.dosage_form) : productFormData.dosage_form;
                                      setProductFormData((prev) => ({
                                        ...prev,
                                        production_line: lineName,
                                        dosage_form: newDosage,
                                        ...(String(newDosage || '').trim().toLowerCase() === 'tablet' ? {} : { coating_type: '', coating_agent: '' })
                                      }));
                                    }}
                                    style={{ padding: '10px 12px', borderRadius: '6px', border: '1px solid var(--border-color)' }}
                                  >
                                    <option value="">-- Select Section --</option>
                                    {commercialLines.map((l) => (
                                      <option key={l.line_code || l.line_name} value={l.line_name}>
                                        {l.line_name}{l.route_of_admin ? ` (${ROUTE_DISPLAY[l.route_of_admin] || l.route_of_admin})` : ''}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                {selectedRoute && (
                                  <div className="form-group" style={{ marginBottom: 0 }}>
                                    <label className="form-label" style={{ fontWeight: '500', color: 'var(--text-secondary)' }}>Route of Administration</label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginTop: '4px' }}>
                                      <span style={{ padding: '5px 14px', borderRadius: '20px', background: 'color-mix(in srgb, var(--success-color, #10b981) 12%, var(--card-bg))', color: 'var(--success-color, #10b981)', border: '1px solid var(--success-color, #10b981)', fontWeight: 700, fontSize: 'var(--fs-md)' }}>
                                        {routeLabel}
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => navigate('/maco', { state: { openTab: 'settings', highlightRoute: selectedRoute } })}
                                        title={`View MACO Dose Safety Factor settings for ${routeLabel} route`}
                                        style={{ fontSize: 'var(--fs-sm)', padding: '4px 10px', borderRadius: '6px', border: '1px solid var(--info-border, #93c5fd)', background: 'var(--info-bg, #dbeafe)', color: 'var(--info-text, #1e40af)', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}
                                      >
                                        → View MACO Dose SF for this Route
                                      </button>
                                    </div>
                                    <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', marginTop: '4px' }}>
                                      Dosage form and MACO dose safety factor are auto-derived from this route.
                                    </div>
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
                          <div className="form-group">
                            <label className="form-label" style={{ fontWeight: '500', color: 'var(--text-secondary)' }}>Dosage Form *</label>
                            <select className="form-control" value={productFormData.dosage_form} onChange={(e) => {
                              const dosageForm = e.target.value;
                              setProductFormData((prev) => ({
                                ...prev,
                                dosage_form: dosageForm,
                                ...(String(dosageForm).trim().toLowerCase() === 'tablet' ? {} : { coating_type: '', coating_agent: '' })
                              }));
                            }} required style={{ padding: '10px 12px', borderRadius: '6px', border: '1px solid var(--border-color)' }}>
                              <option>Dry Powder Injection</option>
                              <option>Liquid Injection</option>
                              <option>Tablet</option>
                              <option>Capsule</option>
                              <option>Ointment</option>
                              <option>Solution</option>
                              <option>Dry Syrup</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label className="form-label" style={{ fontWeight: '500', color: 'var(--text-secondary)' }}>Batch Size (in Units)</label>
                            <input type="number" step="1" min="1" className="form-control" value={productFormData.batch_size} onChange={(e) => setProductFormData({ ...productFormData, batch_size: e.target.value })} style={{ padding: '10px 12px', borderRadius: '6px', border: '1px solid var(--border-color)' }} />
                          </div>
                          <div className="form-group" style={{ gridColumn: 'span 2' }}>
                            <div style={{ display: 'flex', gap: '10px' }}>
                              <div style={{ flex: 1 }}>
                                <label className="form-label" style={{ fontWeight: '500', color: 'var(--text-secondary)' }}>Fill Weight / Vol / Avg Wt</label>
                                <input type="number" step="any" min="0.000001" className="form-control" value={productFormData.fill_weight} onChange={(e) => setProductFormData({ ...productFormData, fill_weight: e.target.value })} style={{ padding: '10px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '100%' }} placeholder="Value" />
                              </div>
                              <div style={{ width: '120px' }}>
                                <label className="form-label" style={{ fontWeight: '500', color: 'var(--text-secondary)' }}>Unit</label>
                                <select className="form-control" value={productFormData.fill_unit} onChange={(e) => handleFillUnitChange(e.target.value)} style={{ padding: '10px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '100%' }}>
                                  {getFillUnitValueList(unitConfig).map((unit) => (
                                    <option key={unit} value={unit}>{unit}</option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Card 2: Associated APIs */}
                      <div style={{ background: 'var(--card-bg)', padding: '12px', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid var(--border-color)' }}>
                        <h4 style={{ fontSize: 'var(--fs-xl)', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ width: '4px', height: '20px', background: 'var(--success-color, #16a34a)', borderRadius: '2px' }}></span>
                          <FaFlask style={{ color: 'var(--success-color, #16a34a)' }} />
                          Associated APIs & Composition
                        </h4>

                        {productFormData.apis.map((api, index) => {
                          const selectedAPI = allApis.find(a => String(a.api_id) === String(api.api_id));
                          return (
                            <div key={api.api_id || index} style={{ background: 'var(--card-bg)', padding: '15px', borderRadius: '8px', marginBottom: '20px', border: '1px solid var(--border-color)', position: 'relative' }}>
                              {/* Top Row: API Selection Only */}
                              <div style={{ marginBottom: '15px', paddingBottom: '15px', borderBottom: '1px solid var(--border-color)' }}>
                                <label className='form-label' style={{ fontWeight: '500', color: 'var(--text-secondary)', fontSize: 'var(--fs-base)', marginBottom: '8px', display: 'block' }}>Search for Existing API Code / Name *</label>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '10px', alignItems: 'start' }}>
                                  <div className='form-group' style={{ position: 'relative', marginBottom: 0 }}>
                                    <input
                                      type='text'
                                      className='form-control'
                                      placeholder='Search API code or name...'
                                      name={`product_api_lookup_${index}`}
                                      autoComplete='off'
                                      data-form-type='other'
                                      data-lpignore='true'
                                      spellCheck={false}
                                      value={getApiSearchInputValue(index, selectedAPI)}
                                      onFocus={(e) => {
                                        setActiveApiDropdownIndex(index);
                                        if (!allApis.length) loadAllApis();
                                        e.target.select();
                                      }}
                                      onBlur={() => setTimeout(() => setActiveApiDropdownIndex((current) => (current === index ? null : current)), 150)}
                                      onChange={(e) => {
                                        setApiSearchTerms({ ...apiSearchTerms, [index]: e.target.value });
                                        setActiveApiDropdownIndex(index);
                                      }}
                                      style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border-color)' }}
                                    />
                                    {activeApiDropdownIndex === index && (
                                      <div style={{
                                        position: 'relative',
                                        marginTop: '4px',
                                        backgroundColor: 'var(--card-bg)',
                                        border: '1px solid var(--border-color)',
                                        borderRadius: '6px',
                                        zIndex: 2
                                      }} onMouseDown={(e) => e.preventDefault()}>
                                        <div style={{ padding: '6px 8px', borderBottom: '1px solid var(--border-color)' }}>
                                          <button
                                            type="button"
                                            className="btn btn-outline btn-sm"
                                            onMouseDown={(e) => {
                                              e.preventDefault();
                                              const term = (apiSearchTerms[index] || '').trim();
                                              const params = new URLSearchParams({ new: '1' });
                                              if (term) {
                                                if (/^[A-Za-z0-9_-]+$/.test(term) && term.length <= 20) {
                                                  params.set('code', term);
                                                } else {
                                                  params.set('name', term);
                                                }
                                              }
                                              window.open(`/api-master?${params.toString()}`, '_blank', 'noopener,noreferrer');
                                            }}
                                          >
                                            <FaPlus style={{ marginRight: 4 }} /> Add New API
                                          </button>
                                        </div>
                                        <div>
                                          {apisLoading ? (
                                            <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-secondary)' }}>Loading APIs...</div>
                                          ) : getFilteredApis(index).map((apiMaster) => (
                                            <div
                                              key={apiMaster.api_id}
                                              onMouseDown={(e) => {
                                                e.preventDefault();
                                                handleProductApiChange(index, 'api_id', apiMaster.api_id);
                                                const nextSearchTerms = { ...apiSearchTerms };
                                                delete nextSearchTerms[index];
                                                setApiSearchTerms(nextSearchTerms);
                                                setActiveApiDropdownIndex(null);
                                              }}
                                              style={{
                                                padding: '6px 10px',
                                                borderBottom: '1px solid var(--border-color)',
                                                cursor: 'pointer',
                                                backgroundColor: api.api_id === apiMaster.api_id ? 'var(--primary-light, rgba(37,99,235,0.12))' : 'var(--card-bg)',
                                                color: 'var(--text-primary)',
                                                transition: 'background-color 0.2s'
                                              }}
                                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--hover-bg, rgba(0,0,0,0.06))'}
                                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = api.api_id === apiMaster.api_id ? 'var(--primary-light, rgba(37,99,235,0.12))' : 'var(--card-bg)'}
                                            >
                                              <div style={{ fontWeight: '600', color: 'var(--text-primary)', fontSize: 'var(--fs-sm)', lineHeight: 1.2 }}>
                                                {apiMaster.api_code}
                                              </div>
                                              <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)', marginTop: '2px', lineHeight: 1.2 }}>
                                                {apiMaster.api_name}
                                              </div>
                                            </div>
                                          ))}
                                        {!apisLoading && getFilteredApis(index).length === 0 && (
                                          <div style={{ padding: '12px', textAlign: 'center' }}>
                                            <div style={{ color: 'var(--text-light)', fontSize: 'var(--fs-md)', marginBottom: '8px' }}>
                                              No APIs found{apiSearchTerms[index] ? ` for "${apiSearchTerms[index]}"` : ''}
                                            </div>
                                          </div>
                                        )}
                                        </div>
                                      </div>
                                    )}
                                    {api.api_id && selectedAPI && (
                                      <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--success-text, #059669)', marginTop: '4px', padding: '4px 8px', backgroundColor: 'var(--success-bg, #ecfdf5)', borderRadius: '4px', display: 'inline-block' }}>
                                        Selected: {selectedAPI.api_code} - {selectedAPI.api_name}
                                      </div>
                                    )}
                                  </div>
                                  <button type='button' className='btn btn-danger-outline' onClick={() => removeProductApiFromForm(index)} title='Remove' style={{ height: '40px', width: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, marginTop: '0' }}>
                                    <FaTrash />
                                  </button>
                                </div>
                              </div>

                              {/* API Details Section - requires an existing API to be selected from search above */}
                              {selectedAPI && (
                                <div style={{ backgroundColor: 'var(--card-bg)', padding: '15px', borderRadius: '6px', border: '1px solid var(--border-color)', marginTop: '10px' }}>
                                  <h5 style={{ fontSize: 'var(--fs-lg)', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: 'var(--fs-md)', fontWeight: 700 }}>Edit</span>
                                    {`Editing Details for: ${selectedAPI.api_name}`}
                                    <button
                                      type="button"
                                      title="Edit this API's master data (toxicology, solubility, cleanability, hazard classification) in API Master"
                                      onClick={() => navigate(`/api-master?api_id=${selectedAPI.api_id}`)}
                                      style={{ marginLeft: 'auto', background: 'var(--info-bg, #eff6ff)', color: 'var(--info-text, #2563eb)', border: '1px solid var(--info-border, #bfdbfe)', borderRadius: '6px', padding: '3px 10px', fontWeight: 600, fontSize: 'var(--fs-sm)', cursor: 'pointer' }}
                                    >
                                      Manage in API Master
                                    </button>
                                  </h5>
                                  <p style={{ margin: '0 0 12px 0', fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>
                                    Dosage, toxicological (PDE/LD50, hazard flags) and solubility/cleanability details for this API are maintained in API Master. Use "Manage in API Master" above to view or edit them.
                                  </p>

                                  {/* Label Claim & Composition */}
                                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 0.8fr 1.5fr auto', gap: '10px', marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid var(--border-light)' }}>
                                    <div className="form-group">
                                      <label className="form-label" style={{ fontWeight: '500', color: 'var(--text-secondary)', fontSize: 'var(--fs-md)' }}>Label Claim *</label>
                                      <input type="number" step="any" min="0.000001" className="form-control" value={productFormData.apis[index]?.label_claim || ''} onChange={(e) => handleProductApiChange(index, 'label_claim', e.target.value)} required style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid var(--border-color)', fontSize: 'var(--fs-md)' }} placeholder="e.g., 250" />
                                    </div>
                                    <div className="form-group">
                                      <label className="form-label" style={{ fontWeight: '500', color: 'var(--text-secondary)', fontSize: 'var(--fs-md)' }}>Unit *</label>
                                      <select className="form-control" value={productFormData.apis[index]?.label_claim_unit || getDefaultLabelClaimUnit(productFormData.fill_unit, unitConfig)} onChange={(e) => handleProductApiChange(index, 'label_claim_unit', e.target.value)} required style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid var(--border-color)', fontSize: 'var(--fs-md)' }}>
                                        {getLabelClaimUnitOptions(productFormData.fill_unit)}
                                      </select>
                                    </div>
                                    {(productFormData.apis[index]?.label_claim_unit?.includes('IU') || productFormData.apis[index]?.label_claim_unit?.includes('MIU')) && (
                                      <div className="form-group">
                                        <label className="form-label" style={{ fontWeight: '500', color: 'var(--text-secondary)', fontSize: 'var(--fs-md)' }}>IU/MIU Converter (to mg) *</label>
                                        <input type="number" step="any" min="0.000001" className="form-control" value={productFormData.apis[index]?.iu_to_mg_conversion || ''} onChange={(e) => handleProductApiChange(index, 'iu_to_mg_conversion', e.target.value)} required style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid var(--border-color)', fontSize: 'var(--fs-md)' }} placeholder="e.g., 1667" />
                                      </div>
                                    )}
                                  </div>

                                  {/* Product-level cleanability override */}
                                  {(() => {
                                    const entry = productFormData.apis[index] || {};
                                    const apiRating = selectedAPI.cleanability_rating || entry.api_cleanability_rating || '';
                                    const overrideFiles = parseFiles(entry.cleanability_override_document);
                                    const checklistPending = entry.cleanability_override_assessment_pending !== undefined;
                                    const checklist = checklistPending ? entry.cleanability_override_assessment_pending : entry.cleanability_override_assessment;
                                    const checklistLabel = [productFormData.product_code, selectedAPI.api_code || selectedAPI.api_name].filter(Boolean).join(' / ');
                                    const checklistSubject = productChecklistSubject(productFormData, { ...selectedAPI, api_cleanability_rating: apiRating });
                                    return (
                                      <div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid var(--border-light)' }}>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                          <div className="form-group" style={{ marginBottom: 0 }}>
                                            <label className="form-label" style={{ fontWeight: '500', color: 'var(--text-secondary)', fontSize: 'var(--fs-md)' }}>API Cleanability (from API Master)</label>
                                            <input className="form-control" value={apiRating || 'Not rated'} disabled />
                                          </div>
                                          <div className="form-group" style={{ marginBottom: 0 }}>
                                            <label className="form-label" style={{ fontWeight: '500', color: 'var(--text-secondary)', fontSize: 'var(--fs-md)' }} title="The formulation (coating, binders, lubricants, dyes) can make the product harder to clean than its API. The product rating is what Risk Assessment, Worst Case ranking, Impact Assessment and MACO use.">
                                              Product Cleanability
                                            </label>
                                            <div style={{ display: 'flex', gap: '6px' }}>
                                              <select className="form-control" style={{ flex: 1 }} value={entry.cleanability_override || ''} onChange={(e) => handleProductApiChange(index, 'cleanability_override', e.target.value)}>
                                                <option value="">Same as API{apiRating ? ` (${apiRating})` : ''}</option>
                                                {['Easy', 'Medium', 'Difficult'].map((r) => <option key={r} value={r}>{r} (override)</option>)}
                                              </select>
                                              <button type="button" className="btn btn-outline btn-sm" onClick={() => setOverrideChecklist({ index, readOnly: false })} title="Rate this product with the cleanability checklist (formulation, coating and excipients as they behave in cleaning)" style={{ whiteSpace: 'nowrap' }}>
                                                Help me rate
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                        {checklist && (
                                          <div style={{ marginTop: '6px', padding: '6px 10px', borderRadius: '6px', background: 'var(--bg-color)', border: '1px solid var(--border-color)', fontSize: 'var(--fs-sm)' }}>
                                            <div>
                                              Checklist: <strong>{checklist.total}/{checklist.max}</strong> → suggested <strong>{checklist.suggested}</strong>
                                              {checklist.final_rating !== checklist.suggested && <span style={{ color: 'var(--warning-text)' }}> · overridden to {checklist.final_rating}</span>}
                                              {checklistPending && <span style={{ color: 'var(--info-text)' }}> · not saved yet</span>}
                                            </div>
                                            <div style={{ display: 'flex', gap: '12px', marginTop: '2px', flexWrap: 'wrap' }}>
                                              <a href="#view" onClick={(e) => { e.preventDefault(); setOverrideChecklist({ index, readOnly: true }); }}>View answers</a>
                                              {!checklistPending && (
                                                <>
                                                  <a href="#print" onClick={(e) => { e.preventDefault(); printProductChecklist(checklistSubject, cleanConfig, checklist, checklistLabel); }}>Print checklist</a>
                                                  <a href="#excel" onClick={(e) => { e.preventDefault(); exportCleanabilityChecklistXlsx(checklistSubject, cleanConfig, checklist, checklistSubject.fileName); }}>Export checklist (Excel)</a>
                                                </>
                                              )}
                                              <a href="#remove" style={{ color: 'var(--danger-text)' }} onClick={(e) => { e.preventDefault(); handleProductApiChange(index, 'cleanability_override_assessment_pending', null); }}>Remove</a>
                                            </div>
                                          </div>
                                        )}
                                        {overrideChecklist?.index === index && (
                                          <CleanabilityAssessmentModal
                                            isOpen
                                            onClose={() => setOverrideChecklist(null)}
                                            config={cleanConfig}
                                            initial={checklist}
                                            readOnly={!!overrideChecklist.readOnly}
                                            apiLabel={checklistLabel}
                                            subject={checklistSubject}
                                            onApply={(result) => {
                                              const updatedApis = [...productFormData.apis];
                                              const current = updatedApis[index] || {};
                                              updatedApis[index] = {
                                                ...current,
                                                cleanability_override: result.final_rating,
                                                cleanability_override_assessment_pending: result,
                                                // The checklist is the basis; pre-fill the reason only if none is written yet
                                                cleanability_override_reason: String(current.cleanability_override_reason || '').trim()
                                                  || `Product cleanability checklist: score ${result.total}/${result.max}, suggested ${result.suggested}${result.final_rating !== result.suggested ? `, rated ${result.final_rating} (${result.override_reason})` : ''}.`,
                                              };
                                              setProductFormData({ ...productFormData, apis: updatedApis });
                                              setOverrideChecklist(null);
                                            }}
                                          />
                                        )}
                                        {entry.cleanability_override && (
                                          <div style={{ marginTop: '10px', padding: '10px', borderRadius: '6px', background: 'var(--warning-bg)', border: '1px solid var(--warning-border, var(--border-color))' }}>
                                            <label className="form-label" style={{ fontWeight: '500', color: 'var(--warning-text)', fontSize: 'var(--fs-md)' }}>Reason for override *</label>
                                            <textarea className="form-control" rows={2} value={entry.cleanability_override_reason || ''} onChange={(e) => handleProductApiChange(index, 'cleanability_override_reason', e.target.value)} placeholder="e.g. Film coat + Mg stearate; coupon study QC/CS/031 shows Difficult" />
                                            <label className="form-label" style={{ fontWeight: '500', color: 'var(--text-secondary)', fontSize: 'var(--fs-md)', marginTop: '8px' }}>Supporting document (study / assessment)</label>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                              <label className="btn btn-outline btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', margin: 0, cursor: 'pointer' }}>
                                                <FaUpload /> Choose file
                                                <input type="file" style={{ display: 'none' }} onChange={(e) => {
                                                  const file = e.target.files?.[0];
                                                  e.target.value = '';
                                                  if (file) handleProductApiChange(index, 'cleanability_override_document', [...overrideFiles, file]);
                                                }} />
                                              </label>
                                              {overrideFiles.length === 0 && <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>No file chosen</span>}
                                              {overrideFiles.map((file, fi) => {
                                                const name = file instanceof File ? file.name : String(file);
                                                return (
                                                  <span key={`${name}-${fi}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '2px 4px 2px 8px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--card-bg)', fontSize: 'var(--fs-sm)' }}>
                                                    <FaFileAlt style={{ color: 'var(--text-secondary)' }} />
                                                    {file instanceof File ? name : <a href={toFileUrl(name)} target="_blank" rel="noreferrer">{name}</a>}
                                                    <button type="button" title="Remove this file" onClick={() => handleProductApiChange(index, 'cleanability_override_document', overrideFiles.filter((_, k) => k !== fi))} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '20px', height: '20px', padding: 0, border: 'none', background: 'transparent', color: 'var(--danger-text)', cursor: 'pointer' }}>
                                                      <FaTimes />
                                                    </button>
                                                  </span>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })()}

                                  {/* API Quantity Card */}
                                  <div style={{ backgroundColor: 'var(--info-bg, #e0f2fe)', border: '1px solid var(--info-border, #7dd3fc)', borderRadius: '6px', padding: '12px', marginBottom: '15px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <div>
                                        <h6 style={{ margin: 0, color: 'var(--info-color)', fontSize: 'var(--fs-base)', fontWeight: '600' }}>Content per Unit</h6>
                                        <p style={{ margin: '4px 0 0 0', fontSize: 'var(--fs-md)', color: 'var(--info-text, #0c4a6e)' }}>
                                          Based on Label Claim & Avg. wt. /Fill wt. / Volume
                                        </p>
                                      </div>
                                      <div style={{ fontSize: 'var(--fs-xl)', fontWeight: '700', color: 'var(--info-text, #0284c7)' }}>
                                        {(() => {
                                          const result = calculateContentPerUnit(index);
                                          return (
                                            <div style={{ textAlign: 'right' }}>
                                              <div>{result.display}</div>
                                              {result.mg > 0 && (productFormData.apis[index]?.label_claim_unit?.includes('IU') || productFormData.apis[index]?.label_claim_unit?.includes('MIU')) && (
                                                <div style={{ fontSize: 'var(--fs-md)', color: 'var(--success-text, #059669)' }}>Eq. {result.mg.toFixed(3)} mg</div>
                                              )}
                                            </div>
                                          );
                                        })()}
                                      </div>
                                    </div>
                                  </div>

                                </div>
                              )}
                            </div>
                          );
                        })}
                        <button type="button" className="btn btn-secondary" onClick={addProductApiToForm} style={{ width: '100%', borderStyle: 'dashed', background: 'var(--info-bg, #f0f9ff)', color: 'var(--info-color)', borderColor: 'var(--info-border, #bae6fd)' }}>
                          <FaPlus /> Add Another API
                        </button>
                      </div>

                      {/* Card 3: Physical Attributes */}
                      {(() => {
                        const df = String(productFormData.dosage_form || '').toLowerCase();
                        const isTablet = df.trim() === 'tablet';
                        const isSyrup = df.includes('syrup') || df.includes('suspension') || df.includes('solution') || df.includes('liquid') || df.includes('oral');
                        const colorEnabled = reportSettings.product_master_color_field_enabled !== 'false';
                        const coatingEnabled = reportSettings.product_master_coating_fields_enabled !== 'false';
                        if (!colorEnabled && !(isTablet && coatingEnabled) && !isSyrup) return null;
                        return (
                          <div style={{ background: 'var(--card-bg)', padding: '12px', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid var(--border-color)' }}>
                            <h4 style={{ fontSize: 'var(--fs-xl)', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <span style={{ width: '4px', height: '20px', background: 'var(--warning-color, #ea580c)', borderRadius: '2px' }}></span>
                              Physical Attributes
                            </h4>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                              {colorEnabled && (() => {
                                const colorChecked = showColorField || !!productFormData.product_color;
                                return (
                                  <div className="form-group" style={{ marginBottom: 0, gridColumn: '1 / -1' }}>
                                    <label
                                      className="form-label"
                                      style={{ fontWeight: '500', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
                                      title="Color used in the tablet/syrup formulation (e.g. colorant/dye) — helps identify this product on the Worst Case Ranking list"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={colorChecked}
                                        onChange={() => {
                                          if (colorChecked) {
                                            setProductFormData((prev) => ({ ...prev, product_color: '' }));
                                            setShowColorField(false);
                                          } else {
                                            setShowColorField(true);
                                          }
                                        }}
                                      />
                                      Color Used in Formulation
                                    </label>
                                    {colorChecked && (
                                      <div style={{ position: 'relative' }}>
                                        <input
                                          type="text"
                                          className="form-control"
                                          value={productFormData.product_color || ''}
                                          onChange={(e) => setProductFormData((prev) => ({ ...prev, product_color: e.target.value }))}
                                          placeholder="Enter color used in formulation"
                                          style={{ padding: '10px 12px', borderRadius: '6px', border: '1px solid var(--border-color)' }}
                                        />
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                              {isSyrup && (
                                <div className="form-group" style={{ marginBottom: 0 }}>
                                  <label className="form-label" style={{ fontWeight: '500', color: 'var(--text-secondary)' }}>Flavour</label>
                                  <input className="form-control" value={productFormData.product_flavour || ''} onChange={(e) => setProductFormData({ ...productFormData, product_flavour: e.target.value })} placeholder="e.g. Strawberry, Mango, Orange" style={{ padding: '10px 12px', borderRadius: '6px', border: '1px solid var(--border-color)' }} />
                                </div>
                              )}
                              {isTablet && coatingEnabled && (
                                <>
                                  <div className="form-group" style={{ marginBottom: 0 }}>
                                    <label className="form-label" style={{ fontWeight: '500', color: 'var(--text-secondary)' }}>Coating Type</label>
                                    <select className="form-control" value={productFormData.coating_type || ''} onChange={(e) => setProductFormData({ ...productFormData, coating_type: e.target.value })} style={{ padding: '10px 12px', borderRadius: '6px', border: '1px solid var(--border-color)' }}>
                                      <option value="">-- Select --</option>
                                      <option>Uncoated</option>
                                      <option>Film Coat</option>
                                      <option>Sugar Coat</option>
                                      <option>Enteric Coat</option>
                                      <option>Modified Release</option>
                                      <option>Other</option>
                                    </select>
                                  </div>
                                  {productFormData.coating_type && productFormData.coating_type !== 'Uncoated' && (
                                    <div className="form-group" style={{ marginBottom: 0 }}>
                                      <label className="form-label" style={{ fontWeight: '500', color: 'var(--text-secondary)' }}>Coating Agent</label>
                                      <input className="form-control" value={productFormData.coating_agent || ''} onChange={(e) => setProductFormData({ ...productFormData, coating_agent: e.target.value })} placeholder="e.g. HPMC, Opadry, PVA, Eudragit" style={{ padding: '10px 12px', borderRadius: '6px', border: '1px solid var(--border-color)' }} />
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Card 4: Reference */}
                      <div style={{ background: 'var(--card-bg)', padding: '12px', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid var(--border-color)' }}>
                        <h4 style={{ fontSize: 'var(--fs-xl)', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ width: '4px', height: '20px', background: '#7c3aed', borderRadius: '2px' }}></span>
                          <FaFileAlt style={{ color: '#7c3aed' }} />
                          Reference & Documentation
                        </h4>
                        <div className="form-group">
                          <label className="form-label" style={{ fontWeight: '500', color: 'var(--text-secondary)' }}>Reference Document Link/Name</label>
                          <input className="form-control" value={productFormData.reference_document} onChange={(e) => setProductFormData({ ...productFormData, reference_document: e.target.value })} placeholder="Document name, URL, or file path" style={{ padding: '10px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', marginBottom: '15px' }} />
                        </div>
                        <div className="form-group">
                          <label className="form-label" style={{ fontWeight: '500', color: 'var(--text-secondary)' }}>Notes</label>
                          <textarea className="form-control" value={productFormData.product_notes || ''} onChange={(e) => setProductFormData({ ...productFormData, product_notes: e.target.value })} rows="3" placeholder="Additional notes for reference documentation..." style={{ padding: '10px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', marginBottom: '15px', fontFamily: 'monospace', fontSize: 'var(--fs-base)' }} />
                        </div>
                        
                        {/* File Attachments Section */}
                        <div style={{ borderTop: '1px solid var(--border-light)', paddingTop: '15px' }}>
                          <FileUploader 
                            label="Reference Files"
                            files={productFormData.attachedFiles} 
                            onAdd={(files) => setProductFormData({ ...productFormData, attachedFiles: [...(productFormData.attachedFiles || []), ...files] })} 
                            onRemove={(idx) => {
                              const updatedFiles = (productFormData.attachedFiles || []).filter((_, i) => i !== idx);
                              setProductFormData({ ...productFormData, attachedFiles: updatedFiles });
                            }} 
                            id="refFileInput" 
                          />
                        </div>
                      </div>

                    </div>

                  </form>
          </Modal>

          <ChangeReferenceModal
            open={!!changeRefGate}
            qmsLinked={qmsLinked}
            busy={changeRefBusy}
            recordLabel={changeRefGate?.product?.product_name}
            onCancel={() => setChangeRefGate(null)}
            onConfirm={confirmChangeReference}
          />

          {/* E-Sign Modal for submission */}
          <ESignModal
            isOpen={showESignModal}
            onCancel={() => { setShowESignModal(false); setPendingDelete(null); setPendingApproval(null); setPendingBulkDelete(null); setPendingAction(null); setPendingBulkApproval(null); setESignData({ password: '', comment: '', decision: 'approve' }); }}
            message={confirmationMessage || (
              pendingDelete ? (
                pendingDelete.stage === 'draft'
                  ? 'Deactivating draft product requires electronic signature.'
                  : 'Approval stage deactivation requires electronic signature and reason.'
              ) : pendingBulkDelete ? (
                'Bulk deactivating products requires electronic signature and reason.'
              ) : pendingAction ? (
                pendingAction.actionType === 'import'
                  ? 'Bulk importing products requires electronic signature and reason.'
                  : pendingAction.actionType === 'add'
                    ? 'Creating new product requires electronic signature and reason.'
                    : pendingAction.actionType === 'edit'
                      ? 'Editing product requires electronic signature and reason.'
                      : pendingAction.actionType === 'delete'
                        ? 'Deactivating product requires electronic signature and reason.'
                        : 'Action requires electronic signature and reason.'
              ) : pendingApproval ? (
                pendingApproval.stage === 'product'
                  ? 'Product approval requires electronic signature and reason.'
                  : 'QA approval requires electronic signature and reason.'
              ) : (
                isDraftESignMode
                  ? 'Saving draft requires creator electronic signature and reason.'
                  : 'Submitting for approval requires electronic signature and reason.'
              )
            )}
            showDecision={!!(pendingApproval || pendingBulkApproval || (pendingAction?.actionType === 'delete' && ['product', 'qa'].includes(pendingAction.data?.stage)))}
            decisionLabel={`Approval Decision *${pendingAction?.actionType === 'delete' ? ' (any decision other than Approve cancels the deactivation request)' : ''}`}
            decisionOptions={APPROVAL_DECISION_OPTIONS}
            decisionValue={eSignData.decision}
            onDecisionChange={(value) => setESignData({ ...eSignData, decision: value })}
            commentValue={eSignData.comment}
            onCommentChange={(value) => setESignData({ ...eSignData, comment: value })}
            commentPlaceholder={
              confirmationMessage ? 'Reason / Comment'
              : pendingDelete ? (pendingDelete.stage === 'draft' ? 'Reason for deactivating draft' : 'Reason for deactivation approval')
              : pendingBulkDelete ? 'Reason for bulk deactivation'
              : pendingAction?.actionType === 'delete' ? (pendingAction.data?.stage === 'draft' ? 'Reason for deactivating draft' : 'Reason for deactivation approval')
              : pendingAction ? `Reason for ${pendingAction.actionType}`
              : pendingApproval ? (pendingApproval.stage === 'product' ? 'Reason for product approval' : 'Reason for QA approval')
              : (isDraftESignMode ? 'Reason for draft save' : 'Reason for submission')
            }
            passwordValue={eSignData.password}
            onPasswordChange={(value) => setESignData({ ...eSignData, password: value })}
            passwordRef={esignPasswordRef}
            onConfirm={() => handleConfirmESignAndSave()}
            confirmLabel={
              (pendingApproval || pendingBulkApproval)
                ? 'Sign & Submit'
                : pendingDelete ? (pendingDelete.stage === 'draft' ? 'Confirm Deactivate' : 'Confirm & Submit') : pendingBulkDelete ? 'Confirm Bulk Deactivate' : pendingAction?.actionType === 'delete' ? (pendingAction.data?.stage === 'draft' ? 'Confirm Deactivate' : 'Confirm & Submit') : pendingAction ? `Confirm ${pendingAction.actionType.charAt(0).toUpperCase() + pendingAction.actionType.slice(1)}` : (isDraftESignMode ? 'Confirm & Save Draft' : 'Confirm & Submit')
            }
          />

          {/* Cleaning Validation Impact Assessment Modal (pre-save gate or read-only view) */}
          {showImpactModal && (
            <ImpactAssessmentModal
              mode={impactModalMode}
              loading={impactLoading}
              evaluation={impactEvaluation}
              record={impactViewRecord}
              values={impactValues}
              onChange={handleImpactModalChange}
              onConfirm={handleImpactConfirm}
              onClose={handleImpactModalClose}
              onWorkflow={impactModalMode === 'view' ? handleImpactWorkflow : undefined}
            />
          )}

          {/* Product View Modal */}
          {showProductViewModal && viewingProduct && (
            <Modal
              isOpen={showProductViewModal}
              size="lg"
              title="Product Details"
              onClose={() => setShowProductViewModal(false)}
            >
                <ModalSubheader>
                  <span>Unit / Plant: {getActiveUnitName()}</span>
                  <div className="modal-subheader-actions">
                    <button
                      className="btn btn-sm"
                      data-module="risk_assessment"
                      title="View/review the auto-generated Risk Ranking Assessment for this product"
                      onClick={() => {
                        const p = viewingProduct;
                        const api = (p.apis || [])[0] || {};
                        const params = new URLSearchParams({
                          product_id: p.product_id || '',
                          product_name: p.product_name || '',
                          product_code: p.product_code || '',
                          api_name: api.api_name || '',
                          ade_pde_value: api.pde_value || '',
                          solubility_class: api.solubility_classification || api.solubility || '',
                          cleanability_rating: api.cleanability_rating || api.cleanability || '',
                        });
                        setShowProductViewModal(false);
                        navigate(`/risk-assessment?${params.toString()}`);
                      }}
                      style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'var(--info-bg, #eff6ff)', color: 'var(--info-text, #2563eb)', border: '1px solid var(--info-border, #bfdbfe)', borderRadius: '6px', padding: '5px 12px', fontWeight: 600, fontSize: 'var(--fs-md)', cursor: 'pointer' }}
                    >
                      <FaShieldAlt /> Risk Assessment
                    </button>
                    <button
                      className="btn btn-sm"
                      data-module="impact_assessment"
                      title="View the recorded cleaning validation (CV) impact assessment for this product"
                      onClick={() => { setShowProductViewModal(false); openImpactViewModal(viewingProduct); }}
                      style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'var(--warning-bg, #fff7ed)', color: 'var(--warning-text, #c2410c)', border: '1px solid var(--warning-border, #fdba74)', borderRadius: '6px', padding: '5px 12px', fontWeight: 600, fontSize: 'var(--fs-md)', cursor: 'pointer' }}
                    >
                      <FaExclamationTriangle /> Impact Assessment
                    </button>
                    <button className="btn btn-sm btn-outline" data-module="risk_assessment" title="Print the risk and impact assessment of this product as one report" onClick={() => printProductAssessments(viewingProduct)}>
                      <FaPrint /> Risk & Impact Assessment Report
                    </button>
                  </div>
                </ModalSubheader>
                  {viewLoading ? (
                    <div className="loading-container"><div className="spinner"></div></div>
                  ) : (
                    <div id="product-details-print">
                      {(() => {
                        const risk = riskByProductId[String(viewingProduct.product_id)];
                        if (!risk) return null;
                        const colors = RISK_LEVEL_COLORS[risk.risk_color] || RISK_LEVEL_COLORS.green;
                        return (
                          <div style={{ background: colors.badge, border: `1.5px solid ${colors.border}`, borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                            <div>
                              <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: colors.text, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Cleaning Validation Risk Ranking</div>
                              <div style={{ fontSize: 'var(--fs-xl)', fontWeight: 800, color: colors.text }}>{risk.risk_level} (Score: {risk.total_risk_score}) — {risk.status}</div>
                            </div>
                            {risk.cleaning_strategy && (
                              <div style={{ fontSize: 'var(--fs-sm)', color: colors.text, maxWidth: '420px' }}>{risk.cleaning_strategy}</div>
                            )}
                          </div>
                        );
                      })()}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                        <div>
                          <label style={{ fontWeight: 'bold', color: 'var(--text-secondary)' }}>Product Code</label>
                          <p>{viewingProduct.product_code || '-'}</p>
                        </div>
                        <div>
                          <label style={{ fontWeight: 'bold', color: 'var(--text-secondary)' }}>Status</label>
                          <p>
                            <span
                              className={`badge ${
                                normalizeDisplayStatus(viewingProduct.status) === PRODUCT_STATUS.DRAFT
                                  ? 'badge-warning'
                                  : ([PRODUCT_STATUS.PENDING_PRODUCT_APPROVAL, PRODUCT_STATUS.PENDING_QA_APPROVAL].includes(normalizeDisplayStatus(viewingProduct.status)) ? 'badge-info' : 'badge-success')
                              }`}
                            >
                              {normalizeDisplayStatus(viewingProduct.status)}
                            </span>
                          </p>
                        </div>
                        <div style={{ gridColumn: 'span 2' }}>
                          <label style={{ fontWeight: 'bold', color: 'var(--text-secondary)' }}>Product Name</label>
                          <p>{viewingProduct.product_name || '-'}</p>
                        </div>
                        <div>
                          <label style={{ fontWeight: 'bold', color: 'var(--text-secondary)' }}>Dosage Form</label>
                          <p><span className="badge badge-primary">{viewingProduct.dosage_form || '-'}</span></p>
                        </div>
                        <div>
                          <label style={{ fontWeight: 'bold', color: 'var(--text-secondary)' }}>Section</label>
                          <p>{getProductLineDisplay(viewingProduct)}</p>
                        </div>
                        <div style={{ gridColumn: getLinkedTrainsForProduct(viewingProduct).length > 1 ? 'span 2' : 'auto' }}>
                          <label style={{ fontWeight: 'bold', color: 'var(--text-secondary)' }}>Equipment Train</label>
                          {getLinkedTrainsForProduct(viewingProduct).length > 0 ? (
                            <>
                              {getLinkedTrainsForProduct(viewingProduct).length > 1 && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '8px' }}>
                                  {getLinkedTrainsForProduct(viewingProduct).map((link, index) => (
                                    <div key={`${viewingProduct.product_id}-${link.train_id}`} style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '8px 10px' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                        <button
                                          type="button"
                                          onClick={(e) => { e.stopPropagation(); openTrainDetails(link.train_id, 'view'); }}
                                          style={{ background: 'none', border: 'none', padding: 0, color: 'var(--primary-dark, #1d4ed8)', cursor: 'pointer', fontWeight: 600, textDecoration: 'underline' }}
                                          title="View train details"
                                        >
                                          {getTrainLabel(link.train_id, link.train_code, link.train_name)}
                                        </button>
                                        <span className={`badge ${link.is_primary ? 'badge-success' : 'badge-info'}`}>{getTrainTypeLabel(index)}</span>
                                        {link.same_size_equipment ? <span className="badge badge-primary">Same Size Equipment</span> : null}
                                        {link.same_cleaning_method ? <span className="badge badge-primary">Same Cleaning Method</span> : null}
                                      </div>
                                      {link.qualification_remark ? <div style={{ marginTop: '4px', fontSize: 'var(--fs-md)', color: 'var(--text-secondary)' }}>Remark: {link.qualification_remark}</div> : null}
                                      {link.cv_batch_remark ? <div style={{ marginTop: '4px', fontSize: 'var(--fs-md)', color: 'var(--text-secondary)' }}>CV Batch Remark: {link.cv_batch_remark}</div> : null}
                                    </div>
                                  ))}
                                </div>
                              )}
                              {getLinkedTrainsForProduct(viewingProduct).length === 1 && (
                                <p style={{ marginTop: '8px' }}>
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); openTrainDetails(getLinkedTrainsForProduct(viewingProduct)[0].train_id, 'view'); }}
                                    style={{ background: 'none', border: 'none', padding: 0, color: 'var(--primary-dark, #1d4ed8)', cursor: 'pointer', fontWeight: 600, textDecoration: 'underline' }}
                                    title="View train details"
                                  >
                                    {getTrainLabel(getLinkedTrainsForProduct(viewingProduct)[0].train_id, getLinkedTrainsForProduct(viewingProduct)[0].train_code, getLinkedTrainsForProduct(viewingProduct)[0].train_name)}
                                  </button>
                                </p>
                              )}
                              {getLinkedTrainsForProduct(viewingProduct).length > 0 && (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); handleShowAllLinkedEquipment(); }}
                                  style={{ background: 'none', border: 'none', padding: 0, color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 'var(--fs-md)', fontWeight: 500, marginTop: '12px', display: 'block' }}
                                >
                                  {showLinkedTrainDetails ? 'Hide linked equipment' : `Show linked equipment (${getLinkedTrainsForProduct(viewingProduct).length})`}
                                </button>
                              )}
                            </>
                          ) : (
                            <p>-</p>
                          )}
                        </div>
                        <div>
                          <label style={{ fontWeight: 'bold', color: 'var(--text-secondary)' }}>Batch Size</label>
                          <p>{viewingProduct.batch_size || '-'} {viewingProduct.batch_size_unit || ''}</p>
                        </div>
                        <div>
                          <label style={{ fontWeight: 'bold', color: 'var(--text-secondary)' }}>Fill Weight / Vol / Avg Wt</label>
                          <p>{viewingProduct.fill_weight || '-'} {viewingProduct.fill_unit || ''}</p>
                        </div>
                        <AuditMeta
                          updatedAt={viewingProduct.updated_at}
                          updatedBy={formatApprover(viewingProduct.updated_by_user || viewingProduct.created_by_user)}
                          approvedAt={getProductApprovalMeta(viewingProduct).at}
                          approvedBy={formatApprover(getProductApprovalMeta(viewingProduct).by)}
                        />
                      </div>

                      {showLinkedTrainDetails && Object.keys(linkedTrainsMembers).length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '20px' }}>
                          {getLinkedTrainsForProduct(viewingProduct).map((link, index) => {
                            const members = linkedTrainsMembers[link.train_id] || [];
                            return (
                              <div key={link.train_id} style={{ padding: '12px', background: 'var(--card-bg)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '8px', flexWrap: 'wrap' }}>
                                  <label style={{ fontWeight: 'bold', color: 'var(--text-secondary)', display: 'block', fontSize: 'var(--fs-base)', margin: 0 }}>
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); openTrainDetails(link.train_id, 'view'); }}
                                      style={{ background: 'none', border: 'none', padding: 0, color: 'var(--primary-dark, #1d4ed8)', cursor: 'pointer', fontWeight: 600, textDecoration: 'underline' }}
                                      title="View train details"
                                    >
                                      {getTrainLabel(link.train_id, link.train_code, link.train_name)}
                                    </button>
                                    <span style={{ marginLeft: '8px', padding: '2px 8px', borderRadius: '3px', fontSize: 'var(--fs-sm)', fontWeight: 600, background: link.is_primary ? 'var(--success-bg, #dcfce7)' : 'var(--info-bg, #dbeafe)', color: link.is_primary ? 'var(--success-text, #166534)' : 'var(--info-text, #1e40af)' }}>
                                      {getTrainTypeLabel(index)}
                                    </span>
                                  </label>
                                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                    <button className="btn btn-outline btn-sm" onClick={(e) => { e.stopPropagation(); openTrainDetails(link.train_id, 'view'); }}>
                                      View Train
                                    </button>
                                    <button className="btn btn-primary btn-sm" onClick={(e) => { e.stopPropagation(); openTrainDetails(link.train_id, 'edit'); }}>
                                      Edit Train
                                    </button>
                                  </div>
                                </div>
                                {members.length > 0 ? (
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                    {members.sort((a, b) => (Number(a.sequence_order) || 0) - (Number(b.sequence_order) || 0)).map((member, idx) => (
                                      <button
                                        key={idx}
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); openEquipmentDetails(member.equipment_id, 'view'); }}
                                        disabled={!member.equipment_id}
                                        style={{ background: 'var(--card-bg)', padding: '6px 10px', borderRadius: '6px', border: '1px solid var(--border-color)', fontSize: 'var(--fs-md)', display: 'flex', alignItems: 'center', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', cursor: member.equipment_id ? 'pointer' : 'default', textAlign: 'left' }}
                                        title={member.equipment_id ? 'Open equipment details' : 'Equipment record unavailable'}
                                      >
                                        <span style={{ fontWeight: '700', color: 'var(--primary-color, #2563eb)', marginRight: '6px', backgroundColor: 'var(--info-bg, #eff6ff)', padding: '2px 6px', borderRadius: '4px', fontSize: 'var(--fs-sm)' }}>{member.sequence_order}</span>
                                        <span style={{ color: 'var(--text-primary)' }}>{member.equipment_code}</span>
                                        <span style={{ color: 'var(--text-secondary)', marginLeft: '4px' }}>- {member.equipment_name}</span>
                                        {member.production_line ? (
                                          <span style={{ marginLeft: '8px', color: 'var(--text-secondary)', backgroundColor: 'var(--bg-color)', padding: '2px 6px', borderRadius: '999px', fontSize: 'var(--fs-xs)' }}>
                                            {member.production_line}
                                          </span>
                                        ) : null}
                                      </button>
                                    ))}
                                  </div>
                                ) : (
                                  <div style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary)' }}>No equipment in this train</div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <hr />
                      <h4>Associated APIs</h4>
                      {(viewingProduct.apis || []).length === 0 ? (
                        <p className="text-muted">No APIs attached.</p>
                      ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px', marginBottom: '18px' }}>
                          {(viewingProduct.apis || []).map((api, i) => {
                            // Enhanced lookup: Try ID -> Name -> Code to ensure we find the master record
                            const linkedApi = allApis.find(a => String(a.api_id) === String(api.api_id)) ||
                                              allApis.find(a => a.api_name && api.api_name && String(a.api_name).trim().toLowerCase() === String(api.api_name).trim().toLowerCase()) ||
                                              allApis.find(a => a.api_code && api.api_code && String(a.api_code).trim().toLowerCase() === String(api.api_code).trim().toLowerCase());

                            const pdeVal = api.pde_value || linkedApi?.pde_value;
                            const ld50Val = api.ld50_value || linkedApi?.ld50_value;
                            const solubilityVal = api.solubility_classification || linkedApi?.solubility_classification || linkedApi?.solubility;
                            const cleanabilityVal = api.cleanability_rating || linkedApi?.cleanability_rating || linkedApi?.cleanability;

                            const missingData = [];
                            if (!pdeVal) missingData.push('PDE');
                            if (!ld50Val) missingData.push('LD50');
                            if (!solubilityVal) missingData.push('Solubility');
                            if (!cleanabilityVal) missingData.push('Cleanability');

                            return (
                            <div key={i} style={{ border: '1px solid var(--border-color)', borderRadius: '8px', padding: '12px', background: 'var(--card-bg)' }}>
                              {missingData.length > 0 && (
                                <div style={{ marginBottom: '10px', padding: '8px', backgroundColor: 'var(--warning-bg, #fff7ed)', border: '1px solid var(--warning-border, #ffedd5)', borderRadius: '6px', color: 'var(--warning-text, #c2410c)', fontSize: 'var(--fs-md)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <FaExclamationTriangle />
                                  <span><strong>Missing Critical Data:</strong> {missingData.join(', ')}</span>
                                </div>
                              )}
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                <div>
                                  <label style={{ fontWeight: 'bold', color: 'var(--text-secondary)' }}>API</label>
                                  <p><strong>{api.api_code || linkedApi?.api_code || '-'}</strong> - {api.api_name || linkedApi?.api_name || '-'}</p>
                                </div>
                                <div>
                                  <label style={{ fontWeight: 'bold', color: 'var(--text-secondary)' }}>Label Claim</label>
                                  <p>{api.label_claim || '-'} {api.label_claim_unit || ''}</p>
                                </div>
                                <div>
                                  <label style={{ fontWeight: 'bold', color: 'var(--text-secondary)' }}>Therapeutic Dose</label>
                                  <p>{api.therapeutic_dose || linkedApi?.therapeutic_dose || '-'} {api.therapeutic_dose_unit || api.dose_unit || linkedApi?.therapeutic_dose_unit || ''}</p>
                                </div>
                                <div>
                                  <label style={{ fontWeight: 'bold', color: 'var(--text-secondary)' }}>Max Daily Dose</label>
                                  <p>{api.max_daily_dose || linkedApi?.max_daily_dose || '-'} {api.max_daily_dose_unit || api.dose_unit || linkedApi?.max_daily_dose_unit || ''}</p>
                                </div>
                                <div>
                                  <label style={{ fontWeight: 'bold', color: 'var(--text-secondary)' }}>PDE/HBEL</label>
                                  <p>{pdeVal || '-'} {api.pde_unit || api.pde_unit || linkedApi?.pde_unit || linkedApi?.pde_unit || ''}</p>
                                </div>
                                <div>
                                  <label style={{ fontWeight: 'bold', color: 'var(--text-secondary)' }}>LD50</label>
                                  <p>{ld50Val || '-'} {api.ld50_value_unit || api.ld50_unit || linkedApi?.ld50_value_unit || linkedApi?.ld50_unit || ''}</p>
                                </div>
                                <div>
                                  <label style={{ fontWeight: 'bold', color: 'var(--text-secondary)' }}>Solubility</label>
                                  <p><span className="badge badge-info">{solubilityVal || '-'}</span></p>
                                </div>
                                <div>
                                  <label style={{ fontWeight: 'bold', color: 'var(--text-secondary)' }}>Cleanability</label>
                                  <p>
                                    <span className="badge badge-success">{cleanabilityVal || '-'}</span>
                                    {api.cleanability_override && (
                                      <span className="badge badge-warning" style={{ marginLeft: '6px' }} title={`Product override. API Master rating: ${api.api_cleanability_rating || '-'}`}>
                                        product override (API: {api.api_cleanability_rating || '-'})
                                      </span>
                                    )}
                                  </p>
                                </div>
                                {api.cleanability_override && (
                                  <div style={{ gridColumn: 'span 2' }}>
                                    <label style={{ fontWeight: 'bold', color: 'var(--text-secondary)' }}>Cleanability Override Reason</label>
                                    <p style={{ marginBottom: '4px' }}>{api.cleanability_override_reason || '-'}</p>
                                    {api.cleanability_override_assessment && (() => {
                                      const record = api.cleanability_override_assessment;
                                      const subject = productChecklistSubject(viewingProduct, api);
                                      const label = [viewingProduct?.product_code, api.api_code || api.api_name].filter(Boolean).join(' / ');
                                      return (
                                        <p style={{ marginBottom: '4px', fontSize: 'var(--fs-sm)' }}>
                                          Checklist {record.total}/{record.max} → suggested {record.suggested}{record.final_rating !== record.suggested ? `, rated ${record.final_rating}` : ''}
                                          {' · '}<a href="#print" onClick={(e) => { e.preventDefault(); printProductChecklist(subject, cleanConfig, record, label); }}>Print checklist</a>
                                          {' · '}<a href="#excel" onClick={(e) => { e.preventDefault(); exportCleanabilityChecklistXlsx(subject, cleanConfig, record, subject.fileName); }}>Excel</a>
                                        </p>
                                      );
                                    })()}
                                    {parseFiles(api.cleanability_override_document).map((file) => (
                                      <a key={file} href={toFileUrl(file)} target="_blank" rel="noreferrer" className="badge badge-info" style={{ marginRight: '6px' }}>override document: {file}</a>
                                    ))}
                                  </div>
                                )}
                                {String(api.label_claim_unit || '').toUpperCase().includes('IU') && (
                                  <div>
                                    <label style={{ fontWeight: 'bold', color: 'var(--text-secondary)' }}>IU/MIU to mg Conversion</label>
                                    <p>{api.iu_to_mg_conversion || '-'}</p>
                                  </div>
                                )}
                                <div>
                                  <label style={{ fontWeight: 'bold', color: 'var(--text-secondary)' }}>Notes</label>
                                  <p>{api.notes || linkedApi?.notes || '-'}</p>
                                </div>
                                <div style={{ gridColumn: 'span 2' }}>
                                  <label style={{ fontWeight: 'bold', color: 'var(--text-secondary)' }}>API Documents</label>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '4px' }}>
                                    {['dosage_document', 'pde_document', 'ld50_document', 'solubility_document', 'cleanability_document'].map((docKey) => {
                                      const files = parseFiles(api[docKey] || linkedApi?.[docKey]);
                                      if (files.length === 0) return null;
                                      return files.map((file, docIdx) => (
                                        <a key={`${docKey}-${docIdx}`} href={toFileUrl(file)} target="_blank" rel="noreferrer" className="badge badge-info">
                                          {docKey.replace('_', ' ')}: {file}
                                        </a>
                                      ));
                                    })}
                                    {!['dosage_document', 'pde_document', 'ld50_document', 'solubility_document', 'cleanability_document'].some((key) => parseFiles(api[key] || linkedApi?.[key]).length > 0) && (
                                      <span className="text-muted">No API documents</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );})}
                        </div>
                      )}

                      <hr />
                      <h4>Reference & Notes</h4>
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ fontWeight: 'bold', color: 'var(--text-secondary)' }}>Reference Document</label>
                        <p>{viewingProduct.reference_document || '-'}</p>
                      </div>
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ fontWeight: 'bold', color: 'var(--text-secondary)' }}>Product Notes</label>
                        <p>{viewingProduct.product_notes || '-'}</p>
                      </div>

                      <div>
                        <label style={{ fontWeight: 'bold', color: 'var(--text-secondary)' }}>Attached Files</label>
                        {parseFiles(viewingProduct.attached_files || viewingProduct.attachedFiles).length === 0 ? (
                          <p className="text-muted">No attachments</p>
                        ) : (
                          <ul style={{ marginTop: '6px', paddingLeft: '18px' }}>
                            {parseFiles(viewingProduct.attached_files || viewingProduct.attachedFiles).map((file, idx) => (
                              <li key={idx}>
                                <a href={toFileUrl(file)} target="_blank" rel="noreferrer">{file}</a>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  )}
                <ModalFooter>
                  <button className="btn btn-outline" type="button" onClick={printProductDetails}><FaPrint style={{ marginRight: 4 }} />Print</button>
                  <button className="btn btn-primary" onClick={() => { setShowProductViewModal(false); handleProductEdit(viewingProduct); }}>Edit</button>
                  {(() => {
                    const displayStatus = normalizeDisplayStatus(viewingProduct.status);
                    const isDeletionPending = viewingProduct.deletion_requested;
                    const stage = displayStatus === PRODUCT_STATUS.PENDING_PRODUCT_APPROVAL ? 'product'
                      : displayStatus === PRODUCT_STATUS.PENDING_QA_APPROVAL ? 'qa'
                      : null;
                    const canDecide = !isDeletionPending && stage && (stage === 'product' ? canApproveProductStage : canApproveQaStage);
                    if (!canDecide) return null;
                    return (
                      <button className="btn btn-success" onClick={() => { setShowProductViewModal(false); handleProductStageApproval(viewingProduct, stage); }}>Approve / Reject</button>
                    );
                  })()}
                </ModalFooter>
            </Modal>
          )}
      {showAddToTrainModal && (
        <div className="modal-overlay" onClick={() => { setShowAddToTrainModal(false); setTargetTrainId(''); setTargetTrainSearchTerm(''); setShowTargetTrainDropdown(false); }} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', backdropFilter: 'none', backgroundColor: 'rgba(0,0,0,0.4)', transition: 'opacity 200ms ease-in-out' }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', width: '95%', maxHeight: 'none', overflow: 'visible' }}>
            <div className="modal-header">
              <h2 className="modal-title">Add to Existing Train</h2>
              <button className="modal-close" onClick={() => { setShowAddToTrainModal(false); setTargetTrainId(''); setTargetTrainSearchTerm(''); setShowTargetTrainDropdown(false); }}>x</button>
            </div>
            <div className="modal-body" style={{ padding: '20px' }}>
              <div className="form-group">
                <label className="form-label">Select Equipment Train</label>
                <div style={{ position: 'relative', flex: 1 }}>
                  {targetTrainId ? (
                    <div style={{ display: 'flex', alignItems: 'center', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '0.25rem', padding: '0.375rem 0.75rem', height: '38px' }}>
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 'var(--fs-base)' }}>
                        {getTrainLabel(targetTrainId)}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setTargetTrainId('');
                          setTargetTrainSearchTerm('');
                        }}
                        style={{ border: 'none', background: 'transparent', color: 'var(--danger-color)', cursor: 'pointer', padding: '0 0 0 8px' }}
                      >
                        x
                      </button>
                    </div>
                  ) : (
                    <>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search train code or name..."
                        value={targetTrainSearchTerm}
                        onChange={(e) => setTargetTrainSearchTerm(e.target.value)}
                        onFocus={() => {
                          if (!trainOptions.length) loadTrains();
                          setShowTargetTrainDropdown(true);
                        }}
                        onBlur={() => setTimeout(() => setShowTargetTrainDropdown(false), 250)}
                      />
                      {showTargetTrainDropdown && (
                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1060, background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '0.25rem', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', maxHeight: '240px', display: 'flex', flexDirection: 'column' }} onMouseDown={(e) => e.preventDefault()}>
                          <div style={{ overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}>
                            {trainOptions.length === 0 && (
                              <div style={{ padding: '8px 12px', color: 'var(--text-secondary)', fontSize: 'var(--fs-base)' }}>No trains loaded</div>
                            )}
                            {trainOptions
                              .filter((t) =>
                                String(t.train_code || '').toLowerCase().includes(targetTrainSearchTerm.toLowerCase()) ||
                                String(t.train_name || '').toLowerCase().includes(targetTrainSearchTerm.toLowerCase())
                              )
                              .map((t) => (
                                <div
                                  key={t.train_id}
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    setTargetTrainId(t.train_id);
                                    setTargetTrainSearchTerm('');
                                    setShowTargetTrainDropdown(false);
                                  }}
                                  style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border-color)', fontSize: 'var(--fs-base)', color: 'var(--text-primary)' }}
                                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--hover-bg, rgba(0,0,0,0.06))'}
                                  onMouseLeave={(e) => e.currentTarget.style.background = 'var(--card-bg)'}
                                >
                                  <strong>{t.train_code}</strong> - {t.train_name}
                                </div>
                              ))}
                            {trainOptions.filter((t) =>
                              String(t.train_code || '').toLowerCase().includes(targetTrainSearchTerm.toLowerCase()) ||
                              String(t.train_name || '').toLowerCase().includes(targetTrainSearchTerm.toLowerCase())
                            ).length === 0 && (
                              <div style={{ padding: '8px 12px', color: 'var(--text-secondary)', fontSize: 'var(--fs-base)' }}>
                                No matches found.
                              </div>
                            )}
                          </div>
                          <div style={{ padding: '8px 12px', borderTop: '1px solid var(--border-light)', background: 'var(--card-bg)', flex: '0 0 auto' }}>
                            <button
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setShowAddToTrainModal(false);
                                navigate('/equipment-trains', { state: { openAddTrainModal: true } });
                              }}
                              style={{ display: 'inline-block', color: 'var(--primary-dark, #1e40af)', textDecoration: 'none', fontWeight: 700, background: 'var(--info-bg, #dbeafe)', border: '1px solid var(--info-border, #93c5fd)', padding: '4px 8px', borderRadius: '999px', boxShadow: '0 0 12px rgba(59,130,246,0.35)', cursor: 'pointer' }}
                            >
                              + Add New Train
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
              <p className="text-muted" style={{ fontSize: 'var(--fs-sm)' }}>Linking a train returns the selected products to Product/QA approval. Electronic signature required.</p>
              <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
                <div className="form-group">
                  <label className="form-label">Reason / Comment *</label>
                  <textarea className="form-control" rows="2" value={addToTrainESign.comment} onChange={(e) => setAddToTrainESign({ ...addToTrainESign, comment: e.target.value })} placeholder="Reason for linking to this train" />
                </div>
                <div className="form-group">
                  <label className="form-label">Password *</label>
                  <input type="password" className="form-control" autoComplete="new-password" name="esign_password_field" value={addToTrainESign.password} onChange={(e) => setAddToTrainESign({ ...addToTrainESign, password: e.target.value })} placeholder="Enter your login password" />
                </div>
              </form>
            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button className="btn btn-outline" onClick={() => { setShowAddToTrainModal(false); setTargetTrainId(''); setTargetTrainSearchTerm(''); setShowTargetTrainDropdown(false); setAddToTrainESign({ password: '', comment: '' }); }}>Cancel</button>
              <button className="btn btn-primary" onClick={handleBulkLinkProductsToTrain} disabled={!targetTrainId || !addToTrainESign.password || !addToTrainESign.comment.trim()}>Proceed</button>
            </div>
          </div>
        </div>
      )}
      {showAddSecondaryTrainModal && (
        <Modal
          isOpen={showAddSecondaryTrainModal}
          title="Add Additional Equipment Train"
          onClose={() => { setShowAddSecondaryTrainModal(false); setSecondaryTrainSearchTerm(''); setShowSecondaryTrainDropdown(false); }}
          footer={
            <button className="btn btn-outline" onClick={() => { setShowAddSecondaryTrainModal(false); setSecondaryTrainSearchTerm(''); setShowSecondaryTrainDropdown(false); }}>
              Close
            </button>
          }
        >
              <div className="form-group">
                <label className="form-label">Select Additional Equipment Train</label>
                <div style={{ position: 'relative', flex: 1 }}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search equipment train code or name..."
                    value={secondaryTrainSearchTerm}
                    onChange={(e) => setSecondaryTrainSearchTerm(e.target.value)}
                    onFocus={() => {
                      loadTrains();
                      setShowSecondaryTrainDropdown(true);
                    }}
                    onBlur={() => setTimeout(() => setShowSecondaryTrainDropdown(false), 250)}
                  />
                  {showSecondaryTrainDropdown && (
                    <div style={{ position: 'relative', zIndex: 1, marginTop: '4px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '0.25rem', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', maxHeight: '280px', display: 'flex', flexDirection: 'column' }} onMouseDown={(e) => e.preventDefault()}>
                      <div style={{ overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}>
                        {trainOptions.length === 0 && (
                          <div style={{ padding: '8px 12px', color: 'var(--text-secondary)', fontSize: 'var(--fs-base)' }}>No trains loaded</div>
                        )}
                        {trainOptions
                          .filter((t) =>
                            !productFormData.linked_trains?.some((lt) => lt.train_id === t.train_id) &&
                            (String(t.train_code || '').toLowerCase().includes(secondaryTrainSearchTerm.toLowerCase()) ||
                            String(t.train_name || '').toLowerCase().includes(secondaryTrainSearchTerm.toLowerCase()))
                          )
                          .map((t) => (
                            <div
                              key={t.train_id}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setProductFormData((prev) => ({
                                  ...prev,
                                  linked_trains: [...(prev.linked_trains || []), {
                                    train_id: t.train_id,
                                    train_code: t.train_code || '',
                                    train_name: t.train_name || '',
                                    is_primary: false,
                                    qualification_remark: '',
                                    same_size_equipment: false,
                                    same_cleaning_method: false,
                                    cv_batch_remark: ''
                                  }]
                                }));
                                setShowAddSecondaryTrainModal(false);
                                setSecondaryTrainSearchTerm('');
                                setShowSecondaryTrainDropdown(false);
                              }}
                              style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border-color)', fontSize: 'var(--fs-base)', color: 'var(--text-primary)' }}
                              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--hover-bg, rgba(0,0,0,0.06))'}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'var(--card-bg)'}
                            >
                              <strong>{t.train_code}</strong> - {t.train_name}
                            </div>
                          ))}
                        {trainOptions.filter((t) =>
                          !productFormData.linked_trains?.some((lt) => lt.train_id === t.train_id) &&
                          (String(t.train_code || '').toLowerCase().includes(secondaryTrainSearchTerm.toLowerCase()) ||
                          String(t.train_name || '').toLowerCase().includes(secondaryTrainSearchTerm.toLowerCase()))
                        ).length === 0 && (
                          <div style={{ padding: '8px 12px', color: 'var(--text-secondary)', fontSize: 'var(--fs-base)' }}>
                            {productFormData.linked_trains?.length > 0 && trainOptions.length > 0 ? 'All available trains already added.' : 'No matches found.'}
                          </div>
                        )}
                      </div>
                      <div style={{ padding: '8px 12px', borderTop: '1px solid var(--border-color)', background: 'var(--card-bg)', flex: '0 0 auto' }}>
                        <button
                          type="button"
                          className="btn btn-outline btn-sm"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            window.open('/equipment-trains?openAddTrain=1', '_blank', 'noopener,noreferrer');
                          }}
                        >
                          <FaPlus style={{ marginRight: 4 }} /> Add New Equipment Train
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
        </Modal>
      )}

      <AIResultPanel
        isOpen={ai.isOpen}
        onClose={ai.close}
        title={ai.title}
        loading={ai.loading}
        error={ai.error}
        content={ai.content}
        docType="protocol"
        exportFileName="product-ai-draft-protocol.pdf"
      />
      <AIResultPanel
        isOpen={grouping.isOpen}
        onClose={grouping.close}
        title={grouping.title}
        loading={grouping.loading}
        error={grouping.error}
        content={grouping.content}
        groups={grouping.groups}
        exportFileName="product-worstcase-grouping.pdf"
      />
      <VersionHistoryModal
        isOpen={!!historyModal}
        onClose={() => setHistoryModal(null)}
        title={historyModal ? `Version History — ${historyModal.product_name}` : ''}
        fetcher={() => productsAPI.getRevisions(historyModal.product_id)}
      />
    </div>
  );
};

export default ProductMaster;



